import { DOCUMENT } from '@angular/common';
import { isAwaitingFirstGeometry } from './models/first-geometry-wait';
import { resolveSceneQualityProfile, resolveSceneStartCamera } from './models/scene-defaults';
import {
  encodeCameraView,
  readViewerUrlSettings,
  ViewerCameraView,
  ViewerCameraVector,
  writeViewerUrlSettings,
} from './models/viewer-url-settings';
import { HsStableRenderOrder } from '@lib/core-ts/viewer/streaming/stable-render-order.class';
import {
  HS_DESKTOP_POINT_CLOUD_BUDGET_MAX_POINTS,
  HS_POINT_CLOUD_TARGET_SPACING_SHARE,
  hsPointCloudBudgetDevice,
  hsResolvePointCloudBudget,
} from '@lib/core-ts/viewer/streaming/point-cloud-budget.function';
import { HsPointCloudDrawBudget } from '@lib/core-ts/viewer/streaming/point-cloud-draw-budget.class';
import {
  hsOrderPointCloudBlocks,
  hsSelectPointCloudBlocks,
  hsPointCloudDrawCount,
  hsProjectedPointSpacing,
  hsAdaptivePointSize,
  hsPointCloudBlockDescendants,
  type HsPointCloudDrawView,
  type HsPointCloudBlockView,
  type HsPointCloudDrawOrderEntry,
  type HsPointCloudDrawSelection,
} from '@lib/core-ts/viewer/streaming/point-cloud-draw-order.function';
import { HsGpuFrameTimer } from '@lib/core-ts/viewer/runtime/gpu-frame-timer.class';
import { hsSortByCameraDistance } from '@lib/core-ts/viewer/culling/camera-distance-sort.function';
import { HsSpatialCulling } from '@lib/core-ts/viewer/culling/spatial-culling.class';
import { HsNavigationCulling } from '@lib/core-ts/viewer/culling/navigation-culling.class';
import { hsDecodeHlodPayload } from '@sibe/hyperscene/formats/hlod';
import {
  readClientCacheSettings,
  writeClientCacheSettings,
  type ClientCacheSettings,
} from './services/client-cache-settings.store';
import {
  HsHlodPayloadCache,
  HsPhotogrammetrySource,
  type HsHlodPayloadCacheStats,
  type HsHlodTilesetStats,
  type HsPhotogrammetryLoadStatus,
  type HsPhotogrammetryUpdate,
} from '@sibe/hyperscene/sources/hlod';
import {
  HsTileVisibilityPass,
  type HsVisibilityDraw,
  type HsVisibilityResult,
} from '@lib/core-ts/viewer/culling/tile-visibility-pass.class';
import { HsOcclusionHysteresis } from '@lib/shared/culling/occlusion-hysteresis.class';
import { HsMatrix4 } from '@lib/core-ts/math-new/objects/matrix4.class';
import {
  HsCameraPathRecorder,
  HsViewerProfiler,
  hsCreateViewerProbe,
  type HsViewerGpuSpan,
  type HsViewerProbe,
  type HsViewerProbeHandle,
  type HsProbePointCloudBlock,
  type HsProbePointCloudBudget,
  type HsViewerProbePhase,
  type HsViewerProfilerSnapshot,
  type HsViewerProfilerSpan,
} from '@sibe/hyperscene/diagnostics/viewer';
import {
  DEFAULT_POINT_CLOUD_GEOMETRY_FORMAT,
  VIEWER_POINT_CLOUD_GEOMETRY_FORMATS,
  ViewerPointCloudGeometryFormat,
  DEFAULT_VIEWER_FRUSTUM_SORT_ORDER,
  DEFAULT_VIEWER_GEOMETRY_FORMAT,
  DEFAULT_VIEWER_FRUSTUM_RESPONSE_FORMAT,
  DEFAULT_VIEWER_RENDER_ORDER_STRATEGY,
  DEFAULT_VIEWER_NAVIGATION_MIN_SCREEN_SIZE,
  defaultViewerRuntimeSettings,
  validateViewerRuntimeSettings,
  VIEWER_GEOMETRY_FORMATS,
  VIEWER_FRUSTUM_RESPONSE_FORMATS,
  VIEWER_FRUSTUM_SORT_ORDERS,
  VIEWER_RENDER_ORDER_STRATEGIES,
  VIEWER_RUNTIME_FIELDS,
  VIEWER_NAVIGATION_MIN_SCREEN_SIZES,
  ViewerGeometryFormat,
  ViewerFrustumResponseFormat,
  ViewerFrustumSortOrder,
  ViewerRenderOrderStrategy,
  ViewerRuntimeSettings,
  ViewerNavigationMinScreenSize,
  formatViewerRuntimeFieldValue,
  parseViewerRuntimeFieldValue,
} from './models/viewer-runtime-settings';
import {
  defaultViewerVisualQualitySettings,
  validateViewerVisualQualitySettings,
  viewerVisualQualitySettingsForProfile,
  viewerCadEffectsActive,
  viewerVisualQualityShaderSignature,
  VIEWER_VISUAL_QUALITY_ANTI_ALIASING_OPTIONS,
  VIEWER_VISUAL_QUALITY_PROFILES,
  ViewerVisualQualityAntiAliasing,
  ViewerVisualQualityProfile,
  ViewerVisualQualityShadingMode,
  ViewerVisualQualitySettings,
} from './models/viewer-visual-quality';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  Input,
  NgZone,
  OnDestroy,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { getUuid } from '@sibe/utilities/common';
import { HsHypersceneEngineConstants } from '@lib/core-ts/engine-new/constants/hyperscene-engine-constants.global';
import { HsCameraType } from '@lib/core-ts/engine-new/enums/camera-type.enum';
import { HsRenderObjects } from '@lib/core-ts/engine-new/filtering/render-objects.class';
import { HsRenderSorter } from '@lib/core-ts/engine-new/filtering/render-sorter.class';
import { HsPerspectiveCamera } from '@lib/core-ts/engine-new/objects/cameras/perspective-camera.class';
import { HsGraphics } from '@lib/core-ts/graphic-render-new/graphics.class';
import { HsViewerCullingFilter } from '@lib/core-ts/viewer/culling/viewer-culling-filter.class';
import { HsGraphicLayer } from '@lib/core-ts/graphic-render-new/objects/graphic-layer.class';
import { HsGraphicLines } from '@lib/core-ts/graphic-render-new/objects/graphic-lines.class';
import { HsGraphicObject } from '@lib/core-ts/graphic-render-new/objects/graphic-object.class';
import { HsGraphicPoints } from '@lib/core-ts/graphic-render-new/objects/graphic-points.class';
import { HsGraphicQueue } from '@lib/core-ts/graphic-render-new/objects/graphic-queue.class';
import { HsRaycastOutput } from '@lib/core-ts/graphic-render-new/objects/raycast-output.class';
import { HsRaycast } from '@lib/core-ts/graphic-render-new/raycast.class';
import { HsRender } from '@lib/core-ts/graphic-render-new/render.class';
import { HsCadPostProcess } from '@lib/core-ts/graphic-render-new/post-process/cad-post-process.class';
import { SettingsAutoApplyScheduler, SettingsApplyTier } from './services/settings-auto-apply-scheduler';
import { HsHypersceneMathUtils } from '@lib/core-ts/math-new/functions/hyperscene-math-utils.class';
import { HsVector2 } from '@lib/core-ts/math-new/objects/vector2.class';
import { HsVector3 } from '@lib/core-ts/math-new/objects/vector3.class';
import { HsBox } from '@lib/core-ts/math-new/shapes/box.class';
import type { HsHypersceneTypes } from '@lib/core-ts/math-new/types/hyperscene-types.type';
import { HsSubscription as HypersceneSubscription } from '@lib/core-ts/reactive-new/classes/subscription.class';
import type { HsSubscriberData } from '@lib/core-ts/reactive-new/types/subscriber-data.type';
import { HsCameraToolTypeControlAction } from '@lib/core-ts/tools-new/actions/camera-tool-type-control-action.class';
import { HsCameraToolType } from '@lib/core-ts/tools-new/actions/camera-tool-type.enum';
import { HsCameraTypeControlAction } from '@lib/core-ts/tools-new/actions/camera-type-control-action.class';
import { HsControlActionType } from '@lib/core-ts/tools-new/actions/control-action-type.enum';
import { HsKeyboardControlAction } from '@lib/core-ts/tools-new/actions/keyboard-control-action.class';
import { HsCameraToolsHandler } from '@lib/core-ts/tools-new/camera/camera-tools-handler.class';
import type { HsControlActionsHandler } from '@lib/core-ts/tools-new/camera/control-actions-handler.class';
import { HsGlobal } from '@lib/core-ts/tools-new/global';
import { HsTouchGestureRecognizer } from '@lib/core-ts/tools-new/gestures/touch-gesture-recognizer.class';
import { HsToolsConstants } from '@lib/core-ts/tools-new/tools-constants.constants';
import type { HsOutputMessageObject } from '@lib/core-ts/tools-new/messages/output-message-object.class';
import { HsOutputMessageType } from '@lib/core-ts/tools-new/messages/output-message-type.enum';
import { HsViewerOverlays } from '@lib/core-ts/viewer/rendering/viewer-overlays.class';
import { HsCanvasSize } from '@lib/core-ts/tools-new/utils/canvas-size.class';
import { Subscription, fromEvent } from 'rxjs';
import { subscribeOutsideAngular } from '../../functions/rxjs-operators/subscribe-outside-angular.operator';
import { HypersceneIngestPhase, HypersceneIngestState } from './models/hyperscene-ingest-state.interface';
import {
  isAnnotationNode,
  mergeById,
  ViewerAnnotationsStore,
  type HypersceneMarker,
  type HypersceneMeasurementEntry,
  type HypersceneViewerTransferPayload,
  type HypersceneViewpoint,
} from './services/viewer-annotations.store';
import { ViewerMeasurements, type ViewerMeasurementClick } from './services/viewer-measurements';
import { ViewerSelectionHighlight } from './services/viewer-selection-highlight';
import { ViewerLabels, type ViewerLabel } from './services/viewer-labels';
import {
  HypersceneAttributeView,
  HypersceneBoundingBoxView,
  HypersceneFrustumRequestInfo,
  HypersceneFrustumView,
  HypersceneIngestAdapterService,
  HypersceneVertexColorGroupView,
  HypersceneModelView,
  HypersceneNodeSmallView,
  HypersceneNodeView,
  HypersceneNodesVisibilityView,
  HypersceneProcessingView,
  HypersceneSceneNodeFilterResultView,
  HypersceneSceneModelInstanceView,
  HypersceneSceneSettings,
  HypersceneSceneView,
} from './services/hyperscene-ingest-adapter.service';
import { createVisibilitySessionId, loadVisibilitySessionId } from './services/visibility-session.store';
import type {
  HsBoundingBoxView,
  HsDecodedLine,
  HsDecodedMesh,
  HsDecodedPointCloud,
  HsDisposable,
} from '@sibe/hyperscene';
import type { HsGeometryDecodeKind } from '@sibe/hyperscene/workers/geometry-decode';
import {
  HS_GEOMETRY_DECODE_CANCELED,
  HsDecodedGeometryStore,
  HsGeometryDecodePool,
  hsCountMeshTriangles,
  hsNormalizeGeometryId,
  HsFrustumSceneState,
  HsFrustumRefreshScheduler,
  hsParseNodeTags,
  type HsFrustumDownloadSettings,
  HsSceneSource,
  hsSceneLook,
  type HsSceneLook,
  HS_DEFAULT_FRUSTUM_REQUEST_SETTINGS,
  HS_POINT_CLOUD_BUDGET_BOX_LIMIT,
  hsFrustumObjects,
  hsPointCloudDownloadBatching,
  type HsFrustumRequestSettings,
  type HsFrustumViewport,
  hsNodeIdOfRenderKey,
  type HsGeometryPayloadRequest,
  type HsMeshMaterialPartInfo,
  type HsNodeVisibilityState,
  type HsSceneAppliedEvent,
  type HsSceneRefreshProgress,
  type HsSceneRefreshResult,
  type HsSceneStreamingSettings,
  type HsSceneUpdateOptions,
} from '@sibe/hyperscene/sources/scene';
import {
  HsSceneTree,
  hsMakeSceneTreeNode,
  type HsSceneTreeNode,
  type HsVisibilityState,
} from '@sibe/hyperscene/api/viewer';
import {
  hsPixelsToWorld,
  hsProjectToScreen,
  hsScreenRay,
  hsSnapToMesh,
  type HsMeasurementHit,
  type HsMeasurementPoint,
  type HsScreenRay,
  type HsSnapKind,
  type HsSnapResult,
} from '@sibe/hyperscene/tools/measurement';
import { HsSceneObjects } from '@lib/core-ts/viewer/rendering/scene-objects.class';
import { HsSceneMaterialCache } from '@lib/core-ts/viewer/rendering/scene-material-cache.class';
import { HsSceneMaterials } from '@lib/core-ts/viewer/rendering/scene-materials.class';
import type { HsMaterial } from '@lib/core-ts/graphic-render-new/materials/material.class';
import type { HsShaderData } from '@lib/core-ts/graphic-render-new/shaders/classes/shader-data.class';
import type { HsCameraDefaultMove } from '@lib/core-ts/tools-new/camera/camera-default-move.class';
import { HsGraphicCollection } from '@lib/core-ts/graphic-render-new/objects/graphic-collection.class';
import type { HsClippingStorage } from '@lib/core-ts/engine-new/storages/clipping-storage.class';
import {
  hsCameraViewMatrix,
  hsFitCameraMatrix,
  type HsCameraView,
} from '@lib/core-ts/tools-new/camera/camera-views.function';
import { HsSectionTool, type HsSectionAxis } from '@sibe/hyperscene/tools/section';
import {
  HsHypersceneViewer,
  type HsViewerFrameState,
  type HsViewerPick,
} from '@lib/core-ts/viewer/hyperscene-viewer.class';
import { HsViewerInput } from '@lib/core-ts/viewer/runtime/viewer-input.class';
import { HsViewerSurface } from '@lib/core-ts/viewer/runtime/viewer-surface.class';
import { hsAdaptiveNavigationSpeedMultiplier } from '@lib/core-ts/viewer/input/adaptive-navigation-speed.function';
import { hsPreloadDracoDecoder } from '@sibe/hyperscene/formats/draco';
import { KEY_MAP } from './utils/key-map.global';

const CAMERA_POSITION: HsHypersceneTypes.Array3 = [0, 0, 0];
const ROTATION_POINT_DISTANCE: number = 30;
/** Scene opacity in x-ray mode, where the selection stays solid. */
const XRAY_OPACITY: number = 0.12;
/** Overlay group of the orbit pivot dot shown while dragging. */
const ORBIT_PIVOT_OVERLAY: string = 'orbit-pivot';
/** Overlay group of the section outline. */
const SECTION_OVERLAY: string = 'section';
/** How close, in CSS pixels, a click must be to a corner or edge to snap to it. */
const SNAP_PIXELS: number = 10;
/** Vertical field of view of the parallel (telephoto) projection: narrow enough to look orthographic. */
const PARALLEL_FOV: number = 0.05;
/** How far behind a clicked face, in meters, a face section cuts, so the face itself opens up. */
const FACE_SECTION_DEPTH: number = 0.01;

const SNAP_NAMES: Readonly<Record<Exclude<HsSnapKind, 'face'>, string>> = {
  vertex: 'a corner',
  midpoint: 'an edge middle',
  edge: 'an edge',
};

/** A drawn surface under a click, with what it snapped to and the face normal when one is known. */
interface ViewerSurfaceHit extends HsMeasurementHit {
  kind: HsSnapKind;
  normal: HsMeasurementPoint | null;
}
/** Half the size, in meters, of the section range around the view center when the scene has no bounds. */
const SECTION_FALLBACK_HALF_SPAN: number = 100;
/**
 * Quiet time after the last section change before the scene is fetched again for the new download box, so a slider
 * drag costs one Frustum request instead of one per step.
 */
const SECTION_REFETCH_DEBOUNCE_MS: number = 350;
/** The sphere around a box: its center and half its diagonal. */
function boundsSphere(bounds: { min: readonly number[]; max: readonly number[] }): {
  center: [number, number, number];
  radius: number;
} {
  const center: [number, number, number] = [
    (bounds.min[0] + bounds.max[0]) / 2,
    (bounds.min[1] + bounds.max[1]) / 2,
    (bounds.min[2] + bounds.max[2]) / 2,
  ];
  const radius: number =
    Math.hypot(bounds.max[0] - bounds.min[0], bounds.max[1] - bounds.min[1], bounds.max[2] - bounds.min[2]) / 2;
  return { center, radius };
}

/** Keyboard shortcuts of the standard views, as on a numeric keypad of CAD tools. */
const VIEW_SHORTCUTS: ReadonlyMap<string, HsCameraView> = new Map([
  ['1', 'front'],
  ['2', 'back'],
  ['3', 'left'],
  ['4', 'right'],
  ['5', 'top'],
  ['6', 'bottom'],
  ['0', 'iso'],
]);
const MESH_ID_PATTERN: RegExp = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const CAMERA_PATH_SAMPLE_INTERVAL_MS: number = 250;

const GEOMETRY_CACHE_DB_NAME: string = 'hyperscene-demo-cache-v1';
const GEOMETRY_CACHE_STORE_NAME: string = 'geometry-binary';
const MESH_CACHE_BUDGET_BYTES: number = 4 * 1024 * 1024 * 1024;
const TEXTURE_CACHE_BUDGET_BYTES: number = 3 * 1024 * 1024 * 1024;
const DECODE_CANCELED_ERROR_MESSAGE: string = HS_GEOMETRY_DECODE_CANCELED;
const FRUSTUM_CULLING_MIN_INTERVAL_MS: number = 200;
/** Overlay group of objects rebuilt with every render that includes transient objects. */
const TRANSIENT_OVERLAYS: string = 'transient';
/** Overlay group of the drawn HLOD node boxes, rebuilt when the drawn set changes. */
const HLOD_DEBUG_OVERLAYS: string = 'hlod-debug';
/** Overlay group of the debug object and point cloud boxes, rebuilt whenever the scene draws. */
const FRUSTUM_DEBUG_OVERLAYS: string = 'frustum-debug';
/** Distributed rendering draws until shortly before the next 60 Hz frame. */
const VIEWER_FRAME_BUDGET_MS: number = 1000 / 60 - 2;
// The draw budget regulator (budget LOD): the fewest points a navigating frame draws, the GPU draw time it
// aims below, the frame interval it aims below when the browser has no GPU timer (above the 60 Hz period,
// since intervals never drop below it), and how much points may grow while fewer of them are drawn.
const POINT_CLOUD_DRAW_MIN_POINTS: number = 250000;
const POINT_CLOUD_DRAW_TARGET_GPU_MS: number = 14;
const POINT_CLOUD_DRAW_TARGET_INTERVAL_MS: number = 25;
const POINT_CLOUD_NAVIGATION_SIZE_SCALE_MAX: number = 1.6;
// Downloads follow the regulated draw budget with headroom, within a floor share of the configured budget,
// so a device that cannot draw the full budget at the target frame time does not download it either.
const POINT_CLOUD_DOWNLOAD_HEADROOM: number = 1.5;
const POINT_CLOUD_DOWNLOAD_MIN_SHARE: number = 0.25;
// Occluded point cloud blocks: a block is skipped after this many visibility passes in a row found none of
// its pixels; passes run at most this often while the camera moves; each block draws a sample of its
// points into the 256 px wide pass, thinned to this spacing in pass pixels and never below the minimum
// count. The pass draws two-pixel points, so the stacked octree levels close surfaces and walls hide what
// is behind them; a one-pixel spacing took twice the sample points, and on Mechanical-room the extra GPU
// work delayed input frames (input latency p95 51 ms instead of 36 ms).
const POINT_CLOUD_OCCLUSION_HIDE_PASSES: number = 2;
const POINT_CLOUD_OCCLUSION_MIN_INTERVAL_MS: number = 100;
const POINT_CLOUD_OCCLUSION_PASS_WIDTH: number = 256;
const POINT_CLOUD_OCCLUSION_SAMPLE_SPACING_PIXELS: number = 2;
const POINT_CLOUD_OCCLUSION_MIN_SAMPLE_POINTS: number = 32;
// A camera jump (a turn of more than about 25 degrees, or a move of more than 5% of the largest block since
// the last pass) forgets every hidden block: the old result says nothing about the new view.
const POINT_CLOUD_OCCLUSION_JUMP_MIN_DIRECTION_DOT: number = 0.9;
const POINT_CLOUD_OCCLUSION_JUMP_MOVE_SHARE: number = 0.05;
const MAX_VIEWER_DOWNSAMPLE_RATE: number = 10;
const MAX_DEBUG_OBJECT_BOUNDING_BOXES: number = 10000;

function appendLineCubeVertices(vertices: number[], box: HsBox): void {
  const a: [number, number, number] = [box.max.x, box.max.y, box.max.z];
  const b: [number, number, number] = [box.min.x, box.max.y, box.max.z];
  const c: [number, number, number] = [box.min.x, box.min.y, box.max.z];
  const d: [number, number, number] = [box.max.x, box.min.y, box.max.z];
  const e: [number, number, number] = [box.max.x, box.max.y, box.min.z];
  const f: [number, number, number] = [box.min.x, box.max.y, box.min.z];
  const g: [number, number, number] = [box.min.x, box.min.y, box.min.z];
  const h: [number, number, number] = [box.max.x, box.min.y, box.min.z];
  vertices.push(...a, ...b, ...b, ...c, ...c, ...d, ...d, ...a);
  vertices.push(...e, ...f, ...f, ...g, ...g, ...h, ...h, ...e);
  vertices.push(...a, ...e, ...b, ...f, ...c, ...g, ...d, ...h);
}

interface HypersceneRenderedObjectsStat {
  meshCount: number;
  lineCount: number;
  pointCloudCount: number;
  collidedPointsCount: number;
  meshRejectedInvalidVertices: number;
  meshRejectedEmptyIndices: number;
  meshRejectedInvalidIndices: number;
  meshRejectedIndexedBuildFailed: number;
  meshRejectedRegularFallbackFailed: number;
}

interface HypersceneSceneStats {
  queueObjectsCount: number;
  frustumVisibleObjectsCount: number;
  frustumSortedObjectsCount: number;
  frustumCullingMs: number;
  distributedRendering: boolean;
  clippingEnabled: boolean;
  measurementEnabled: boolean;
  downsampleRate: number;
}

interface HypersceneInitialScenePreparation {
  sceneTreeLoaded: boolean;
  viewpointsLoadTask: Promise<void> | null;
}

type HypersceneSceneTreeNode = HsSceneTreeNode;

type HypersceneVisibilityState = HsVisibilityState;
type HypersceneTransferImportMode = 'replace' | 'merge';
type HypersceneGeometryKind = HsGeometryDecodeKind;

interface HypersceneExternalLinkItem {
  label: string;
  url: string;
}

interface HypersceneRuntimeConfig {
  apiBaseUrl?: string;
}

interface HypersceneSceneLoadDebugState {
  loadMode: string;
  rootNodesCount: number;
  totalSceneObjectsCount: number;
  visibleRootNodesCount: number;
  meshIdsCount: number;
  lineIdsCount: number;
  pointCloudIdsCount: number;
  meshesBinaryBytes: number;
  linesBinaryBytes: number;
  pointCloudsBinaryBytes: number;
  decodedMeshesCount: number;
  decodedLinesCount: number;
  decodedPointCloudsCount: number;
  meshRejectedInvalidVertices: number;
  meshRejectedEmptyIndices: number;
  meshRejectedInvalidIndices: number;
  meshRejectedIndexedBuildFailed: number;
  meshRejectedRegularFallbackFailed: number;
  lastUpdatedAt: string;
  lastError: string;
}

interface HypersceneSceneGeometryDebugState {
  loadedMeshGeometriesCount: number;
  loadedLineGeometriesCount: number;
  loadedPointCloudGeometriesCount: number;
  loadedTrianglesCount: number;
  loadedLineSegmentsCount: number;
  loadedPointsCount: number;
}

interface HypersceneSceneCacheBudgetDebugState {
  meshBytes: number;
  meshBudgetBytes: number;
  meshCount: number;
  textureBytes: number;
  textureBudgetBytes: number;
  textureCount: number;
}

interface HypersceneSceneOverviewDebugState {
  frustumMeshObjectsCount: number;
  frustumLineObjectsCount: number;
  frustumPointCloudObjectsCount: number;
}

interface HypersceneSceneRenderTotals {
  drawCalls: number;
  triangles: number;
  totalInstancedBatches: number;
  culledInstancedBatches: number;
  instancedDrawCalls: number;
  instancedInstances: number;
}

interface HypersceneSceneDebugSnapshot {
  capturedAt: string;
  url: string;
  scene: {
    sceneId: string | null;
    selectedSceneId: string | null;
    modelId: string | null;
    rootNodeId: string | null;
  };
  cameraPosition: number[];
  render: {
    currentCameraDrawCalls: number;
    totalInstancedBatches: number;
    culledInstancedBatches: number;
    instancedDrawCalls: number;
    instancedInstances: number;
    cachedShaderPrograms: number;
    activeSceneTriangles: number;
    downsampleRate: number;
    distributedRendering: boolean;
  };
  sceneStats: HypersceneSceneStats;
  sceneLoad: HypersceneSceneLoadDebugState;
  sceneGeometry: HypersceneSceneGeometryDebugState;
  cacheBudgets: HypersceneSceneCacheBudgetDebugState;
  sceneOverview: HypersceneSceneOverviewDebugState;
  profiler: HsViewerProfilerSnapshot;
  ingest: HypersceneIngestState;
}

interface HypersceneViewerLoadingState {
  message: string;
  completed: number;
  total: number | null;
}

/** What the two IndexedDB caches held at the last scan (Settings tab). */
interface ClientCacheUsage {
  photogrammetryEntries: number;
  photogrammetryBytes: number;
  geometryEntries: number;
  geometryBytes: number;
}

/** The photogrammetry line of the loading overlay: the SDK status plus the progress the page derives from it. */
interface HypersceneViewerPhotogrammetryLoadingState {
  status: HsPhotogrammetryLoadStatus;
  /** Most work seen pending since the stream last settled; progress is what is done of it. */
  peakPending: number;
}

// A 2,048 px ETC1S atlas transcodes to BC1 in about 17 ms, several times faster than it downloads at
// 40 Mbit/s, so one worker keeps up; three competed with the main thread (frame interval p95 +1.4 ms).
const HLOD_TEXTURE_TRANSCODE_WORKERS: number = 1;

/** Nodes and manifests the photogrammetry stream still has to bring in for the view. */
function pendingPhotogrammetryWork(status: HsPhotogrammetryLoadStatus): number {
  return (
    status.manifestsLoading +
    status.subtreesLoading +
    status.nodesLoading +
    status.nodesQueued +
    status.nodesDecoded +
    status.nodesRetrying
  );
}

@Component({
  selector: 'hyperscene-advanced',
  templateUrl: './hyperscene-advanced.component.html',
  styleUrls: ['./hyperscene-advanced.component.scss', './artifacts-workspace.scss'],
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
/* eslint-disable @typescript-eslint/member-ordering */
export class HypersceneAdvancedDemo implements AfterViewInit, OnDestroy {
  @Input() public sceneOnly: boolean = false;
  @Input() public debugEnabled: boolean = false;
  public activeViewerWorkspace: 'structure' | 'viewpoints' | 'tools' | 'quality' | 'settings' | 'performance' | null =
    null;
  private runtimeSceneReloadPending: boolean = false;

  public selectViewerWorkspace(
    tab: 'structure' | 'viewpoints' | 'tools' | 'quality' | 'settings' | 'performance'
  ): void {
    this.activeViewerWorkspace = this.activeViewerWorkspace === tab ? null : tab;
  }

  public get standaloneViewerQuery(): Record<string, string | null> {
    return this.getStandaloneViewerQuery(
      {
        position: this.cameraPositionDebug,
        direction: this.cameraDirectionDebug,
      },
      null
    );
  }

  public get standaloneViewpointQuery(): Record<string, string | null> {
    return this.getStandaloneViewerQuery(undefined, this.selectedViewpointId);
  }

  private getStandaloneViewerQuery(
    cameraView: ViewerCameraView | undefined,
    viewpointId: string | null
  ): Record<string, string | null> {
    return {
      sceneId: this.selectedSceneId,
      ...writeViewerUrlSettings(
        this.runtimeSettings,
        this.viewerDistributedRendering,
        this.viewerRenderOrderStrategy,
        this.disableTransparentMaterials,
        this.freezeFrustum,
        this.showPointCloudBoxes,
        this.enableInstancing,
        this.showViewerLoading,
        this.viewerProfilingEnabled,
        this.viewerDeepProfilingEnabled,
        this.useCompactFrustum,
        this.geometryFormat,
        this.visualQualitySettings,
        this.pointCloudGeometryFormat,
        this.showObjectBoundingBoxes,
        this.meshFrustumSortOrder,
        cameraView,
        viewpointId,
        this.navigationMinScreenSize,
        this.downloadBatchAuto,
        this.frustumResponseFormat,
        this.pointCloudAdaptivePointSize
      ),
    };
  }
  public readonly runtimeFields: typeof VIEWER_RUNTIME_FIELDS = VIEWER_RUNTIME_FIELDS;
  public readonly navigationMinScreenSizes: typeof VIEWER_NAVIGATION_MIN_SCREEN_SIZES =
    VIEWER_NAVIGATION_MIN_SCREEN_SIZES;
  public readonly geometryFormats: typeof VIEWER_GEOMETRY_FORMATS = VIEWER_GEOMETRY_FORMATS;
  public readonly frustumResponseFormats: typeof VIEWER_FRUSTUM_RESPONSE_FORMATS = VIEWER_FRUSTUM_RESPONSE_FORMATS;
  public readonly frustumSortOrders: typeof VIEWER_FRUSTUM_SORT_ORDERS = VIEWER_FRUSTUM_SORT_ORDERS;
  public readonly pointCloudGeometryFormats: typeof VIEWER_POINT_CLOUD_GEOMETRY_FORMATS =
    VIEWER_POINT_CLOUD_GEOMETRY_FORMATS;
  public readonly renderOrderStrategies: typeof VIEWER_RENDER_ORDER_STRATEGIES = VIEWER_RENDER_ORDER_STRATEGIES;
  public readonly visualQualityProfiles: typeof VIEWER_VISUAL_QUALITY_PROFILES = VIEWER_VISUAL_QUALITY_PROFILES;
  public readonly visualQualityAntiAliasingOptions: typeof VIEWER_VISUAL_QUALITY_ANTI_ALIASING_OPTIONS =
    VIEWER_VISUAL_QUALITY_ANTI_ALIASING_OPTIONS;
  public readonly runtimeGroups: string[] = ['Render pipeline', 'Camera', 'Frustum'];
  public runtimeSettings: ViewerRuntimeSettings = defaultViewerRuntimeSettings();
  public runtimeSettingsDraft: ViewerRuntimeSettings = defaultViewerRuntimeSettings();
  public downloadBatchAutoDraft: boolean = true;
  public runtimeSettingsError: string | null = null;
  public runtimeSettingsMessage: string = '';
  public distributedRenderingDraft: boolean = true;
  public navigationMinScreenSizeDraft: ViewerNavigationMinScreenSize = DEFAULT_VIEWER_NAVIGATION_MIN_SCREEN_SIZE;
  public renderOrderStrategyDraft: ViewerRenderOrderStrategy = DEFAULT_VIEWER_RENDER_ORDER_STRATEGY;
  public disableTransparentMaterialsDraft: boolean = false;
  public enableInstancingDraft: boolean = true;
  public pointCloudAdaptivePointSizeDraft: boolean = true;
  public hlodOcclusionRefinementDraft: boolean = true;
  public freezeFrustumDraft: boolean = false;
  public showPointCloudBoxesDraft: boolean = false;
  public showObjectBoundingBoxesDraft: boolean = false;
  public showViewerLoadingDraft: boolean = true;
  public viewerProfilingEnabledDraft: boolean = false;
  public viewerDeepProfilingEnabledDraft: boolean = false;
  public adaptiveNavigationWheelDraft: boolean = true;
  public adaptiveNavigationKeyboardDraft: boolean = true;
  public geometryFormatDraft: ViewerGeometryFormat = DEFAULT_VIEWER_GEOMETRY_FORMAT;
  public pointCloudGeometryFormatDraft: ViewerPointCloudGeometryFormat = DEFAULT_POINT_CLOUD_GEOMETRY_FORMAT;
  public frustumResponseFormatDraft: ViewerFrustumResponseFormat = DEFAULT_VIEWER_FRUSTUM_RESPONSE_FORMAT;
  public meshFrustumSortOrderDraft: ViewerFrustumSortOrder = DEFAULT_VIEWER_FRUSTUM_SORT_ORDER;
  public visualQualitySettings: ViewerVisualQualitySettings = defaultViewerVisualQualitySettings();
  public visualQualitySettingsDraft: ViewerVisualQualitySettings = defaultViewerVisualQualitySettings();
  public visualQualityDiagnosticMessage: string = '';
  private cadPostProcess: HsCadPostProcess | null = null;
  public visualQualityShaderBuildCount: number = 0;
  private viewerDistributedRendering: boolean = true;
  private navigationMinScreenSize: ViewerNavigationMinScreenSize = DEFAULT_VIEWER_NAVIGATION_MIN_SCREEN_SIZE;
  private navigationCullingActive: boolean = false;
  private readonly viewerNavigationCulling: HsNavigationCulling<HsGraphicObject> = new HsNavigationCulling(
    FRUSTUM_CULLING_MIN_INTERVAL_MS * 4
  );
  private viewerRenderOrderStrategy: ViewerRenderOrderStrategy = DEFAULT_VIEWER_RENDER_ORDER_STRATEGY;
  private disableTransparentMaterials: boolean = false;
  private enableInstancing: boolean = true;
  private pointCloudAdaptivePointSize: boolean = true;
  private freezeFrustum: boolean = false;
  private showPointCloudBoxes: boolean = false;
  private showObjectBoundingBoxes: boolean = false;
  private showViewerLoading: boolean = true;
  private useCompactFrustum: boolean = true;
  private frustumResponseFormat: ViewerFrustumResponseFormat = DEFAULT_VIEWER_FRUSTUM_RESPONSE_FORMAT;
  private viewerProfilingEnabled: boolean = false;
  private viewerDeepProfilingEnabled: boolean = false;
  private adaptiveNavigationWheel: boolean = true;
  private adaptiveNavigationKeyboard: boolean = true;
  private geometryFormat: ViewerGeometryFormat = DEFAULT_VIEWER_GEOMETRY_FORMAT;
  private downloadBatchAuto: boolean = true;
  private pointCloudGeometryFormat: ViewerPointCloudGeometryFormat = DEFAULT_POINT_CLOUD_GEOMETRY_FORMAT;
  private meshFrustumSortOrder: ViewerFrustumSortOrder = DEFAULT_VIEWER_FRUSTUM_SORT_ORDER;
  private readonly viewerProfiler: HsViewerProfiler = new HsViewerProfiler();
  public viewerProfilerSnapshot: HsViewerProfilerSnapshot = this.viewerProfiler.snapshot();

  public get runtimeSettingsBusy(): boolean {
    return this.sceneLoadInProgress || this.frustumRefreshInProgress;
  }

  private readonly settingsAutoApply: SettingsAutoApplyScheduler = new SettingsAutoApplyScheduler({
    apply: (): Promise<void> => this.applyRuntimeSettings(),
    isBlocked: (): boolean => this.runtimeSettingsBusy,
    onChange: (): void => this.changeDetector.markForCheck(),
  });

  public get settingsApplyCountdown(): number | null {
    return this.settingsAutoApply.secondsLeft;
  }

  public get settingsApplyPending(): boolean {
    return this.settingsAutoApply.pending;
  }

  public applyRuntimeSettingsNow(): void {
    if (this.settingsAutoApply.pending) {
      this.settingsAutoApply.flush();
      return;
    }
    void this.applyRuntimeSettings();
  }

  /** Drops the unapplied edits and restores the drafts from the settings currently in effect. */
  public cancelPendingSettings(): void {
    this.settingsAutoApply.cancel();
    this.runtimeSettingsDraft = { ...this.runtimeSettings };
    this.downloadBatchAutoDraft = this.downloadBatchAuto;
    this.visualQualitySettingsDraft = { ...this.visualQualitySettings, cad: { ...this.visualQualitySettings.cad } };
    this.distributedRenderingDraft = this.viewerDistributedRendering;
    this.navigationMinScreenSizeDraft = this.navigationMinScreenSize;
    this.renderOrderStrategyDraft = this.viewerRenderOrderStrategy;
    this.disableTransparentMaterialsDraft = this.disableTransparentMaterials;
    this.enableInstancingDraft = this.enableInstancing;
    this.pointCloudAdaptivePointSizeDraft = this.pointCloudAdaptivePointSize;
    this.hlodOcclusionRefinementDraft = this.hlodOcclusionRefinement;
    this.freezeFrustumDraft = this.freezeFrustum;
    this.showPointCloudBoxesDraft = this.showPointCloudBoxes;
    this.showObjectBoundingBoxesDraft = this.showObjectBoundingBoxes;
    this.showViewerLoadingDraft = this.showViewerLoading;
    this.viewerProfilingEnabledDraft = this.viewerProfilingEnabled;
    this.viewerDeepProfilingEnabledDraft = this.viewerDeepProfilingEnabled;
    this.adaptiveNavigationWheelDraft = this.adaptiveNavigationWheel;
    this.adaptiveNavigationKeyboardDraft = this.adaptiveNavigationKeyboard;
    this.geometryFormatDraft = this.geometryFormat;
    this.pointCloudGeometryFormatDraft = this.pointCloudGeometryFormat;
    this.frustumResponseFormatDraft = this.frustumResponseFormat;
    this.meshFrustumSortOrderDraft = this.meshFrustumSortOrder;
    this.runtimeSettingsError = null;
    this.runtimeSettingsMessage = 'Pending changes discarded.';
  }

  private queueSettingsApply(tier: SettingsApplyTier): void {
    this.runtimeSettingsMessage = '';
    if (this.runtimeSettingsError !== null) {
      this.settingsAutoApply.cancel();
      return;
    }
    this.settingsAutoApply.schedule(tier);
  }

  public onRuntimeSettingChanged(key: keyof ViewerRuntimeSettings, event: Event): void {
    if (!(event.target instanceof HTMLInputElement)) {
      return;
    }
    const field: (typeof VIEWER_RUNTIME_FIELDS)[number] | undefined = this.runtimeFields.find(
      (entry: (typeof VIEWER_RUNTIME_FIELDS)[number]) => entry.key === key
    );
    if (field === undefined) {
      return;
    }
    this.runtimeSettingsDraft = {
      ...this.runtimeSettingsDraft,
      [key]: parseViewerRuntimeFieldValue(field, event.target.value),
    };
    this.runtimeSettingsError = validateViewerRuntimeSettings(this.runtimeSettingsDraft);
    this.queueSettingsApply(key === 'pointCloudPointSize' ? 'short' : 'delayed');
  }

  public onDownloadBatchAutoChanged(event: Event): void {
    if (event.target instanceof HTMLInputElement) {
      this.downloadBatchAutoDraft = event.target.checked;
      this.queueSettingsApply('delayed');
    }
  }

  public getRuntimeFieldInputValue(field: (typeof VIEWER_RUNTIME_FIELDS)[number]): number {
    return formatViewerRuntimeFieldValue(field, this.runtimeSettingsDraft[field.key]);
  }

  public getRuntimeFieldInputMin(field: (typeof VIEWER_RUNTIME_FIELDS)[number]): number {
    return formatViewerRuntimeFieldValue(field, field.min);
  }

  public getRuntimeFieldInputMax(field: (typeof VIEWER_RUNTIME_FIELDS)[number]): number {
    return formatViewerRuntimeFieldValue(field, field.max);
  }

  public getRuntimeFieldInputStep(field: (typeof VIEWER_RUNTIME_FIELDS)[number]): number {
    return formatViewerRuntimeFieldValue(field, field.step);
  }

  public onVisualQualityShadingModeChanged(event: Event): void {
    if (!(event.target instanceof HTMLSelectElement)) {
      return;
    }
    if (event.target.value !== 'unlit' && event.target.value !== 'diffuse') {
      return;
    }
    this.visualQualitySettingsDraft = {
      ...this.visualQualitySettingsDraft,
      shadingMode: event.target.value as ViewerVisualQualityShadingMode,
    };
    this.runtimeSettingsError = validateViewerVisualQualitySettings(this.visualQualitySettingsDraft);
    this.queueSettingsApply('instant');
  }

  public onVisualQualityProfileChanged(event: Event): void {
    if (!(event.target instanceof HTMLSelectElement)) {
      return;
    }
    const profile: string = event.target.value;
    if (
      !this.visualQualityProfiles.some(
        (item: (typeof VIEWER_VISUAL_QUALITY_PROFILES)[number]) => item.value === profile
      )
    ) {
      return;
    }
    this.visualQualitySettingsDraft = viewerVisualQualitySettingsForProfile(profile as ViewerVisualQualityProfile);
    this.runtimeSettingsError = validateViewerVisualQualitySettings(this.visualQualitySettingsDraft);
    this.queueSettingsApply('instant');
  }

  public onVisualQualityNumberChanged(
    key: Exclude<
      keyof ViewerVisualQualitySettings,
      'profile' | 'antiAliasing' | 'shadingMode' | 'specularEnabled' | 'fresnelEnabled'
    >,
    event: Event
  ): void {
    if (!(event.target instanceof HTMLInputElement)) {
      return;
    }
    this.visualQualitySettingsDraft = {
      ...this.visualQualitySettingsDraft,
      [key]: event.target.valueAsNumber,
    };
    this.runtimeSettingsError = validateViewerVisualQualitySettings(this.visualQualitySettingsDraft);
    this.queueSettingsApply('short');
  }

  public onVisualQualityToggleChanged(key: 'specularEnabled' | 'fresnelEnabled', event: Event): void {
    if (!(event.target instanceof HTMLInputElement)) {
      return;
    }
    this.visualQualitySettingsDraft = {
      ...this.visualQualitySettingsDraft,
      [key]: event.target.checked,
    };
    this.runtimeSettingsError = validateViewerVisualQualitySettings(this.visualQualitySettingsDraft);
    this.queueSettingsApply('instant');
  }

  public onCadToggleChanged(
    key: 'ssaoEnabled' | 'edgesEnabled' | 'tonemapEnabled' | 'gradientBackground' | 'vignette',
    event: Event
  ): void {
    if (!(event.target instanceof HTMLInputElement)) {
      return;
    }
    this.visualQualitySettingsDraft = {
      ...this.visualQualitySettingsDraft,
      cad: { ...this.visualQualitySettingsDraft.cad, [key]: event.target.checked },
    };
    this.runtimeSettingsError = validateViewerVisualQualitySettings(this.visualQualitySettingsDraft);
    this.queueSettingsApply('instant');
  }

  public get visualQualityCadEffectsDraftActive(): boolean {
    return viewerCadEffectsActive(this.visualQualitySettingsDraft);
  }

  public onCadNumberChanged(
    key:
      | 'ssaoIntensity'
      | 'ssaoRadius'
      | 'edgeIntensity'
      | 'edgeWidth'
      | 'tonemapStrength'
      | 'saturation'
      | 'warmth'
      | 'keyLight'
      | 'sharpen',
    event: Event
  ): void {
    if (!(event.target instanceof HTMLInputElement)) {
      return;
    }
    this.visualQualitySettingsDraft = {
      ...this.visualQualitySettingsDraft,
      cad: { ...this.visualQualitySettingsDraft.cad, [key]: event.target.valueAsNumber },
    };
    this.runtimeSettingsError = validateViewerVisualQualitySettings(this.visualQualitySettingsDraft);
    this.queueSettingsApply('short');
  }

  public onVisualQualityAntiAliasingChanged(event: Event): void {
    if (!(event.target instanceof HTMLSelectElement)) {
      return;
    }
    const antiAliasing: string = event.target.value;
    if (
      !VIEWER_VISUAL_QUALITY_ANTI_ALIASING_OPTIONS.some(
        (option: (typeof VIEWER_VISUAL_QUALITY_ANTI_ALIASING_OPTIONS)[number]) => option.value === antiAliasing
      )
    ) {
      return;
    }
    this.visualQualitySettingsDraft = {
      ...this.visualQualitySettingsDraft,
      antiAliasing: antiAliasing as ViewerVisualQualityAntiAliasing,
    };
    this.runtimeSettingsError = validateViewerVisualQualitySettings(this.visualQualitySettingsDraft);
    this.queueSettingsApply('instant');
  }

  public resetRuntimeSettingsDraft(): void {
    this.runtimeSettingsDraft = defaultViewerRuntimeSettings();
    this.downloadBatchAutoDraft = true;
    this.visualQualitySettingsDraft = defaultViewerVisualQualitySettings();
    this.distributedRenderingDraft = true;
    this.navigationMinScreenSizeDraft = DEFAULT_VIEWER_NAVIGATION_MIN_SCREEN_SIZE;
    this.renderOrderStrategyDraft = DEFAULT_VIEWER_RENDER_ORDER_STRATEGY;
    this.disableTransparentMaterialsDraft = false;
    this.enableInstancingDraft = true;
    this.pointCloudAdaptivePointSizeDraft = true;
    this.hlodOcclusionRefinementDraft = true;
    this.freezeFrustumDraft = false;
    this.showPointCloudBoxesDraft = false;
    this.showObjectBoundingBoxesDraft = false;
    this.showViewerLoadingDraft = true;
    this.viewerProfilingEnabledDraft = false;
    this.viewerDeepProfilingEnabledDraft = false;
    this.adaptiveNavigationWheelDraft = true;
    this.adaptiveNavigationKeyboardDraft = true;
    this.geometryFormatDraft = DEFAULT_VIEWER_GEOMETRY_FORMAT;
    this.pointCloudGeometryFormatDraft = DEFAULT_POINT_CLOUD_GEOMETRY_FORMAT;
    this.frustumResponseFormatDraft = DEFAULT_VIEWER_FRUSTUM_RESPONSE_FORMAT;
    this.meshFrustumSortOrderDraft = DEFAULT_VIEWER_FRUSTUM_SORT_ORDER;
    this.runtimeSettingsError = null;
    this.queueSettingsApply('instant');
  }

  public onRenderOrderStrategyChanged(event: Event): void {
    if (!(event.target instanceof HTMLSelectElement)) {
      return;
    }

    const strategy: string = event.target.value;
    const isSelectedStrategy: boolean = this.renderOrderStrategies.some(
      (item: (typeof VIEWER_RENDER_ORDER_STRATEGIES)[number]) => item.value === strategy
    );
    if (isSelectedStrategy) {
      this.renderOrderStrategyDraft = strategy as ViewerRenderOrderStrategy;
      this.queueSettingsApply('delayed');
    }
  }

  public onNavigationMinScreenSizeChanged(event: Event): void {
    if (!(event.target instanceof HTMLSelectElement)) {
      return;
    }
    const value: number = Number(event.target.value);
    const option: (typeof VIEWER_NAVIGATION_MIN_SCREEN_SIZES)[number] | undefined = this.navigationMinScreenSizes.find(
      (item: (typeof VIEWER_NAVIGATION_MIN_SCREEN_SIZES)[number]) => item.value === value
    );
    if (option !== undefined) {
      this.navigationMinScreenSizeDraft = option.value;
      this.queueSettingsApply('instant');
    }
  }

  private setNavigationMinScreenSize(value: ViewerNavigationMinScreenSize): void {
    if (this.navigationMinScreenSize === value) {
      return;
    }
    this.navigationMinScreenSize = value;
    this.viewerNavigationCulling.invalidate();
    this.forceFrustumRefresh = true;
    this.lastFrustumCullingAt = Number.NEGATIVE_INFINITY;
    this.lastRenderedObjectIndex = 0;
    this.requestRender();
  }

  public onPointCloudGeometryFormatChanged(event: Event): void {
    if (!(event.target instanceof HTMLSelectElement)) {
      return;
    }

    const format: string = event.target.value;
    const option: (typeof VIEWER_POINT_CLOUD_GEOMETRY_FORMATS)[number] | undefined =
      this.pointCloudGeometryFormats.find(
        (item: (typeof VIEWER_POINT_CLOUD_GEOMETRY_FORMATS)[number]) => item.value === format
      );
    if (option !== undefined) {
      this.pointCloudGeometryFormatDraft = option.value;
      this.queueSettingsApply('delayed');
    }
  }

  public onGeometryFormatChanged(event: Event): void {
    if (!(event.target instanceof HTMLSelectElement)) {
      return;
    }

    const format: string = event.target.value;
    if (this.geometryFormats.some((item: (typeof VIEWER_GEOMETRY_FORMATS)[number]) => item.value === format)) {
      this.geometryFormatDraft = format as ViewerGeometryFormat;
      this.queueSettingsApply('delayed');
    }
  }

  public onFrustumResponseFormatChanged(event: Event): void {
    if (!(event.target instanceof HTMLSelectElement)) {
      return;
    }

    const format: string = event.target.value;
    if (
      this.frustumResponseFormats.some(
        (item: (typeof VIEWER_FRUSTUM_RESPONSE_FORMATS)[number]) => item.value === format
      )
    ) {
      this.frustumResponseFormatDraft = format as ViewerFrustumResponseFormat;
      this.queueSettingsApply('delayed');
    }
  }

  public onFrustumSortOrderChanged(event: Event): void {
    if (!(event.target instanceof HTMLSelectElement)) {
      return;
    }

    const sortOrder: string = event.target.value;
    if (this.frustumSortOrders.some((item: (typeof VIEWER_FRUSTUM_SORT_ORDERS)[number]) => item.value === sortOrder)) {
      this.meshFrustumSortOrderDraft = sortOrder as ViewerFrustumSortOrder;
      this.queueSettingsApply('delayed');
    }
  }

  // eslint-disable-next-line complexity
  public async applyRuntimeSettings(): Promise<void> {
    this.runtimeSettingsError = validateViewerRuntimeSettings(this.runtimeSettingsDraft);
    if (this.runtimeSettingsError === null) {
      this.runtimeSettingsError = validateViewerVisualQualitySettings(this.visualQualitySettingsDraft);
    }
    if (this.runtimeSettingsError !== null || this.runtimeSettingsBusy) {
      return;
    }
    const freezeFrustumChanged: boolean = this.freezeFrustum !== this.freezeFrustumDraft;
    const frustumDebugChanged: boolean =
      this.showPointCloudBoxes !== this.showPointCloudBoxesDraft ||
      this.showObjectBoundingBoxes !== this.showObjectBoundingBoxesDraft;
    const instancingChanged: boolean = this.enableInstancing !== this.enableInstancingDraft;
    const meshFrustumSortOrderChanged: boolean = this.meshFrustumSortOrder !== this.meshFrustumSortOrderDraft;
    const frustumSortOrderChanged: boolean = meshFrustumSortOrderChanged;
    const visualQualityChanged: boolean =
      viewerVisualQualityShaderSignature(this.visualQualitySettings) !==
      viewerVisualQualityShaderSignature(this.visualQualitySettingsDraft);
    this.setFrustumFrozen(this.freezeFrustumDraft);
    this.setNavigationMinScreenSize(this.navigationMinScreenSizeDraft);
    this.showPointCloudBoxes = this.showPointCloudBoxesDraft;
    this.showObjectBoundingBoxes = this.showObjectBoundingBoxesDraft;
    this.showViewerLoading = this.showViewerLoadingDraft;
    if (this.pointCloudAdaptivePointSize !== this.pointCloudAdaptivePointSizeDraft) {
      this.pointCloudAdaptivePointSize = this.pointCloudAdaptivePointSizeDraft;
      this.requestRender();
    }
    if (this.hlodOcclusionRefinement !== this.hlodOcclusionRefinementDraft) {
      this.hlodOcclusionRefinement = this.hlodOcclusionRefinementDraft;
      this.photogrammetry.invalidateVisibility();
      this.requestRender();
    }
    this.viewerProfilingEnabled = this.viewerProfilingEnabledDraft;
    this.viewerDeepProfilingEnabled = this.viewerDeepProfilingEnabledDraft;
    this.adaptiveNavigationWheel = this.adaptiveNavigationWheelDraft;
    this.adaptiveNavigationKeyboard = this.adaptiveNavigationKeyboardDraft;
    this.lastNavigationSpeedUpdateTime = 0;
    this.navigationSpeedSampleRevision++;
    this.navigationSpeedSampling = false;
    this.meshFrustumSortOrder = this.meshFrustumSortOrderDraft;
    this.visualQualitySettings = { ...this.visualQualitySettingsDraft };
    this.applyVisualQualityFramebufferSettings();
    if (this.viewerProfiler !== undefined) {
      this.viewerProfiler.configure(
        {
          enabled: this.viewerProfilingEnabled,
          deepGpu: this.viewerDeepProfilingEnabled,
        },
        this.gl
      );
      this.viewerProfilerSnapshot = this.viewerProfiler.snapshot();
    }
    this.enableInstancing = this.enableInstancingDraft;
    if (frustumDebugChanged || frustumSortOrderChanged || instancingChanged || visualQualityChanged) {
      if (
        instancingChanged &&
        this.objectsQueue !== undefined &&
        this.objectsQueue !== null &&
        this.graphics !== undefined &&
        this.graphics !== null
      ) {
        this.clearTrackedSceneBaseObjects(this.objectsQueue, this.graphics);
      }
      this.lastRenderedSceneSignature = null;
      this.applyVisualControlChanges();
      if (!this.freezeFrustum && this.selectedSceneId !== null && this.ingestState.phase === 'ready') {
        this.scheduleFrustumRefresh(this.selectedSceneId, 0, 'runtime-settings');
      }
    }
    if (
      freezeFrustumChanged &&
      !this.freezeFrustum &&
      this.selectedSceneId !== null &&
      this.ingestState.phase === 'ready'
    ) {
      this.scheduleFrustumRefresh(this.selectedSceneId, 0, 'frustum-unfrozen');
    }
    await this.router.navigate([], {
      relativeTo: this.route,
      queryParams: writeViewerUrlSettings(
        this.runtimeSettingsDraft,
        this.distributedRenderingDraft,
        this.renderOrderStrategyDraft,
        this.disableTransparentMaterialsDraft,
        this.freezeFrustumDraft,
        this.showPointCloudBoxesDraft,
        this.enableInstancingDraft,
        this.showViewerLoadingDraft,
        this.viewerProfilingEnabledDraft,
        this.viewerDeepProfilingEnabledDraft,
        this.useCompactFrustum,
        this.geometryFormatDraft,
        this.visualQualitySettingsDraft,
        this.pointCloudGeometryFormatDraft,
        this.showObjectBoundingBoxesDraft,
        this.meshFrustumSortOrderDraft,
        undefined,
        null,
        this.navigationMinScreenSizeDraft,
        this.downloadBatchAutoDraft,
        this.frustumResponseFormatDraft,
        this.pointCloudAdaptivePointSizeDraft,
        this.hlodOcclusionRefinementDraft
      ),
      queryParamsHandling: 'merge',
    });
    this.runtimeSettingsMessage = 'Settings applied.';
    this.changeDetector.markForCheck();
    return;
  }

  public fps: number = 0;
  public onDistributedRenderingChanged(event: Event): void {
    if (event.target instanceof HTMLInputElement) {
      this.distributedRenderingDraft = event.target.checked;
      this.queueSettingsApply('instant');
    }
  }

  public onDisableTransparentMaterialsChanged(event: Event): void {
    if (event.target instanceof HTMLInputElement) {
      this.disableTransparentMaterialsDraft = event.target.checked;
      this.queueSettingsApply('delayed');
    }
  }

  public onPointCloudAdaptivePointSizeChanged(event: Event): void {
    if (event.target instanceof HTMLInputElement) {
      this.pointCloudAdaptivePointSizeDraft = event.target.checked;
      this.queueSettingsApply('delayed');
    }
  }

  public onHlodOcclusionRefinementChanged(event: Event): void {
    if (event.target instanceof HTMLInputElement) {
      this.hlodOcclusionRefinementDraft = event.target.checked;
      this.queueSettingsApply('delayed');
    }
  }

  public onInstancingChanged(event: Event): void {
    if (event.target instanceof HTMLInputElement) {
      this.enableInstancingDraft = event.target.checked;
      this.queueSettingsApply('delayed');
    }
  }

  public onFreezeFrustumChanged(event: Event): void {
    if (event.target instanceof HTMLInputElement) {
      this.freezeFrustumDraft = event.target.checked;
      this.queueSettingsApply('instant');
    }
  }

  public onShowPointCloudBoxesChanged(event: Event): void {
    if (event.target instanceof HTMLInputElement) {
      this.showPointCloudBoxesDraft = event.target.checked;
      this.queueSettingsApply('instant');
    }
  }

  public onShowObjectBoundingBoxesChanged(event: Event): void {
    if (event.target instanceof HTMLInputElement) {
      this.showObjectBoundingBoxesDraft = event.target.checked;
      this.queueSettingsApply('instant');
    }
  }

  public onAdaptiveNavigationWheelChanged(event: Event): void {
    if (event.target instanceof HTMLInputElement) {
      this.adaptiveNavigationWheelDraft = event.target.checked;
      this.queueSettingsApply('instant');
    }
  }

  public onAdaptiveNavigationKeyboardChanged(event: Event): void {
    if (event.target instanceof HTMLInputElement) {
      this.adaptiveNavigationKeyboardDraft = event.target.checked;
      this.queueSettingsApply('instant');
    }
  }

  public onShowViewerLoadingChanged(event: Event): void {
    if (event.target instanceof HTMLInputElement) {
      this.showViewerLoadingDraft = event.target.checked;
      this.queueSettingsApply('instant');
    }
  }

  public onViewerProfilingEnabledChanged(event: Event): void {
    if (event.target instanceof HTMLInputElement) {
      this.viewerProfilingEnabledDraft = event.target.checked;
      if (!this.viewerProfilingEnabledDraft) {
        this.viewerDeepProfilingEnabledDraft = false;
      }
      this.queueSettingsApply('instant');
    }
  }

  public onViewerDeepProfilingEnabledChanged(event: Event): void {
    if (event.target instanceof HTMLInputElement) {
      this.viewerDeepProfilingEnabledDraft = event.target.checked;
      this.queueSettingsApply('instant');
    }
  }

  public get cameraPathRecording(): boolean {
    return this.cameraPathRecorderTimer !== null;
  }

  public get cameraPathRecordedKeys(): number {
    return this.cameraPathRecorder?.keyCount ?? 0;
  }

  // Records the camera as a benchmark path (dev-scripts/benchmark/run-viewer-benchmark.mjs --path-file).
  public toggleCameraPathRecording(): void {
    if (this.cameraPathRecorderTimer === null) {
      this.cameraPathRecorder = new HsCameraPathRecorder();
      this.cameraPathRecorder.start(this.cameraPositionDebug, this.cameraDirectionDebug, performance.now());
      this.cameraPathRecorderTimer = window.setInterval(() => {
        this.cameraPathRecorder?.sample(this.cameraPositionDebug, this.cameraDirectionDebug, performance.now());
        this.changeDetector.markForCheck();
      }, CAMERA_PATH_SAMPLE_INTERVAL_MS);
      return;
    }
    window.clearInterval(this.cameraPathRecorderTimer);
    this.cameraPathRecorderTimer = null;
    const recorder: HsCameraPathRecorder | null = this.cameraPathRecorder;
    this.cameraPathRecorder = null;
    if (recorder === null) {
      return;
    }
    recorder.sample(this.cameraPositionDebug, this.cameraDirectionDebug, performance.now());
    const recordedAt: Date = new Date();
    const name: string = `recorded-${recordedAt.toISOString().slice(0, 19).replace(/[:T]/g, '-')}`;
    const blob: Blob = new Blob([JSON.stringify(recorder.finish(name, this.selectedSceneId, recordedAt), null, 2)], {
      type: 'application/json',
    });
    const blobUrl: string = URL.createObjectURL(blob);
    const link: HTMLAnchorElement = this.document.createElement('a');
    link.href = blobUrl;
    link.download = `${name}.json`;
    link.click();
    URL.revokeObjectURL(blobUrl);
    this.changeDetector.markForCheck();
  }

  public resetViewerProfiler(): void {
    this.viewerProfiler.reset();
    this.viewerProfilerSnapshot = this.viewerProfiler.snapshot();
    this.changeDetector.markForCheck();
  }

  private cameraPathRecorder: HsCameraPathRecorder | null = null;
  private cameraPathRecorderTimer: number | null = null;
  public ingestApiBaseUrl: string = this.resolveDefaultIngestApiBaseUrl();
  public ingestTag: string = '';
  public ingestHighlightCollisions: boolean = true;
  public registryUseTagFilter: boolean = false;
  public modelsRegistry: HypersceneModelView[] = [];
  public selectedModelId: string | null = null;
  public similarityBulkModelIds: string[] = [];
  public selectedSceneModelInstance: HypersceneSceneModelInstanceView | null = null;
  public selectedSceneModelInstanceLoading: boolean = false;
  public selectedSceneModelInstanceError: string | null = null;
  private selectedSceneModelInstanceRequestId: number = 0;
  public sceneModelActionPending: boolean = false;
  public selectedModelRenameDraft: string = '';
  public scenesRegistry: HypersceneSceneView[] = [];
  public selectedSceneId: string | null = null;
  public selectedSceneRenameDraft: string = '';
  public processingsRegistry: HypersceneProcessingView[] = [];

  public sceneSettingsBusy: boolean = false;
  public sceneSettingsStatus: { kind: 'saved' | 'error'; text: string } | null = null;

  public get selectedSceneSettings(): HypersceneSceneSettings {
    return this.selectedScene?.settings ?? this.viewerSceneSettings ?? {};
  }

  public get selectedSceneHasStartCamera(): boolean {
    return (this.selectedSceneSettings.startCamera ?? '').length > 0;
  }

  public useCurrentCameraAsSceneStart(): void {
    const sceneId: string | null = this.selectedSceneId;
    const startCamera: string | null = encodeCameraView({
      position: this.cameraPositionDebug,
      direction: this.cameraDirectionDebug,
    });
    if (sceneId === null || startCamera === null) {
      return;
    }
    void this.updateSceneSettings(sceneId, { startCamera }, 'Start camera saved.');
  }

  public clearSceneStartCamera(): void {
    if (this.selectedSceneId !== null) {
      void this.updateSceneSettings(this.selectedSceneId, { startCamera: null }, 'Start camera cleared.');
    }
  }

  private async updateSceneSettings(sceneId: string, patch: HypersceneSceneSettings, savedText: string): Promise<void> {
    if (this.sceneSettingsBusy) {
      return;
    }
    this.sceneSettingsBusy = true;
    this.sceneSettingsStatus = null;
    try {
      // The other field is merged from the server's copy so a stale registry cannot overwrite it.
      const current: HypersceneSceneView = await this.ingestAdapter.getScene(this.ingestApiBaseUrl, sceneId);
      const updated: HypersceneSceneView = await this.ingestAdapter.putSceneSettings(this.ingestApiBaseUrl, sceneId, {
        ...current.settings,
        ...patch,
      });
      const settings: HypersceneSceneSettings | null = updated.settings ?? null;
      this.scenesRegistry = this.scenesRegistry.map((scene: HypersceneSceneView) =>
        scene.id === sceneId ? { ...scene, settings } : scene
      );
      if (this.ingestState.sceneId === sceneId) {
        this.viewerSceneSettings = settings;
      }
      this.sceneSettingsStatus = { kind: 'saved', text: savedText };
    } catch (error: unknown) {
      this.sceneSettingsStatus = {
        kind: 'error',
        text: error instanceof Error ? error.message : 'Failed to save scene settings.',
      };
    } finally {
      this.sceneSettingsBusy = false;
      this.changeDetector.markForCheck();
    }
  }

  private applySceneDefaults(scene: HypersceneSceneView): void {
    this.viewerSceneSettings = scene.settings ?? null;
    if (this.sceneDefaultsAppliedFor === scene.id) {
      return;
    }
    this.sceneDefaultsAppliedFor = scene.id;
    // pendingCameraView and pendingViewpointId hold the explicit URL values, which win over the defaults.
    this.pendingCameraView = resolveSceneStartCamera(this.pendingCameraView, this.pendingViewpointId, scene.settings);
    const profile: string | null = resolveSceneQualityProfile(
      this.route.snapshot.queryParamMap.get('qualityProfile'),
      scene.settings
    );
    if (profile !== null) {
      // Through the URL, so the profile stays in effect for later URL writes and shared links.
      void this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { qualityProfile: profile },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    }
  }

  public get selectedScene(): HypersceneSceneView | undefined {
    return this.scenesRegistry.find((scene: HypersceneSceneView) => scene.id === this.selectedSceneId);
  }

  public get cameraPositionDebug(): HsHypersceneTypes.Array3 {
    return this.camera.worldMatrix.getPositionArray();
  }

  public get cameraDirectionDebug(): ViewerCameraVector {
    const values: HsHypersceneTypes.Array16 = this.camera.worldMatrix.values;
    return [-values[8], -values[9], -values[10]];
  }
  public sceneOpacity: number = 1;
  /** The section the toolbar and the Tools tab edit; its planes are the scene's clipping. */
  public readonly section: HsSectionTool = new HsSectionTool();
  /** The clipping planes the scene's shaders read; section moves rewrite them in place. */
  private sectionClipping: HsClippingStorage | null = null;
  /** The next click on the model places the section: through the point, along the clicked face, or nothing. */
  public sectionPickArmed: false | 'point' | 'face' = false;
  /** While on, a left drag moves the section plane along its normal instead of orbiting. */
  public sectionDragArmed: boolean = false;
  /** Measurement and marker clicks snap to corners, edge middles and edges of the mesh under them. */
  public measurementSnapToEdges: boolean = true;
  /** Parallel projection: a narrow field of view from far away, so plans and elevations look orthographic. */
  public parallelProjection: boolean = false;
  /** The field of view to return to when parallel projection turns off. */
  private perspectiveFov: number = 1;
  private perspectiveFar: number = 0;
  private viewerLabels: ViewerLabels | null = null;
  /** X-ray: everything but the selection is drawn faint. */
  public xrayEnabled: boolean = false;
  /** Extent of the open scene from the API, for fitting, standard views and the section range. */
  private sceneBounds: HypersceneBoundingBoxView | null = null;
  /** The point a right drag orbits around: the model point last pressed, or the center of the last framing. */
  private readonly orbitPivot: HsVector3 = new HsVector3(0, 0, 0);
  /** Scene nodes drawn highlighted: the selection and, for a group, its descendants. */
  private selectionHighlightNodeIds: ReadonlySet<string> = new Set();
  private readonly selectionHighlight: ViewerSelectionHighlight = new ViewerSelectionHighlight({
    meshes: () => this.sceneSource.objects.meshes,
    collectionRenderKeys: (collection: HsGraphicCollection) =>
      this.sceneSource.objects.renderKeysByCollection.get(collection),
    nodeIdOf: (renderKey: string) => hsNodeIdOfRenderKey(renderKey),
    graphics: () => this.graphics ?? null,
    material: () => this.selectionHighlightMaterial(),
  });
  /** The Tools tab's measurements: the SDK tool with its points, the on/off switch and the saved ones. */
  public readonly measurements: ViewerMeasurements = new ViewerMeasurements((history: HypersceneMeasurementEntry[]) =>
    this.annotations.storeLocalMeasurements(history)
  );
  public isFullscreen: boolean = false;
  public viewpoints: HypersceneViewpoint[] = [];
  public selectedViewpointId: string | null = null;
  public viewpointNameDraft: string = '';
  public viewpointCreateNameDraft: string = 'Viewpoint 1';
  public viewpointRenameActive: boolean = false;
  public markerPlacementEnabled: boolean = false;
  public markersVisible: boolean = true;
  public markers: HypersceneMarker[] = [];
  public selectedMarkerId: string | null = null;
  public markerNameDraft: string = '';
  public viewerDataTransferText: string = '';
  public viewerDataImportMode: HypersceneTransferImportMode = 'replace';
  private readonly sceneTree: HsSceneTree = new HsSceneTree({
    getSceneRootNodes: (sceneId: string) => this.ingestAdapter.getSceneRootNodes(this.ingestApiBaseUrl, sceneId),
    getNodeChildren: (nodeId: string) => this.ingestAdapter.getNodeChildren(this.ingestApiBaseUrl, nodeId),
    getNodeChildrenBulk: (nodeIds: readonly string[]) =>
      this.ingestAdapter.getNodeChildrenBulk(this.ingestApiBaseUrl, [...nodeIds]),
    getNodeRoute: (nodeId: string) => this.ingestAdapter.getNodeRoute(this.ingestApiBaseUrl, nodeId),
    getNodesVisibility: (sceneId: string, nodeIds: readonly string[]) =>
      this.ingestAdapter.getSceneNodesVisibility(
        this.ingestApiBaseUrl,
        sceneId,
        [...nodeIds],
        this.visibilitySessionId
      ),
  });

  public get sceneTreeRoots(): HypersceneSceneTreeNode[] {
    return this.sceneTree.roots;
  }

  public set sceneTreeRoots(value: HypersceneSceneTreeNode[]) {
    this.sceneTree.roots = value;
  }
  public selectedSceneNodeId: string | null = null;
  public selectedSceneNodeTitle: string | null = null;
  public selectedSceneNodeAttributes: HypersceneAttributeView[] = [];
  public selectedSceneNodeVisibility: HypersceneVisibilityState = 'unknown';
  public enterpriseAttributeName: string = '';
  public enterpriseAttributeValue: string = '';
  public enterpriseAttributeValues: string[] = [];
  public enterpriseFilterResults: HypersceneSceneNodeFilterResultView[] = [];
  public enterpriseSelectedNodeId: string | null = null;
  public enterpriseFilterLoading: boolean = false;
  public enterpriseFilterMessage: string = 'Set attribute filter to search scene nodes.';
  public sceneTreeLoading: boolean = false;
  public selectedSceneNodeLoading: boolean = false;
  /**
   * This browser's visibility session: Hide, Isolate and Show all apply to it only (kept in browser storage, so they
   * survive page loads and leave the scene unchanged for others). Tools → New visibility session replaces it.
   */
  public visibilitySessionId: string = loadVisibilitySessionId();
  /** Descendants of the selected node as the API counts them; null until its details are loaded. */
  private selectedSceneNodeDescendantCount: number | null = null;
  /** Visibility requests in click order; see `persistSceneVisibility`. */
  private sceneVisibilityRequests: Promise<void> = Promise.resolve();
  public viewerLoadingState: HypersceneViewerLoadingState = {
    message: '',
    completed: 0,
    total: null,
  };

  public get viewerLoadingActive(): boolean {
    return this.viewerGeometryLoadingActive || this.photogrammetryLoading.status.busy;
  }

  /** The scene requests the overlay's geometry line covers; photogrammetry streaming has its own line. */
  public get viewerGeometryLoadingActive(): boolean {
    return (
      this.sceneLoadInProgress ||
      this.frustumRefreshInProgress ||
      this.sceneTreeLoading ||
      this.selectedSceneNodeLoading ||
      this.enterpriseFilterLoading
    );
  }

  public get viewerPhotogrammetryLoadingMessage(): string {
    const status: HsPhotogrammetryLoadStatus = this.photogrammetryLoading.status;
    const parts: string[] = [];
    if (status.manifestsLoading > 0) {
      parts.push(`${status.manifestsLoading} manifest${status.manifestsLoading === 1 ? '' : 's'}`);
    }
    if (status.subtreesLoading > 0) {
      parts.push(`${status.subtreesLoading} subtree${status.subtreesLoading === 1 ? '' : 's'}`);
    }
    parts.push(`${status.nodesLoading + status.nodesDecoded} loading`, `${status.nodesQueued} queued`);
    if (status.nodesRetrying > 0) {
      parts.push(`${status.nodesRetrying} retrying`);
    }
    parts.push(`${(status.downloadedBytes / 1e6).toFixed(1)} MB`);
    const cache: HsHlodPayloadCacheStats | null = this.photogrammetry.cacheStats();
    if (cache !== null && cache.hits + cache.misses > 0) {
      parts.push(`cache ${cache.hits}/${cache.hits + cache.misses} hits, ${(cache.hitBytes / 1e6).toFixed(1)} MB`);
    }
    return `Photogrammetry: ${parts.join(', ')}`;
  }

  /** Counters of the persistent HLOD document cache; the benchmark harness reads them from the component. */
  public photogrammetryCacheStats(): HsHlodPayloadCacheStats | null {
    return this.photogrammetry.cacheStats();
  }

  /** Done share of the most work seen pending since the stream last settled, or null while nothing is known. */
  public get viewerPhotogrammetryProgressPercent(): number | null {
    const { status, peakPending }: HypersceneViewerPhotogrammetryLoadingState = this.photogrammetryLoading;
    if (peakPending <= 0 || !status.rootReady) {
      return null;
    }
    return Math.round((1 - Math.min(1, pendingPhotogrammetryWork(status) / peakPending)) * 100);
  }

  /** Set when a geometry request finishes for the open scene; the empty-canvas hint stops with it. */
  private firstGeometryRefreshFinished: boolean = false;

  /** The canvas is empty only because the first geometry is still on its way. */
  public get viewerAwaitingFirstGeometry(): boolean {
    return (
      this.showViewerLoading &&
      isAwaitingFirstGeometry({
        sceneId: this.ingestState.sceneId,
        error: this.ingestState.error,
        renderedObjectsCount:
          this.ingestState.renderedMeshObjectsCount +
          this.ingestState.renderedLineObjectsCount +
          this.ingestState.renderedPointCloudObjectsCount,
        firstRefreshFinished: this.firstGeometryRefreshFinished,
      })
    );
  }

  public get viewerLoadingPanelVisible(): boolean {
    return this.showViewerLoading && this.viewerLoadingActive;
  }

  public get viewerLoadingMessage(): string {
    if (this.viewerLoadingState.message.length > 0) {
      return this.viewerLoadingState.message;
    }
    if (this.frustumRefreshInProgress) {
      return 'Updating visible scene geometry...';
    }
    if (this.sceneLoadInProgress) {
      return 'Loading scene...';
    }
    return 'Loading viewer data...';
  }

  public get viewerLoadingProgressPercent(): number | null {
    if (this.viewerLoadingState.total === null || this.viewerLoadingState.total <= 0) {
      return null;
    }
    return Math.round((this.viewerLoadingState.completed / this.viewerLoadingState.total) * 100);
  }

  public sceneStats: HypersceneSceneStats = {
    queueObjectsCount: 0,
    frustumVisibleObjectsCount: 0,
    frustumSortedObjectsCount: 0,
    frustumCullingMs: 0,
    distributedRendering: false,
    clippingEnabled: false,
    measurementEnabled: false,
    downsampleRate: 1,
  };
  public sceneLoadDebug: HypersceneSceneLoadDebugState = {
    loadMode: 'root-only',
    rootNodesCount: 0,
    totalSceneObjectsCount: 0,
    visibleRootNodesCount: 0,
    meshIdsCount: 0,
    lineIdsCount: 0,
    pointCloudIdsCount: 0,
    meshesBinaryBytes: 0,
    linesBinaryBytes: 0,
    pointCloudsBinaryBytes: 0,
    decodedMeshesCount: 0,
    decodedLinesCount: 0,
    decodedPointCloudsCount: 0,
    meshRejectedInvalidVertices: 0,
    meshRejectedEmptyIndices: 0,
    meshRejectedInvalidIndices: 0,
    meshRejectedIndexedBuildFailed: 0,
    meshRejectedRegularFallbackFailed: 0,
    lastUpdatedAt: 'n/a',
    lastError: 'none',
  };
  public sceneGeometryDebug: HypersceneSceneGeometryDebugState = {
    loadedMeshGeometriesCount: 0,
    loadedLineGeometriesCount: 0,
    loadedPointCloudGeometriesCount: 0,
    loadedTrianglesCount: 0,
    loadedLineSegmentsCount: 0,
    loadedPointsCount: 0,
  };
  public sceneCacheBudgetDebug: HypersceneSceneCacheBudgetDebugState = {
    meshBytes: 0,
    meshBudgetBytes: MESH_CACHE_BUDGET_BYTES,
    meshCount: 0,
    textureBytes: 0,
    textureBudgetBytes: TEXTURE_CACHE_BUDGET_BYTES,
    textureCount: 0,
  };
  public sceneOverviewDebug: HypersceneSceneOverviewDebugState = {
    frustumMeshObjectsCount: 0,
    frustumLineObjectsCount: 0,
    frustumPointCloudObjectsCount: 0,
  };
  public sceneRenderTotals: HypersceneSceneRenderTotals = {
    drawCalls: 0,
    triangles: 0,
    totalInstancedBatches: 0,
    culledInstancedBatches: 0,
    instancedDrawCalls: 0,
    instancedInstances: 0,
  };
  public readonly downsampleMin: number = HsRender.minDownsampleRate;
  public readonly downsampleMax: number = Math.min(HsRender.maxDownsampleRate, MAX_VIEWER_DOWNSAMPLE_RATE);
  public ingestState: HypersceneIngestState = {
    phase: 'idle',
    message: '',
    fileName: null,
    parserType: 'ifc',
    documentId: null,
    processingId: null,
    modelId: null,
    rootNodeId: null,
    sceneId: null,
    sceneRootNodesCount: 0,
    geometryIdsCount: 0,
    meshGeometryIdsCount: 0,
    lineGeometryIdsCount: 0,
    pointCloudGeometryIdsCount: 0,
    renderedMeshObjectsCount: 0,
    renderedLineObjectsCount: 0,
    renderedPointCloudObjectsCount: 0,
    collidedPointsCount: 0,
    meshesBinaryBytes: 0,
    linesBinaryBytes: 0,
    pointCloudsBinaryBytes: 0,
    processingStatus: null,
    error: null,
  };

  @ViewChild('canvasDocument', { static: false })
  private readonly canvasDocument: ElementRef<HTMLCanvasElement> | null = null;

  @ViewChild('labelsLayer', { static: false })
  private readonly labelsLayer: ElementRef<HTMLElement> | null = null;

  private viewer: HsHypersceneViewer | null = null;
  private viewerFrameSpan: HsViewerProfilerSpan | null = null;
  /** The benchmark probe and its hooks, when the page runs with `viewerBenchmark=true`. */
  private probeHandle: HsViewerProbeHandle | null = null;
  private viewerDrawSpan: HsViewerProfilerSpan | null = null;
  private viewerGpuSpan: HsViewerGpuSpan | null = null;
  private frame: number = 0;
  private time: number = performance.now();

  private readonly camera: HsPerspectiveCamera = new HsPerspectiveCamera(getUuid(), 'camera');

  private readonly cameraPoint: HsVector3 = new HsVector3(400, 150, 400);
  private readonly observablePoint: HsVector3 = new HsVector3(0, 0, 0);

  private gl: WebGL2RenderingContext | null = null;

  private objectsLayer: HsGraphicLayer | null = null;
  private objectsQueue: HsGraphicQueue | null = null;

  private readonly render: HsRender = new HsRender();
  private graphics: HsGraphics | null = null;
  private readonly raycast: HsRaycast = new HsRaycast();
  private readonly raycastOutput: HsRaycastOutput = new HsRaycastOutput();

  private canvasSize: HsCanvasSize | null = null;

  private renderObjects: HsRenderObjects<HsGraphicObject, HsGraphicLayer> | null = null;

  private readonly cameraToolsHandler: HsCameraToolsHandler = new HsCameraToolsHandler(this.camera);
  private readonly controlActionsHandler: HsControlActionsHandler = this.cameraToolsHandler.controlActionsHandler;
  public selectedCameraToolType: HsCameraToolType | null = null;

  public touchNavigationControlsVisible: boolean =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(any-pointer: coarse)').matches;
  private readonly touchGestures: HsTouchGestureRecognizer = new HsTouchGestureRecognizer(this.controlActionsHandler, {
    resolvePivot: (position: HsVector2, gestureId: number): HsVector3 | null =>
      this.resolveTouchPivot(position, gestureId),
    getFallbackDistance: (): number => this.getTouchZoomFallbackDistance(),
    getPanScale: (): number => this.getTouchPanScale(),
    onDoubleTap: (position: HsVector2): void => this.focusCameraAtScreenPoint(position.x, position.y),
    onNavigate: (): void => this.requestRender(),
  });
  private touchPivotGestureId: number = 0;
  private touchPivotRequestedAt: number = 0;
  private navigationPickPoint: HsVector3 | null = null;
  private navigationPickDistance: number | null = null;
  private navigationPickRequestedAt: number = 0;
  private touchMoveActionType: HsControlActionType | null = null;
  private touchMoveFrameId: number = 0;
  private touchNavigationActive: boolean = false;
  private navigationSpeedMultiplier: number = 1;
  private lastNavigationSpeedUpdateTime: number = 0;
  private navigationSpeedSampling: boolean = false;
  private navigationSpeedSampleRevision: number = 0;
  /** The mouse button held on the canvas, or the one the benchmark probe simulates. */
  private get mouseButton(): number | null {
    return this.navigationButtonOverride ?? this.viewerInput?.button ?? null;
  }

  private frustumUpdateTime: number = 0;
  private canvasResizeObserver: ResizeObserver | null = null;
  private distributedRendering: boolean = false;
  private readonly viewerSpatialCulling: HsSpatialCulling<HsGraphicObject> = new HsSpatialCulling(
    () => new Worker(new URL('./services/sphere-culling.worker.ts', import.meta.url), { type: 'classic' }),
    () => !this.sceneLoadInProgress && !this.frustumRefreshInProgress,
    (message: string) => {
      // eslint-disable-next-line no-console
      console.error(`Viewer spatial culling: ${message}`);
      this.viewerProfiler.recordEvent('frustum.index-error', message);
    }
  );

  private readonly filter: HsViewerCullingFilter = new HsViewerCullingFilter({
    order: (queue: HsGraphicQueue) => this.getViewerRenderOrder(queue),
    spatial: this.viewerSpatialCulling,
    navigation: this.viewerNavigationCulling,
    sizeCulling: (queue: HsGraphicQueue) =>
      queue === this.objectsQueue
        ? {
            canvasSize: this.canvasSize,
            threshold: this.navigationMinScreenSize,
            navigating: this.navigationCullingActive,
            now: this.lastFrustumCullingAt,
          }
        : null,
    alwaysDrawn: (object: HsGraphicObject) => object.name.startsWith('debug-'),
    keepWhileNavigating: (object: HsGraphicObject) =>
      object.name.startsWith('selected-') || object.name === 'annotation-markers',
  });

  private readonly sorter: HsRenderSorter<HsGraphicObject, HsPerspectiveCamera, HsGraphicLayer> = new HsRenderSorter(
    (camera: HsPerspectiveCamera, renderObjects: HsRenderObjects<HsGraphicObject, HsGraphicLayer>) => {
      hsSortByCameraDistance(renderObjects.sortedObjects, camera.worldMatrix, true);
      return renderObjects;
    }
  );

  private readonly subscription: Subscription = new Subscription();
  private readonly hypersceneSubscription: HypersceneSubscription = new HypersceneSubscription();

  private readonly ingestAdapter: HypersceneIngestAdapterService = new HypersceneIngestAdapterService();
  private readonly annotations: ViewerAnnotationsStore = new ViewerAnnotationsStore(
    this.ingestAdapter,
    () => this.ingestApiBaseUrl
  );

  private selectedNodeBoundingBox: HypersceneBoundingBoxView | null = null;
  private selectedNodeBoundingBoxNodeId: string | null = null;
  private readonly homeCameraPoint: HsVector3 = new HsVector3(8, 3, 8);
  private readonly homeObservablePoint: HsVector3 = HsVector3.makeZeroVector();

  private sceneEpoch: number = 0;

  private pointCloudDetailTimer: number | null = null;
  private pointCloudNavigationActive: boolean = false;
  private readonly pointCloudDrawBudget: HsPointCloudDrawBudget = new HsPointCloudDrawBudget({
    maxPoints: HS_DESKTOP_POINT_CLOUD_BUDGET_MAX_POINTS,
    minPoints: POINT_CLOUD_DRAW_MIN_POINTS,
    targetGpuMs: POINT_CLOUD_DRAW_TARGET_GPU_MS,
    targetIntervalMs: POINT_CLOUD_DRAW_TARGET_INTERVAL_MS,
  });
  private readonly pointCloudGpuTimer: HsGpuFrameTimer = new HsGpuFrameTimer();
  private pointCloudDrawGrowing: boolean = false;
  private pointCloudDrawnPoints: number = 0;
  private pointCloudLoadedPoints: number = 0;
  /** Points the loaded blocks draw at the target spacing; the draw budget grows towards it at rest. */
  private pointCloudWantedPoints: number = 0;
  // Occluded block skipping: a tile-ID pass of every loaded block in the view tells which blocks have no
  // visible pixel; those are skipped until a later pass sees them again.
  private pointCloudVisibilityPass: HsTileVisibilityPass<HsGraphicPoints> | null = null;
  private readonly pointCloudOcclusion: HsOcclusionHysteresis<HsGraphicPoints> =
    new HsOcclusionHysteresis<HsGraphicPoints>(POINT_CLOUD_OCCLUSION_HIDE_PASSES);
  private pointCloudOcclusionEnabled: boolean = true;
  private pointCloudOcclusionStale: boolean = true;
  private pointCloudOcclusionSignature: string = '';
  /** Camera of the last pass; programmatic camera changes do not show up as `cameraChanged`. */
  private readonly pointCloudOcclusionCamera: HsMatrix4 = HsMatrix4.makeIdentityMatrix();
  private pointCloudOcclusionLastPassAt: number = Number.NEGATIVE_INFINITY;
  private pointCloudOcclusionChecked: readonly HsGraphicPoints[] = [];
  private pointCloudOcclusionDraws: HsVisibilityDraw<HsGraphicPoints>[] | null = null;
  private pointCloudOcclusionPollTimer: number | null = null;
  private pointCloudOccludedBlocks: number = 0;
  // Adaptive point sizes: the loaded descendants of each loaded block, found again when the blocks change.
  private pointCloudDescendants: Uint32Array[] = [];
  private pointCloudDescendantsSignature: string = '';
  private lastFrameStartedAt: number = 0;
  private lastFrameNavigating: boolean = false;
  private lastFrameContinuous: boolean = false;
  private sceneGeometryDebugUpdateTimer: number | null = null;
  private readonly sceneSource: HsSceneSource = new HsSceneSource({
    api: {
      getGeometryPayload: (
        kind: HypersceneGeometryKind,
        geometryIds: readonly string[],
        request: HsGeometryPayloadRequest
      ): Promise<ArrayBuffer> => {
        const ids: string[] = [...geometryIds];
        if (kind === 'lines') {
          return this.ingestAdapter.getLinesBinary(this.ingestApiBaseUrl, ids, request.signal);
        }
        if (kind === 'pointClouds') {
          return this.ingestAdapter.getPointCloudsBinary(
            this.ingestApiBaseUrl,
            ids,
            request.signal,
            request.format as ViewerGeometryFormat
          );
        }
        const canonical: string[] = [...(request.canonicalGeometryIds ?? [])];
        return canonical.length === 0
          ? this.ingestAdapter.getMeshesBinary(
              this.ingestApiBaseUrl,
              ids,
              request.signal,
              request.format as ViewerGeometryFormat
            )
          : this.ingestAdapter.getMeshesBinary(
              this.ingestApiBaseUrl,
              ids,
              request.signal,
              request.format as ViewerGeometryFormat,
              canonical
            );
      },
      getMaterials: (ids: readonly string[]) => this.ingestAdapter.getMaterialsByIds(this.ingestApiBaseUrl, [...ids]),
      getNodeTreeOriginalMaterials: (rootNodeId: string) =>
        this.ingestAdapter.getNodesTreeOriginalMaterials(this.ingestApiBaseUrl, rootNodeId),
      getSceneRootNodes: (sceneId: string) => this.ingestAdapter.getSceneRootNodes(this.ingestApiBaseUrl, sceneId),
      getDocuments: (ids: readonly string[]) => this.ingestAdapter.getTextures(this.ingestApiBaseUrl, [...ids]),
      getDocument: (id: string) => this.ingestAdapter.getTexture(this.ingestApiBaseUrl, id),
      getOriginalMeshSources: (nodeIds: readonly string[]) =>
        this.ingestAdapter.getOriginalMeshSources(this.ingestApiBaseUrl, [...nodeIds]),
      getSceneFrustum: (sceneId: string, request: HypersceneFrustumRequestInfo, signal?: AbortSignal) => {
        const useCompact: boolean = this.useCompactFrustum;
        const format: ViewerFrustumResponseFormat = this.frustumResponseFormat;
        const call = (): Promise<HypersceneFrustumView> =>
          this.ingestAdapter.getSceneFrustum(this.ingestApiBaseUrl, sceneId, request, signal, useCompact, format);
        return this.probeHandle === null
          ? call()
          : this.probeHandle.frustum(
              sceneId,
              request,
              { endpoint: useCompact ? 'FrustumCompact' : 'Frustum', format },
              call
            );
      },
    },
    gl: () => this.gl,
    currentEpoch: () => this.sceneEpoch,
    textureBudgetBytes: TEXTURE_CACHE_BUDGET_BYTES,
    textureBatchSize: () => this.runtimeSettings.texturedMeshDownloadBatchSize,
    meshCacheBudgetBytes: MESH_CACHE_BUDGET_BYTES,
    downloadBatchSize: () => this.runtimeSettings.downloadBatchSize,
    onCacheChanged: () => this.scheduleSceneGeometryDebugUpdate(),
    geometryFormats: () => ({ meshes: this.geometryFormat, pointClouds: this.pointCloudGeometryFormat }),
    cacheScope: () => ({
      sceneId: this.ingestState.sceneId ?? this.selectedSceneId ?? 'scene:unknown',
      modelId: this.ingestState.modelId ?? this.selectedModelId ?? 'model:unknown',
    }),
    spans: () => (this.viewerProfilingEnabled ? this.viewerProfiler : null),
    onWarning: (message: string) => this.setIngestError(message),
    onEvent: (name: string, detail: string) => this.viewerProfiler?.recordEvent(name, detail),
    onSceneCleared: () => this.resetViewerSceneCaches(),
    onMaterialError: (summary: string) => {
      this.sceneLoadDebug = { ...this.sceneLoadDebug, lastError: summary };
    },
    timers: window,
    followCamera: false,
    canRefresh: () => this.canRefreshScene(),
    streaming: () => this.makeSceneStreaming(),
    look: () => this.makeSceneLook(),
    objectHooks: {
      invalidateTotals: () => this.invalidateSceneGeometryTotals(),
      refreshCulling: () => this.viewerSpatialCulling.refresh(),
      invalidateOrder: () => this.viewerRenderOrder.invalidate(),
      updateBounds: (object: HsGraphicObject) => this.updateViewerObjectBounds(object),
    },
    // The SDK's budget applies: 3 GiB (1 GiB on constrained devices), at most half of the storage quota.
    payloadCache: {
      databaseName: GEOMETRY_CACHE_DB_NAME,
      storeName: GEOMETRY_CACHE_STORE_NAME,
    },
    decodePool: {
      createWorker: () =>
        new Worker(new URL('./services/hyperscene-geometry-decode.worker.ts', import.meta.url), { type: 'classic' }),
      currentEpoch: () => this.sceneEpoch,
      onInflightChange: (inflight: number) => {
        this.sceneSettingsDecodeInflight = inflight;
        this.sceneSettingsDecodeQueuePeak = Math.max(this.sceneSettingsDecodeQueuePeak, inflight);
      },
      onCanceled: (count: number) => {
        this.sceneSettingsDecodeCanceled += count;
      },
      onWorkerFailure: () => {
        this.sceneSettingsWorkerStatus = 'enabled-fallback';
      },
    },
  });
  private frustumRequestSequence: number = 0;
  /** What asked for the next scene refresh, for the profiler. */
  private sceneRefreshTrigger: string = 'direct';
  private sceneRefreshSpan: HsViewerProfilerSpan | null = null;
  private sceneSourceListeners: HsDisposable[] = [];
  private sceneLoadRequestId: number = 0;
  private viewerRuntimeInitialized: boolean = false;
  private sceneLoadInProgress: boolean = false;
  private pendingCameraView: ViewerCameraView | null = null;
  // The scene whose stored defaults were applied; keeps a reload from overriding the user's later choices.
  private sceneDefaultsAppliedFor: string | null = null;
  private viewerSceneSettings: HypersceneSceneSettings | null = null;
  private pendingViewpointId: string | null = null;
  private cameraViewUrlKey: string | null = null;
  private readonly handleWindowResize: () => void = () => this.resize();
  private readonly meshCacheBudgetBytes: number = MESH_CACHE_BUDGET_BYTES;

  private get decodePool(): HsGeometryDecodePool {
    return this.sceneSource.decodePool;
  }

  private get frustumScheduler(): HsFrustumRefreshScheduler {
    return this.sceneSource.scheduler;
  }

  private get frustumScene(): HsFrustumSceneState {
    return this.sceneSource.frustum;
  }

  private get geometryStore(): HsDecodedGeometryStore {
    return this.sceneSource.geometry;
  }

  private get sceneObjects(): HsSceneObjects {
    return this.sceneSource.objects;
  }

  private get sceneMaterials(): HsSceneMaterialCache {
    return this.sceneSource.materials;
  }

  private get sceneMaterialRgbaById(): Record<string, number[]> {
    return this.sceneSource.materialRgbaById;
  }

  private set sceneMaterialRgbaById(value: Record<string, number[]>) {
    this.sceneSource.materialRgbaById = value;
  }

  private get sceneMaterialVertexGroupsById(): Record<string, HypersceneVertexColorGroupView[]> {
    return this.sceneSource.vertexGroupsById;
  }

  private set sceneMaterialVertexGroupsById(value: Record<string, HypersceneVertexColorGroupView[]>) {
    this.sceneSource.vertexGroupsById = value;
  }

  private get sceneTextures(): Map<string, WebGLTexture> {
    return this.sceneSource.textures;
  }

  private get sceneTextureBytesById(): Map<string, number> {
    return this.sceneSource.textureBytes;
  }

  private get sceneMeshPartInfoByRenderKey(): Map<string, HsMeshMaterialPartInfo> {
    return this.sceneSource.meshPartInfoByRenderKey;
  }

  private get sceneMeshMaterialErrors(): Map<string, string> {
    return this.sceneSource.materialErrors;
  }

  // Decoded geometry lives in the SDK store; these accessors keep the names the rest of the
  // component (and its source-text specs) use.
  private get decodedOriginalMeshesById(): Map<string, HsDecodedMesh> {
    return this.geometryStore.originals;
  }

  private get frustumRefreshTimer(): number | null {
    return this.frustumScheduler.timer;
  }

  private set frustumRefreshTimer(value: number | null) {
    this.frustumScheduler.timer = value;
  }

  private get lastDecodedMeshes(): HsDecodedMesh[] {
    return this.sceneSource.drawnMeshes;
  }

  private set lastDecodedMeshes(value: HsDecodedMesh[]) {
    this.sceneSource.drawnMeshes = value;
  }

  private get lastDecodedLines(): HsDecodedLine[] {
    return this.sceneSource.drawnLines;
  }

  private set lastDecodedLines(value: HsDecodedLine[]) {
    this.sceneSource.drawnLines = value;
  }

  private get lastDecodedPointClouds(): HsDecodedPointCloud[] {
    return this.sceneSource.drawnPointClouds;
  }

  private set lastDecodedPointClouds(value: HsDecodedPointCloud[]) {
    this.sceneSource.drawnPointClouds = value;
  }

  private get frustumSceneSessionId(): string | null {
    return this.sceneSource.sessionId;
  }

  private set frustumSceneSessionId(value: string | null) {
    this.sceneSource.sessionId = value;
  }

  private get lastRenderedSceneSignature(): string | null {
    return this.sceneSource.lastViewSignature;
  }

  private set lastRenderedSceneSignature(value: string | null) {
    this.sceneSource.lastViewSignature = value;
  }

  // Frame state lives in the viewer; these keep the names the rest of the page (and its specs) use.
  private get lastRenderedObjectIndex(): number {
    return this.viewer?.drawnObjects ?? 0;
  }

  /** Only a restart (0) can be requested; the viewer advances the cursor while drawing. */
  private set lastRenderedObjectIndex(value: number) {
    if (value === 0) {
      this.viewer?.restartDrawing();
    }
  }

  private get forceFrustumRefresh(): boolean {
    return this.viewer?.cullingPending ?? true;
  }

  /** Setting true asks for a culling pass; the viewer clears it when the pass runs. */
  private set forceFrustumRefresh(value: boolean) {
    if (value) {
      this.viewer?.invalidate();
    }
  }

  private get lastFrustumCullingAt(): number {
    return this.viewer?.lastCullingAt ?? Number.NEGATIVE_INFINITY;
  }

  /** Setting negative infinity culls on the next frame without waiting for the culling interval. */
  private set lastFrustumCullingAt(value: number) {
    if (value === Number.NEGATIVE_INFINITY) {
      this.viewer?.invalidate(true);
    }
  }

  /**
   * The applied HLOD error threshold, a runtime setting (`hlodMaxScreenErrorPixels` in the URL). The setter is
   * for the benchmark harness, which sets it before streaming starts; the Settings tab goes through the draft.
   */
  public get hlodMaxScreenErrorPixels(): number {
    return this.runtimeSettings.hlodMaxScreenErrorPixels;
  }

  public set hlodMaxScreenErrorPixels(value: number) {
    this.runtimeSettings.hlodMaxScreenErrorPixels = value;
    this.runtimeSettingsDraft.hlodMaxScreenErrorPixels = value;
    this.photogrammetry.maxScreenErrorPixels = value;
  }

  public get hlodOcclusionRefinement(): boolean {
    return this.photogrammetry.occlusionRefinement;
  }

  public set hlodOcclusionRefinement(value: boolean) {
    this.photogrammetry.occlusionRefinement = value;
  }

  private get viewerRenderOrder(): HsStableRenderOrder<HsGraphicObject> {
    return this.sceneSource.renderOrder;
  }

  private get frustumRefreshInProgress(): boolean {
    return this.frustumScheduler.inProgress;
  }

  private set frustumRefreshInProgress(value: boolean) {
    this.frustumScheduler.inProgress = value;
  }

  private get frustumRefreshPending(): boolean {
    return this.frustumScheduler.pending;
  }

  private set frustumRefreshPending(value: boolean) {
    this.frustumScheduler.pending = value;
  }

  // Client caches (Settings tab): the geometry cache of the scene source and the photogrammetry payload cache
  // persist in IndexedDB across sessions; the switches persist in local storage.
  public get geometryCacheEnabled(): boolean {
    return this.sceneSource.loadSettings.useCache;
  }

  public get photogrammetryCacheEnabled(): boolean {
    return this.hlodPayloadCache.enabled;
  }

  public onGeometryCacheEnabledChanged(event: Event): void {
    this.sceneSource.loadSettings.useCache = (event.target as HTMLInputElement).checked;
    this.saveClientCacheSettings();
  }

  public onPhotogrammetryCacheEnabledChanged(event: Event): void {
    this.hlodPayloadCache.enabled = (event.target as HTMLInputElement).checked;
    this.saveClientCacheSettings();
  }

  /** One line for the Settings tab: what each cache holds, or why it holds nothing. */
  public get clientCacheUsageMessage(): string {
    const usage: ClientCacheUsage | null = this.clientCacheUsage;
    if (usage === null) {
      return this.clientCacheBusy ? 'Reading the caches...' : '';
    }
    const format = (bytes: number, entries: number, noun: string): string =>
      `${(bytes / 1048576).toFixed(1)} MiB in ${entries} ${noun}`;
    const photogrammetry: string = this.hlodPayloadCache.available
      ? format(usage.photogrammetryBytes, usage.photogrammetryEntries, 'documents')
      : 'unavailable';
    return `Photogrammetry tiles: ${photogrammetry}; scene geometry: ${format(
      usage.geometryBytes,
      usage.geometryEntries,
      'batches'
    )}.`;
  }

  /** Scans both stores; the counts are as of the scan, not live. */
  public async refreshClientCacheUsage(): Promise<void> {
    if (this.clientCacheBusy) {
      return;
    }
    this.clientCacheBusy = true;
    try {
      const [hlod, geometry] = await Promise.all([
        this.hlodPayloadCache.usage().catch(() => ({ entries: 0, bytes: 0 })),
        this.sceneSource.payloadCache.usage().catch(() => ({ entries: 0, bytes: 0 })),
      ]);
      this.clientCacheUsage = {
        photogrammetryEntries: hlod.entries,
        photogrammetryBytes: hlod.bytes,
        geometryEntries: geometry.entries,
        geometryBytes: geometry.bytes,
      };
    } finally {
      this.clientCacheBusy = false;
      this.changeDetector.markForCheck();
    }
  }

  /** Empties both IndexedDB caches; downloads of the open scene continue and fill them again. */
  public async clearClientCaches(): Promise<void> {
    if (this.clientCacheBusy) {
      return;
    }
    this.clientCacheBusy = true;
    this.clientCacheMessage = 'Clearing...';
    try {
      const results: PromiseSettledResult<void>[] = await Promise.allSettled([
        this.hlodPayloadCache.clear(),
        this.sceneSource.payloadCache.clear(),
      ]);
      const failed: number = results.filter(
        (result: PromiseSettledResult<void>) => result.status === 'rejected'
      ).length;
      this.clientCacheMessage = failed === 0 ? 'Caches cleared.' : 'Some caches could not be cleared.';
    } finally {
      this.clientCacheBusy = false;
    }
    await this.refreshClientCacheUsage();
  }

  private applyClientCacheSettings(): void {
    const settings: ClientCacheSettings = readClientCacheSettings();
    this.sceneSource.loadSettings.useCache = settings.geometry;
    this.hlodPayloadCache.enabled = settings.photogrammetry;
  }

  private saveClientCacheSettings(): void {
    writeClientCacheSettings({
      geometry: this.sceneSource.loadSettings.useCache,
      photogrammetry: this.hlodPayloadCache.enabled,
    });
  }

  // Geometry load settings and counters live in the scene source.
  public get sceneSettingsUseWebWorkers(): boolean {
    return this.sceneSource.loadSettings.useWorkers;
  }

  public set sceneSettingsUseWebWorkers(value: boolean) {
    this.sceneSource.loadSettings.useWorkers = value;
  }

  public get sceneSettingsWorkerStatus(): string {
    return this.sceneSource.stats.workerStatus;
  }

  public set sceneSettingsWorkerStatus(value: string) {
    this.sceneSource.stats.workerStatus = value;
  }

  public get sceneSettingsDecodeInflight(): number {
    return this.sceneSource.stats.decodeInflight;
  }

  public set sceneSettingsDecodeInflight(value: number) {
    this.sceneSource.stats.decodeInflight = value;
  }

  public get sceneSettingsDecodeQueuePeak(): number {
    return this.sceneSource.stats.decodeQueuePeak;
  }

  public set sceneSettingsDecodeQueuePeak(value: number) {
    this.sceneSource.stats.decodeQueuePeak = value;
  }

  public get sceneSettingsDecodeCanceled(): number {
    return this.sceneSource.stats.decodeCanceled;
  }

  public set sceneSettingsDecodeCanceled(value: number) {
    this.sceneSource.stats.decodeCanceled = value;
  }

  /** Distinct shader programs behind the cached scene materials, for the debug panel. */
  public get sceneCachedShaderPrograms(): number {
    return this.sceneMaterials.programCount;
  }

  // Scene GPU objects live in sceneObjects; these accessors keep the names the viewer code uses.
  private get sceneMeshObjectsByGeometryId(): Map<string, HsGraphicObject> {
    return this.sceneObjects.meshes;
  }

  private set sceneMeshObjectsByGeometryId(value: Map<string, HsGraphicObject>) {
    this.sceneObjects.meshes = value;
  }

  private get sceneLineObjectsByGeometryId(): Map<string, HsGraphicLines> {
    return this.sceneObjects.lines;
  }

  private set sceneLineObjectsByGeometryId(value: Map<string, HsGraphicLines>) {
    this.sceneObjects.lines = value;
  }

  private get scenePointCloudObjectsByGeometryId(): Map<string, HsGraphicPoints> {
    return this.sceneObjects.pointClouds;
  }

  private set scenePointCloudObjectsByGeometryId(value: Map<string, HsGraphicPoints>) {
    this.sceneObjects.pointClouds = value;
  }

  // Frustum scene content lives in frustumScene; these accessors keep the names the viewer code uses.
  private get sceneMeshGeometryIds(): string[] {
    return this.frustumScene.meshes.geometryIds;
  }

  private set sceneMeshGeometryIds(value: string[]) {
    this.frustumScene.meshes.geometryIds = value;
  }

  private get sceneLineGeometryIds(): string[] {
    return this.frustumScene.lines.geometryIds;
  }

  private set sceneLineGeometryIds(value: string[]) {
    this.frustumScene.lines.geometryIds = value;
  }

  private get scenePointCloudGeometryIds(): string[] {
    return this.frustumScene.pointClouds.geometryIds;
  }

  private set scenePointCloudGeometryIds(value: string[]) {
    this.frustumScene.pointClouds.geometryIds = value;
  }

  private get sceneMeshRenderKeys(): string[] {
    return this.frustumScene.meshes.renderKeys;
  }

  private set sceneMeshRenderKeys(value: string[]) {
    this.frustumScene.meshes.renderKeys = value;
  }

  private get sceneLineRenderKeys(): string[] {
    return this.frustumScene.lines.renderKeys;
  }

  private set sceneLineRenderKeys(value: string[]) {
    this.frustumScene.lines.renderKeys = value;
  }

  private get scenePointCloudRenderKeys(): string[] {
    return this.frustumScene.pointClouds.renderKeys;
  }

  private set scenePointCloudRenderKeys(value: string[]) {
    this.frustumScene.pointClouds.renderKeys = value;
  }

  private get sceneMeshGeometryIdByRenderKey(): Record<string, string> {
    return this.frustumScene.meshes.geometryIdByRenderKey;
  }

  private set sceneMeshGeometryIdByRenderKey(value: Record<string, string>) {
    this.frustumScene.meshes.geometryIdByRenderKey = value;
  }

  private get sceneLineGeometryIdByRenderKey(): Record<string, string> {
    return this.frustumScene.lines.geometryIdByRenderKey;
  }

  private set sceneLineGeometryIdByRenderKey(value: Record<string, string>) {
    this.frustumScene.lines.geometryIdByRenderKey = value;
  }

  private get scenePointCloudGeometryIdByRenderKey(): Record<string, string> {
    return this.frustumScene.pointClouds.geometryIdByRenderKey;
  }

  private set scenePointCloudGeometryIdByRenderKey(value: Record<string, string>) {
    this.frustumScene.pointClouds.geometryIdByRenderKey = value;
  }

  private get sceneMeshTextureIdByRenderKey(): Record<string, string> {
    return this.frustumScene.meshes.textureIdByRenderKey;
  }

  private set sceneMeshTextureIdByRenderKey(value: Record<string, string>) {
    this.frustumScene.meshes.textureIdByRenderKey = value;
  }

  private get scenePointCloudWorldMatricesByRenderKey(): Record<string, number[]> {
    return this.frustumScene.pointClouds.worldMatricesByRenderKey;
  }

  private set scenePointCloudWorldMatricesByRenderKey(value: Record<string, number[]>) {
    this.frustumScene.pointClouds.worldMatricesByRenderKey = value;
  }

  private get decodedMeshesById(): Map<string, HsDecodedMesh> {
    return this.geometryStore.meshes;
  }

  private get decodedLinesById(): Map<string, HsDecodedLine> {
    return this.geometryStore.lines;
  }

  private get decodedPointCloudsById(): Map<string, HsDecodedPointCloud> {
    return this.geometryStore.pointClouds;
  }

  private get loadedTrianglesCount(): number {
    return this.geometryStore.loadedTriangles;
  }

  private set loadedTrianglesCount(value: number) {
    this.geometryStore.loadedTriangles = value;
  }

  private get loadedLineSegmentsCount(): number {
    return this.geometryStore.loadedLineSegments;
  }

  private set loadedLineSegmentsCount(value: number) {
    this.geometryStore.loadedLineSegments = value;
  }

  private get loadedPointsCount(): number {
    return this.geometryStore.loadedPoints;
  }

  private set loadedPointsCount(value: number) {
    this.geometryStore.loadedPoints = value;
  }
  private lastMeshBatchingRevision: number = -1;
  private readonly usePreparedMeshBatches: boolean = true;
  private readonly useCachedBatchData: boolean = true;
  private readonly useMeasuredDistributedCost: boolean = true;
  private viewerRenderTotalsPending: boolean = false;
  private cameraNavigationFrame: boolean = false;
  /** Measurement lines and points, markers and debug boxes; rebuilt with the scene's transient objects. */
  private overlays: HsViewerOverlays | null = null;
  // Tileset documents persist in IndexedDB, so reopening a photogrammetry scene downloads only what changed.
  public readonly hlodPayloadCache: HsHlodPayloadCache = new HsHlodPayloadCache({
    onWarning: (message: string, error: unknown) => console.warn(message, error),
  });
  public clientCacheUsage: ClientCacheUsage | null = null;
  public clientCacheMessage: string = '';
  public clientCacheBusy: boolean = false;
  // HLOD tilesets: the imported root mesh stands in until the tileset's own root node is drawn, then
  // tileset nodes replace it and are selected every frame by projected error.
  private readonly photogrammetry: HsPhotogrammetrySource = new HsPhotogrammetrySource({
    api: {
      hasPhotogrammetry: (sceneId: string) => this.ingestAdapter.getScenePhotogrammetry(this.ingestApiBaseUrl, sceneId),
      getPhotogrammetryTilesets: (meshIds: readonly string[]) =>
        this.ingestAdapter.getPhotogrammetryProgressiveMeshes(this.ingestApiBaseUrl, [...meshIds]),
      getDocuments: (documentIds: readonly string[]) =>
        this.ingestAdapter.getTextures(this.ingestApiBaseUrl, [...documentIds]),
    },
    // The benchmark probe intercepts these downloads; cache hits never reach it.
    downloadDocuments: (documentIds: string[]) => this.downloadDocumentBuffers(documentIds),
    payloadCache: this.hlodPayloadCache,
    decodeNode: (payload: ArrayBuffer, origin: readonly [number, number, number]) =>
      hsDecodeHlodPayload(payload, origin),
    createTranscodeWorker: () =>
      new Worker(new URL('./services/hlod-texture-transcode.worker.ts', import.meta.url), { type: 'classic' }),
    basisDecoderUrl: new URL('assets/decoders/basis/', globalThis.document.baseURI).href,
    transcodeWorkers: HLOD_TEXTURE_TRANSCODE_WORKERS,
    target: () =>
      this.gl === null || this.graphics === null || this.objectsQueue === null
        ? null
        : { gl: this.gl, graphics: this.graphics, queue: this.objectsQueue },
    rootObjects: (meshId: string) => this.findHlodRootObjects(meshId),
    currentEpoch: () => this.sceneEpoch,
    requestRender: () => this.requestRender(),
    onObjectsChanged: () => {
      this.viewerRenderOrder.invalidate();
      this.viewerSpatialCulling.refresh();
    },
    onEvent: (name: string, detail: string) => this.viewerProfiler?.recordEvent(name, detail),
    onWarning: (message: string, error: unknown) => console.warn(message, error),
  });
  private hlodLookupTimer: number | null = null;
  private photogrammetryStatusListener: HsDisposable | null = null;
  public photogrammetryLoading: HypersceneViewerPhotogrammetryLoadingState = {
    status: this.photogrammetry.status(),
    peakPending: 0,
  };
  /** Counts change on most streaming frames; the overlay redraws at most every 200 ms unless `busy` flips. */
  private photogrammetryStatusTimer: number | null = null;
  public hlodDebug: string = '';
  private hlodDebugUpdatedAt: number = 0;
  // Bounding boxes of the drawn HLOD nodes; rebuilt when the drawn set changes, unlike the scene's
  // debug boxes, which only follow frustum responses.
  private hlodDebugBoxesShown: boolean = false;

  constructor(
    private readonly changeDetector: ChangeDetectorRef,
    private readonly ngZone: NgZone,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    @Inject(DOCUMENT) private readonly document: Document
  ) {
    this.subscription.add(this.handleFullscreenChange());

    this.hypersceneSubscription.add(this.handleOutputMessage());
    this.photogrammetryStatusListener = this.photogrammetry.on('status', (status: HsPhotogrammetryLoadStatus) =>
      this.onPhotogrammetryStatus(status)
    );
    this.sceneSource.viewSessionId = this.visibilitySessionId;
    this.applyClientCacheSettings();
  }

  public ngAfterViewInit(): void {
    void this.refreshClientCacheUsage();
    if (new URLSearchParams(this.document.defaultView?.location.search ?? '').get('viewerBenchmark') === 'true') {
      // Production builds have no `ng.getComponent`; the benchmark harness finds the viewer here.
      // Harness builds that know the probe use it; older ones still read the component.
      // eslint-disable-next-line @typescript-eslint/naming-convention
      (globalThis as { __hypersceneViewer?: HypersceneAdvancedDemo }).__hypersceneViewer = this;
      // eslint-disable-next-line @typescript-eslint/naming-convention
      this.probeHandle = this.createBenchmarkProbe();
      // eslint-disable-next-line @typescript-eslint/naming-convention
      (globalThis as { __hypersceneViewerProbe?: HsViewerProbe }).__hypersceneViewerProbe = this.probeHandle.probe;
    }

    if (!this.sceneOnly) {
      void this.refreshScenesRegistry();
    }

    if (!this.sceneSettingsUseWebWorkers) {
      void hsPreloadDracoDecoder().catch((error: unknown) => {
        console.warn('Unable to preload Draco decoder.', error);
      });
    }

    if (!this.ensureViewerRuntimeReady()) {
      this.sceneLoadDebug = {
        ...this.sceneLoadDebug,
        lastUpdatedAt: new Date().toLocaleTimeString(),
        lastError: 'viewer-runtime-not-initialized',
      };
      return;
    }

    this.subscription.add(
      this.route.queryParamMap.subscribe(
        // eslint-disable-next-line complexity
        (params: ParamMap) => {
          const configuration: ReturnType<typeof readViewerUrlSettings> = readViewerUrlSettings(params);
          this.runtimeSettingsError = configuration.error;
          if (configuration.error !== null) {
            this.runtimeSceneReloadPending = false;
            this.clearViewerScene();
            this.changeDetector.markForCheck();
            return;
          }
          const frustumChanged: boolean = this.runtimeFields.some(
            (field: (typeof VIEWER_RUNTIME_FIELDS)[number]) =>
              field.group === 'Frustum' && this.runtimeSettings[field.key] !== configuration.settings[field.key]
          );
          const cameraFarChanged: boolean = this.camera.far !== configuration.settings.farPlane;
          const renderOrderChanged: boolean = this.viewerRenderOrderStrategy !== configuration.renderOrderStrategy;
          const meshFrustumSortOrderChanged: boolean = this.meshFrustumSortOrder !== configuration.meshFrustumSortOrder;
          const frustumSortOrderChanged: boolean = meshFrustumSortOrderChanged;
          const transparentMaterialsChanged: boolean =
            this.disableTransparentMaterials !== configuration.disableTransparentMaterials;
          const instancingChanged: boolean = this.enableInstancing !== configuration.enableInstancing;
          const meshGeometryFormatChanged: boolean = this.geometryFormat !== configuration.geometryFormat;
          const pointCloudGeometryFormatChanged: boolean =
            this.pointCloudGeometryFormat !== configuration.pointCloudGeometryFormat;
          const geometryFormatChanged: boolean = meshGeometryFormatChanged || pointCloudGeometryFormatChanged;
          const pointCloudPointSizeChanged: boolean =
            this.runtimeSettings.pointCloudPointSize !== configuration.settings.pointCloudPointSize;
          const freezeFrustumChanged: boolean = this.freezeFrustum !== configuration.freezeFrustum;
          const frustumDebugChanged: boolean =
            this.showPointCloudBoxes !== configuration.showPointCloudBoxes ||
            this.showObjectBoundingBoxes !== configuration.showObjectBoundingBoxes;
          const cameraViewUrlKey: string | null = this.getCameraViewUrlKey(configuration.cameraView);
          const cameraViewChanged: boolean = cameraViewUrlKey !== this.cameraViewUrlKey;
          this.cameraViewUrlKey = cameraViewUrlKey;
          const viewpointIdChanged: boolean = configuration.viewpointId !== this.pendingViewpointId;
          this.runtimeSettings = configuration.settings;
          this.runtimeSettingsDraft = { ...configuration.settings };
          this.downloadBatchAuto = configuration.downloadBatchAuto;
          this.downloadBatchAutoDraft = configuration.downloadBatchAuto;
          this.viewerDistributedRendering = configuration.distributedRendering;
          this.distributedRenderingDraft = configuration.distributedRendering;
          this.setNavigationMinScreenSize(configuration.navigationMinScreenSize);
          this.navigationMinScreenSizeDraft = configuration.navigationMinScreenSize;
          this.viewerRenderOrderStrategy = configuration.renderOrderStrategy;
          this.renderOrderStrategyDraft = configuration.renderOrderStrategy;
          this.meshFrustumSortOrder = configuration.meshFrustumSortOrder;
          this.meshFrustumSortOrderDraft = configuration.meshFrustumSortOrder;
          this.disableTransparentMaterials = configuration.disableTransparentMaterials;
          this.disableTransparentMaterialsDraft = configuration.disableTransparentMaterials;
          this.enableInstancing = configuration.enableInstancing;
          this.enableInstancingDraft = configuration.enableInstancing;
          this.pointCloudAdaptivePointSize = configuration.pointCloudAdaptivePointSize;
          this.pointCloudAdaptivePointSizeDraft = configuration.pointCloudAdaptivePointSize;
          this.hlodOcclusionRefinement = configuration.hlodOcclusionRefinement;
          this.hlodOcclusionRefinementDraft = configuration.hlodOcclusionRefinement;
          this.setFrustumFrozen(configuration.freezeFrustum);
          this.freezeFrustumDraft = configuration.freezeFrustum;
          this.showPointCloudBoxes = configuration.showPointCloudBoxes;
          this.showPointCloudBoxesDraft = configuration.showPointCloudBoxes;
          this.showObjectBoundingBoxes = configuration.showObjectBoundingBoxes;
          this.showObjectBoundingBoxesDraft = configuration.showObjectBoundingBoxes;
          this.showViewerLoading = configuration.showViewerLoading;
          this.showViewerLoadingDraft = configuration.showViewerLoading;
          this.useCompactFrustum = configuration.useCompactFrustum;
          this.viewerProfilingEnabled = configuration.viewerProfilingEnabled;
          this.viewerProfilingEnabledDraft = configuration.viewerProfilingEnabled;
          this.viewerDeepProfilingEnabled = configuration.viewerDeepProfilingEnabled;
          this.viewerDeepProfilingEnabledDraft = configuration.viewerDeepProfilingEnabled;
          this.lastNavigationSpeedUpdateTime = 0;
          this.navigationSpeedSampleRevision++;
          this.navigationSpeedSampling = false;
          this.geometryFormat = configuration.geometryFormat;
          this.geometryFormatDraft = configuration.geometryFormat;
          this.pointCloudGeometryFormat = configuration.pointCloudGeometryFormat;
          this.pointCloudGeometryFormatDraft = configuration.pointCloudGeometryFormat;
          const frustumResponseFormatChanged: boolean =
            this.frustumResponseFormat !== configuration.frustumResponseFormat;
          this.frustumResponseFormat = configuration.frustumResponseFormat;
          this.frustumResponseFormatDraft = configuration.frustumResponseFormat;
          const visualQualityChanged: boolean =
            viewerVisualQualityShaderSignature(this.visualQualitySettings) !==
            viewerVisualQualityShaderSignature(configuration.visualQuality);
          this.visualQualitySettings = configuration.visualQuality;
          this.visualQualitySettingsDraft = { ...configuration.visualQuality };
          if (pointCloudPointSizeChanged) {
            this.applyPointCloudPointSize();
          }
          this.applyVisualQualityFramebufferSettings();
          if (this.viewerProfiler !== undefined) {
            this.viewerProfiler.configure(
              {
                enabled: this.viewerProfilingEnabled,
                deepGpu: this.viewerDeepProfilingEnabled,
              },
              this.gl
            );
            this.viewerProfilerSnapshot = this.viewerProfiler.snapshot();
          }
          if (renderOrderChanged) {
            this.viewerRenderOrder.setStrategy(configuration.renderOrderStrategy);
          }
          this.render.frameTimeForDistributedRendering = this.runtimeSettings.frameBudgetMs;
          this.render.distributedRenderingInertiaFrames = this.runtimeSettings.drawCountInertiaFrames;
          if (cameraFarChanged) {
            this.camera.far = this.runtimeSettings.farPlane;
            this.camera.updateProjectionMatrix();
          }
          this.lastRenderedObjectIndex = 0;
          this.forceFrustumRefresh =
            this.forceFrustumRefresh ||
            frustumChanged ||
            freezeFrustumChanged ||
            frustumSortOrderChanged ||
            cameraFarChanged ||
            renderOrderChanged ||
            transparentMaterialsChanged ||
            instancingChanged ||
            frustumDebugChanged ||
            geometryFormatChanged ||
            frustumResponseFormatChanged ||
            visualQualityChanged;
          if (transparentMaterialsChanged || instancingChanged || frustumDebugChanged || visualQualityChanged) {
            if (
              instancingChanged &&
              this.objectsQueue !== undefined &&
              this.objectsQueue !== null &&
              this.graphics !== undefined &&
              this.graphics !== null
            ) {
              this.clearTrackedSceneBaseObjects(this.objectsQueue, this.graphics);
            }
            this.lastRenderedSceneSignature = null;
            this.applyVisualControlChanges();
          }
          const sceneId: string | null = params.get('sceneId');
          const previousSceneId: string | null = this.selectedSceneId;
          this.selectedSceneId = sceneId !== null && sceneId.trim().length > 0 ? sceneId.trim() : null;
          const sceneChanged: boolean =
            previousSceneId !== this.selectedSceneId || this.ingestState.sceneId !== this.selectedSceneId;
          let appliedCameraView: ViewerCameraView | null = null;
          let appliedViewpointId: string | null = null;
          if (sceneChanged || viewpointIdChanged) {
            this.pendingViewpointId = configuration.viewpointId;
          }
          if (sceneChanged || (cameraViewChanged && this.sceneLoadInProgress)) {
            this.pendingCameraView = configuration.cameraView;
          } else if (
            cameraViewChanged &&
            configuration.cameraView !== null &&
            !sceneChanged &&
            !this.sceneLoadInProgress &&
            this.ingestState.phase === 'ready'
          ) {
            this.applyCameraView(configuration.cameraView);
            appliedCameraView = configuration.cameraView;
          }
          if (
            viewpointIdChanged &&
            configuration.viewpointId !== null &&
            !sceneChanged &&
            !this.sceneLoadInProgress &&
            this.ingestState.phase === 'ready'
          ) {
            if (this.restoreViewpointById(configuration.viewpointId)) {
              appliedViewpointId = configuration.viewpointId;
            }
          }
          if (appliedCameraView !== null || appliedViewpointId !== null) {
            this.clearAppliedViewUrlParameters(this.selectedSceneId, appliedCameraView, appliedViewpointId);
          }
          if (this.sceneLoadInProgress && (sceneChanged || geometryFormatChanged)) {
            this.runtimeSceneReloadPending = true;
            this.bumpSceneEpochAndCancelDecode();
          }
          this.syncSelectedSceneRenameDraft();
          if (this.selectedSceneId !== null && (sceneChanged || geometryFormatChanged)) {
            void this.loadSelectedSceneFromRegistry();
          } else if (this.selectedSceneId === null && !this.sceneLoadInProgress) {
            this.sceneDefaultsAppliedFor = null;
            this.clearViewerScene();
          } else if (
            this.selectedSceneId !== null &&
            (frustumChanged || (freezeFrustumChanged && !this.freezeFrustum)) &&
            !this.sceneLoadInProgress &&
            this.ingestState.phase === 'ready'
          ) {
            this.scheduleFrustumRefresh(this.selectedSceneId, 0, 'route-settings');
          }
          this.changeDetector.markForCheck();
        }
      )
    );
  }

  /** The benchmark probe over this page: its answers come from the page's state. */
  private createBenchmarkProbe(): HsViewerProbeHandle {
    return hsCreateViewerProbe({
      phase: (): HsViewerProbePhase => {
        const phase: HypersceneIngestPhase = this.ingestState.phase;
        if (phase === 'ready') {
          return 'ready';
        }
        if (phase === 'failed') {
          return 'error';
        }
        return phase === 'idle' || phase === 'canceled' ? 'idle' : 'loading';
      },
      error: (): string | null =>
        this.ingestState.error ?? (this.ingestState.phase === 'failed' ? this.ingestState.message : null),
      sceneId: (): string | null => this.ingestState.sceneId,
      busyReasons: (): string[] => {
        const reasons: string[] = [];
        if (this.ingestState.phase !== 'ready') reasons.push(`phase:${this.ingestState.phase}`);
        if (this.viewerLoadingActive) reasons.push('loading');
        if (this.frustumRefreshPending) reasons.push('frustum-pending');
        if (this.frustumRefreshTimer !== null) reasons.push('frustum-timer');
        if (this.hlodLookupTimer !== null) reasons.push('hlod-lookup');
        if (this.pointCloudDetailTimer !== null) reasons.push('navigation-debounce');
        if (this.mouseButton !== null) reasons.push('navigation');
        return reasons;
      },
      visibleObjects: () => this.renderObjects,
      loadedTriangles: (): number => this.loadedTrianglesCount,
      renderedTriangles: (): number => this.sceneRenderTotals.triangles,
      camera: () => this.camera,
      gl: () => this.gl,
      hlodTilesets: () => this.photogrammetry.tilesets(),
      photogrammetryStatus: (): HsPhotogrammetryLoadStatus => this.photogrammetry.status(),
      hasPointClouds: (): boolean => this.scenePointCloudObjectsByGeometryId.size > 0,
      drawnPointCloudKeys: (): string[] => {
        const keys: string[] = [];
        this.scenePointCloudObjectsByGeometryId.forEach((object: HsGraphicPoints, key: string) => {
          if (object.visible && !object.budgetSkipped) {
            keys.push(key);
          }
        });
        return keys;
      },
      pointCloudBlock: (key: string): HsProbePointCloudBlock | null => {
        // Drawn point cloud objects are keyed by render key.
        const object: HsGraphicPoints | undefined = this.scenePointCloudObjectsByGeometryId.get(key);
        const cloud: HsDecodedPointCloud | undefined = this.decodedPointCloudsById.get(
          this.scenePointCloudGeometryIdByRenderKey[key]
        );
        const matrix: number[] | undefined = this.scenePointCloudWorldMatricesByRenderKey[key];
        return object === undefined ||
          !object.visible ||
          object.budgetSkipped ||
          cloud === undefined ||
          matrix === undefined
          ? null
          : {
              // Decoders interleave blocks like the GPU buffers, so a thinned block draws this prefix.
              points:
                object.drawCount === null
                  ? cloud.points
                  : cloud.points instanceof Float32Array
                  ? cloud.points.subarray(0, object.drawCount * 3)
                  : Array.prototype.slice.call(cloud.points, 0, object.drawCount * 3),
              matrix,
              pointSize: object.getPointSize() * this.render.pointCloudSizeScale,
            };
      },
      pointSize: (): number => this.runtimeSettings.pointCloudPointSize,
      pointCloudBudget: (): HsProbePointCloudBudget | null =>
        this.scenePointCloudObjectsByGeometryId.size === 0
          ? null
          : {
              loadedPoints: this.pointCloudLoadedPoints,
              wantedPoints: this.pointCloudWantedPoints,
              drawnPoints: this.pointCloudDrawnPoints,
              occludedBlocks: this.pointCloudOccludedBlocks,
              regulatedPoints: this.pointCloudDrawBudget.regulated,
              downloadPoints: this.getPointCloudLimits().points,
              costSource: this.pointCloudDrawBudget.source,
            },
      sceneBounds: async (): Promise<HsBoundingBoxView | null> => {
        const sceneId: string | null = this.ingestState.sceneId;
        return sceneId === null
          ? null
          : (await this.ingestAdapter.getSceneBoundingBox(this.ingestApiBaseUrl, sceneId)).value;
      },
      applyCameraView: (view: { position: number[]; direction: number[] }): void => {
        this.applyCameraView({
          position: view.position as ViewerCameraView['position'],
          direction: view.direction as ViewerCameraView['direction'],
        });
        // applyCameraView also resets the last camera; marking the state dirty makes the next frame
        // react as to a navigation step (debounced Frustum request, point cloud sampling).
        this.markRenderStateDirty(true);
      },
      setNavigating: (active: boolean): void => {
        if (active) {
          this.navigationButtonOverride = 0;
          return;
        }
        this.navigationButtonOverride = null;
        this.finishCameraNavigation();
      },
      requestRender: (): void => this.requestRender(),
    });
  }

  public ngOnDestroy(): void {
    this.settingsAutoApply.dispose();
    this.navigationSpeedSampleRevision++;
    if (this.cameraPathRecorderTimer !== null) {
      window.clearInterval(this.cameraPathRecorderTimer);
      this.cameraPathRecorderTimer = null;
    }
    this.subscription.unsubscribe();
    this.hypersceneSubscription.unsubscribe();
    this.viewerProfiler.destroy();
    this.viewerSpatialCulling.destroy();
    this.photogrammetryStatusListener?.dispose();
    this.photogrammetryStatusListener = null;
    if (this.photogrammetryStatusTimer !== null) {
      window.clearTimeout(this.photogrammetryStatusTimer);
      this.photogrammetryStatusTimer = null;
    }
    this.photogrammetry.dispose();
    window.removeEventListener('resize', this.handleWindowResize);
    if (this.frustumRefreshTimer !== null) {
      window.clearTimeout(this.frustumRefreshTimer);
      this.frustumRefreshTimer = null;
    }
    if (this.pointCloudDetailTimer !== null) {
      window.clearTimeout(this.pointCloudDetailTimer);
      this.pointCloudDetailTimer = null;
    }
    if (this.sceneGeometryDebugUpdateTimer !== null) {
      window.clearTimeout(this.sceneGeometryDebugUpdateTimer);
      this.sceneGeometryDebugUpdateTimer = null;
    }
    this.canvasResizeObserver?.disconnect();
    this.canvasResizeObserver = null;
    this.sceneSource.abort();
    this.decodePool.dispose();
    this.sceneEpoch++;
    this.resetHlodTilesets();
    this.disposePointCloudVisibilityPass();
    this.clearSceneMaterialCache();
    this.clearSceneTextures();
    this.overlays?.dispose();
    this.overlays = null;
    this.sceneSourceListeners.splice(0).forEach((listener: HsDisposable) => listener.dispose());
    this.viewerInput?.dispose();
    this.viewerInput = null;
    // Last: it releases the WebGL context the cleanup above still used.
    this.viewer?.dispose();
    this.viewer = null;
    this.viewerRuntimeInitialized = false;
  }

  public async refreshModelsRegistry(): Promise<void> {
    try {
      const models: HypersceneModelView[] = await this.ingestAdapter.filterModels(
        this.ingestApiBaseUrl,
        this.getRegistryMetadataFilter()
      );
      const sortedModels: HypersceneModelView[] = this.sortModelsByCreatedAt(models);
      this.modelsRegistry = sortedModels;
      this.similarityBulkModelIds = this.similarityBulkModelIds.filter((id: string) =>
        sortedModels.some((model: HypersceneModelView) => model.id === id)
      );
      if (
        this.selectedModelId !== null &&
        !sortedModels.some((model: HypersceneModelView) => model.id === this.selectedModelId)
      ) {
        this.selectedModelId = null;
      }
      if (this.selectedModelId === null) {
        this.selectedModelId = sortedModels[0]?.id ?? null;
      }
      this.syncSelectedModelRenameDraft();
    } catch (error: unknown) {
      const errorMessage: string = error instanceof Error ? error.message : 'Failed to load models registry.';
      this.setIngestError(errorMessage);
    } finally {
      this.changeDetector.markForCheck();
    }
  }

  public async refreshScenesRegistry(): Promise<void> {
    try {
      const scenes: HypersceneSceneView[] = await this.ingestAdapter.filterScenes(
        this.ingestApiBaseUrl,
        this.getRegistryMetadataFilter()
      );
      const sortedScenes: HypersceneSceneView[] = this.sortScenesByCreatedAt(scenes);
      this.scenesRegistry = sortedScenes;

      this.syncSelectedSceneRenameDraft();
    } catch (error: unknown) {
      const errorMessage: string = error instanceof Error ? error.message : 'Failed to load scenes registry.';
      this.setIngestError(errorMessage);
    } finally {
      this.changeDetector.markForCheck();
    }
  }

  public isSelectedSceneListed(): boolean {
    return this.scenesRegistry.some((scene: HypersceneSceneView) => scene.id === this.selectedSceneId);
  }

  private navigateToViewerScene(sceneId: string | null): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        sceneId,
        ...(sceneId !== this.selectedSceneId ? { camera: null, viewpointId: null } : {}),
      },
      queryParamsHandling: 'merge',
    });
  }

  public onSelectedSceneChanged(event: Event): void {
    if (!(event.target instanceof HTMLSelectElement)) {
      return;
    }

    this.navigateToViewerScene(event.target.value.length > 0 ? event.target.value : null);
    return;
  }

  private clearSelectedSceneModelInstance(): void {
    ++this.selectedSceneModelInstanceRequestId;
    this.selectedSceneModelInstance = null;
    this.selectedSceneModelInstanceLoading = false;
    this.selectedSceneModelInstanceError = null;
  }

  private isMatchingSceneModelInstance(
    instance: HypersceneSceneModelInstanceView,
    sceneId: string,
    rootNodeId: string
  ): boolean {
    return (
      instance.sceneId === sceneId &&
      instance.rootNodeId === rootNodeId &&
      typeof instance.sourceModelId === 'string' &&
      instance.sourceModelId.length > 0 &&
      typeof instance.sourceVersionId === 'string' &&
      instance.sourceVersionId.length > 0 &&
      typeof instance.sourceRootNodeId === 'string' &&
      instance.sourceRootNodeId.length > 0 &&
      Array.isArray(instance.relativeTransform) &&
      instance.relativeTransform.length === 9 &&
      instance.relativeTransform.every((value: number) => Number.isFinite(value)) &&
      Array.isArray(instance.relativeMatrix) &&
      instance.relativeMatrix.length === 16 &&
      instance.relativeMatrix.every((value: number) => Number.isFinite(value))
    );
  }

  public onEnterpriseSelectedNodeChanged(event: Event): void {
    if (!(event.target instanceof HTMLSelectElement)) {
      return;
    }

    this.enterpriseSelectedNodeId = event.target.value.length > 0 ? event.target.value : null;
    this.changeDetector.markForCheck();
  }

  public onEnterpriseAttributeNameChanged(event: Event): void {
    if (!(event.target instanceof HTMLInputElement)) {
      return;
    }

    this.enterpriseAttributeName = event.target.value;
    this.enterpriseAttributeValues = [];
    this.enterpriseFilterResults = [];
    this.enterpriseSelectedNodeId = null;
    this.enterpriseFilterMessage = 'Attribute name changed. Load values or run search.';
    this.changeDetector.markForCheck();
  }

  public onEnterpriseAttributeValueChanged(event: Event): void {
    if (!(event.target instanceof HTMLInputElement)) {
      return;
    }

    this.enterpriseAttributeValue = event.target.value;
    this.changeDetector.markForCheck();
  }

  public async loadEnterpriseAttributeValues(): Promise<void> {
    if (this.ingestState.sceneId === null) {
      this.setIngestError('Load a scene before requesting attribute values.');
      return;
    }

    const attributeName: string = this.enterpriseAttributeName.trim();
    if (attributeName.length === 0) {
      this.setIngestError('Provide an attribute name first.');
      return;
    }

    try {
      const values: string[] = await this.ingestAdapter.getSceneAttributeValues(
        this.ingestApiBaseUrl,
        this.ingestState.sceneId,
        attributeName
      );
      this.enterpriseAttributeValues = values.slice(0, 100);
      this.enterpriseFilterMessage = `Loaded ${this.enterpriseAttributeValues.length} values for ${attributeName}.`;
      this.changeDetector.markForCheck();
    } catch (error: unknown) {
      const errorMessage: string = error instanceof Error ? error.message : 'Failed to load attribute values.';
      this.setIngestError(errorMessage);
    }
  }

  public async runEnterpriseMetadataSearch(): Promise<void> {
    if (this.ingestState.sceneId === null) {
      this.setIngestError('Load a scene before running metadata search.');
      return;
    }

    const attributeName: string = this.enterpriseAttributeName.trim();
    const attributeValue: string = this.enterpriseAttributeValue.trim();
    if (attributeName.length === 0 || attributeValue.length === 0) {
      this.setIngestError('Provide both attribute name and attribute value.');
      return;
    }

    this.enterpriseFilterLoading = true;
    this.enterpriseFilterMessage = 'Searching scene nodes by metadata...';
    this.changeDetector.markForCheck();

    try {
      const results: HypersceneSceneNodeFilterResultView[] = await this.ingestAdapter.filterSceneNodesByAttribute(
        this.ingestApiBaseUrl,
        this.ingestState.sceneId,
        attributeName,
        attributeValue,
        50,
        0
      );
      this.enterpriseFilterResults = results;
      this.enterpriseSelectedNodeId = results[0]?.id ?? null;
      this.enterpriseFilterMessage = `Metadata search found ${results.length} nodes.`;
    } catch (error: unknown) {
      const errorMessage: string = error instanceof Error ? error.message : 'Failed to filter scene nodes by metadata.';
      this.setIngestError(errorMessage);
    } finally {
      this.enterpriseFilterLoading = false;
      this.changeDetector.markForCheck();
    }
  }

  public async openEnterpriseSelectedNode(): Promise<void> {
    const nodeId: string | null = this.enterpriseSelectedNodeId;
    if (nodeId === null) {
      return;
    }

    const loadedTreeNode: HypersceneSceneTreeNode | null = this.findSceneTreeNodeById(nodeId, this.sceneTreeRoots);
    if (loadedTreeNode !== null) {
      await this.selectSceneTreeNode(loadedTreeNode);
      return;
    }

    try {
      const node: HypersceneNodeView = await this.ingestAdapter.getNode(this.ingestApiBaseUrl, nodeId);
      await this.selectSceneTreeNode(this.makeSceneTreeNode(node));
    } catch (error: unknown) {
      const errorMessage: string =
        error instanceof Error ? error.message : 'Failed to open selected metadata result node.';
      this.setIngestError(errorMessage);
    }
  }

  public getSelectedNodeExternalLinks(): HypersceneExternalLinkItem[] {
    const links: HypersceneExternalLinkItem[] = [];
    const seen: Set<string> = new Set<string>();

    this.selectedSceneNodeAttributes.forEach((attribute: HypersceneAttributeView) => {
      const matches: string[] = this.extractExternalUrls(attribute.value);
      matches.forEach((url: string) => {
        if (seen.has(url)) {
          return;
        }

        seen.add(url);
        links.push({
          label: `${attribute.groupName} / ${attribute.name}`,
          url,
        });
      });
    });

    return links;
  }

  private clearViewerScene(): void {
    this.bumpSceneEpochAndCancelDecode();
    this.clearSceneTextures();
    this.lastNavigationSpeedUpdateTime = 0;
    this.navigationSpeedSampleRevision++;
    this.navigationSpeedSampling = false;
    this.navigationSpeedMultiplier = 1;
    this.clearSceneMaterialCache();
    this.resetEnterpriseMetadataState();
    this.sceneTreeRoots = [];
    this.selectedSceneNodeId = null;
    this.selectedSceneNodeTitle = null;
    this.selectedSceneNodeAttributes = [];
    this.selectedSceneNodeVisibility = 'unknown';
    this.selectedNodeBoundingBox = null;
    this.selectedNodeBoundingBoxNodeId = null;
    this.selectionHighlightNodeIds = new Set();
    this.selectionHighlight.clear();
    // The next scene has its own extent; a section of this one would cut it at random.
    this.sceneBounds = null;
    this.section.mode = 'off';
    this.section.setBounds(null);
    this.sectionPickArmed = false;
    this.sectionDragArmed = false;
    this.viewerLabels?.clear();
    this.overlays?.clear(SECTION_OVERLAY);
    this.lastDecodedMeshes = [];
    this.lastDecodedLines = [];
    this.lastDecodedPointClouds = [];
    this.frustumSceneSessionId = null;
    this.sceneMeshGeometryIds = [];
    this.sceneLineGeometryIds = [];
    this.scenePointCloudGeometryIds = [];
    this.sceneMeshRenderKeys = [];
    this.sceneLineRenderKeys = [];
    this.scenePointCloudRenderKeys = [];
    this.sceneMeshGeometryIdByRenderKey = {};
    this.sceneMeshTextureIdByRenderKey = {};
    this.sceneLineGeometryIdByRenderKey = {};
    this.scenePointCloudGeometryIdByRenderKey = {};
    this.sceneMaterialRgbaById = {};
    this.sceneMaterialVertexGroupsById = {};
    this.clearOriginalMeshSources();
    this.sceneLoadDebug = {
      ...this.sceneLoadDebug,
      totalSceneObjectsCount: 0,
    };
    this.ingestState = {
      ...this.ingestState,
      sceneId: null,
      modelId: null,
      rootNodeId: null,
      phase: 'idle',
      message: '',
      error: null,
    };
    const queueSizeBeforeClear: number = this.objectsQueue?.objects.size ?? 0;
    const clearSpan: HsViewerProfilerSpan | null = this.viewerProfilingEnabled
      ? this.viewerProfiler?.startSpan('scene.clear') ?? null
      : null;
    try {
      this.sceneSource.close();
      this.refreshTransientOverlays();
      this.overlays?.clear(FRUSTUM_DEBUG_OVERLAYS);
    } finally {
      if (clearSpan !== null) {
        this.viewerProfiler?.endSpan(clearSpan);
      }
      this.viewerProfiler?.recordEvent(
        'scene.clear.done',
        `queueBefore=${queueSizeBeforeClear};queueAfter=${this.objectsQueue?.objects.size ?? 0}`
      );
    }
    this.scheduleSceneGeometryDebugUpdate();
    this.changeDetector.markForCheck();
  }

  public async loadSelectedSceneFromRegistry(): Promise<void> {
    if (this.selectedSceneId === null) {
      this.setIngestError('Select scene to load.');
      return;
    }

    if (this.sceneLoadInProgress) {
      return;
    }

    this.sceneLoadInProgress = true;
    const requestedSceneId: string = this.selectedSceneId;

    const currentRequestId: number = ++this.sceneLoadRequestId;
    this.firstGeometryRefreshFinished = false;
    this.beginViewerLoading(`Loading scene ${requestedSceneId}...`);

    this.sceneLoadDebug = {
      ...this.sceneLoadDebug,
      loadMode: 'root-only',
      lastUpdatedAt: new Date().toLocaleTimeString(),
      lastError: 'loading',
    };
    this.changeDetector.markForCheck();

    this.bumpSceneEpochAndCancelDecode();
    this.resetEnterpriseMetadataState();
    this.clearViewerScene();
    this.ingestState = { ...this.ingestState, message: `Loading scene ${requestedSceneId}...` };
    const requestedSceneEpoch: number = this.sceneEpoch;

    let viewpointsLoadTask: Promise<void> | null = null;
    try {
      const sceneInfo: HypersceneSceneView = await this.ingestAdapter.getScene(this.ingestApiBaseUrl, requestedSceneId);
      if (!this.isSceneLoadCurrent(requestedSceneId, requestedSceneEpoch)) {
        return;
      }
      this.ingestState = {
        ...this.ingestState,
        sceneId: sceneInfo.id,
        phase: 'ready',
        message: `Scene ${sceneInfo.id} loaded.`,
        error: null,
      };
      this.frustumSceneSessionId = getUuid();
      this.applySceneDefaults(sceneInfo);
      const preparation: HypersceneInitialScenePreparation = await this.prepareSceneForGeometry(sceneInfo.id);
      viewpointsLoadTask = preparation.viewpointsLoadTask;
      if (!this.isSceneLoadCurrent(requestedSceneId, requestedSceneEpoch)) {
        return;
      }
      this.applyPendingViewUrlParameters(requestedSceneId);
      await this.syncRenderedGeometryWithSceneVisibility(sceneInfo.id);
      if (!this.isSceneLoadCurrent(requestedSceneId, requestedSceneEpoch)) {
        return;
      }
      if (!preparation.sceneTreeLoaded) {
        await this.reloadSceneTree();
      }
      if (!this.isSceneLoadCurrent(requestedSceneId, requestedSceneEpoch)) {
        return;
      }
      if (viewpointsLoadTask === null) {
        viewpointsLoadTask = this.loadSceneViewpoints(sceneInfo.id);
      }
      if (viewpointsLoadTask !== null) {
        await viewpointsLoadTask;
      }
    } catch (error: unknown) {
      if (viewpointsLoadTask !== null) {
        try {
          await viewpointsLoadTask;
        } catch {
          // Preserve the original scene-loading error.
        }
      }
      if (this.isDecodeCanceledError(error)) {
        this.ingestState = {
          ...this.ingestState,
          message: 'Scene loading restarted; previous decode tasks were canceled.',
          error: null,
        };
        if (currentRequestId === this.sceneLoadRequestId) {
          this.sceneLoadDebug = {
            ...this.sceneLoadDebug,
            lastUpdatedAt: new Date().toLocaleTimeString(),
            lastError: 'canceled',
          };
        }
        this.changeDetector.markForCheck();
        return;
      }

      const errorMessage: string = error instanceof Error ? error.message : 'Failed to load selected scene.';
      if (currentRequestId === this.sceneLoadRequestId) {
        this.sceneLoadDebug = {
          ...this.sceneLoadDebug,
          lastUpdatedAt: new Date().toLocaleTimeString(),
          lastError: errorMessage,
        };
      }
      this.setIngestError(errorMessage);
      return;
    } finally {
      if (currentRequestId === this.sceneLoadRequestId && this.sceneLoadDebug.lastError === 'loading') {
        this.sceneLoadDebug = {
          ...this.sceneLoadDebug,
          lastUpdatedAt: new Date().toLocaleTimeString(),
          lastError: 'none',
        };
        this.changeDetector.markForCheck();
      }
      this.sceneLoadInProgress = false;
      this.finishViewerLoadingIfIdle();
      if (
        this.runtimeSettingsError === null &&
        (requestedSceneId !== this.selectedSceneId || this.runtimeSceneReloadPending)
      ) {
        this.runtimeSceneReloadPending = false;
        if (this.selectedSceneId === null) {
          this.clearViewerScene();
        } else {
          void this.loadSelectedSceneFromRegistry();
        }
      } else {
        this.flushFrustumRefreshPendingAfterSceneLoad(requestedSceneId, requestedSceneEpoch);
      }
    }
  }

  private flushFrustumRefreshPendingAfterSceneLoad(sceneId: string, sceneEpoch: number): void {
    if (!this.frustumRefreshPending || this.frustumRefreshInProgress || !this.isSceneLoadCurrent(sceneId, sceneEpoch)) {
      return;
    }
    this.frustumRefreshPending = false;
    this.scheduleFrustumRefresh(sceneId, 0, 'pending-after-scene-load');
  }

  private isSceneLoadCurrent(sceneId: string, sceneEpoch: number): boolean {
    return this.selectedSceneId === sceneId && this.sceneEpoch === sceneEpoch;
  }

  private async prepareSceneForGeometry(sceneId: string): Promise<HypersceneInitialScenePreparation> {
    let sceneTreeLoaded: boolean = false;
    if (!(await this.frameViewerSceneBounds(sceneId))) {
      await this.reloadSceneTree();
      const recoveredSceneRoots: boolean = await this.ensureSceneHasRoots(sceneId);
      sceneTreeLoaded = true;
      if (recoveredSceneRoots) {
        await this.frameViewerSceneBounds(sceneId);
      }
    }
    let viewpointsLoadTask: Promise<void> | null = null;
    if (this.pendingViewpointId !== null) {
      viewpointsLoadTask = this.loadSceneViewpoints(sceneId);
      if (this.pendingViewpointId !== null) {
        await viewpointsLoadTask;
      }
    }
    return { sceneTreeLoaded, viewpointsLoadTask };
  }

  public getRegistryMetadataFilter(): unknown[] {
    if (!this.registryUseTagFilter || this.ingestTag.trim().length === 0) {
      return [];
    }

    return [
      {
        source: this.ingestTag.trim(),
      },
    ];
  }

  public onSceneOpacityChanged(event: Event): void {
    if (!(event.target instanceof HTMLInputElement)) {
      return;
    }

    const parsedValue: number = Number.parseFloat(event.target.value);
    this.sceneOpacity = Number.isNaN(parsedValue) ? 1 : Math.max(0.05, Math.min(1, parsedValue));
    this.lastRenderedSceneSignature = null;
    this.applyVisualControlChanges();
  }

  /** Turns the section off, on as a plane across the current axis, or on as a box around the selection. */
  public setSectionMode(mode: string): void {
    this.ensureSectionBounds();
    if (mode === 'box') {
      void this.startSectionBox();
      return;
    } else {
      if (mode === 'plane' && this.section.mode === 'off') {
        this.section.center();
      }
      this.section.mode = mode === 'plane' ? 'plane' : 'off';
    }
    this.sectionPickArmed = false;
    this.sectionDragArmed = this.sectionDragArmed && this.section.mode !== 'off';
    this.applySection();
  }

  public setSectionAxis(axis: string): void {
    this.ensureSectionBounds();
    this.section.setAxis(axis === 'y' || axis === 'z' ? (axis as HsSectionAxis) : 'x');
    this.applySection();
  }

  public onSectionPositionChanged(event: Event): void {
    if (event.target instanceof HTMLInputElement) {
      const value: number = Number.parseFloat(event.target.value);
      if (!Number.isNaN(value)) {
        this.section.setPosition(value);
        this.applySection();
      }
    }
  }

  public flipSection(): void {
    this.section.flipped = !this.section.flipped;
    this.applySection();
  }

  /**
   * The next click on the model places the section: across the current axis through the point (`point`), or
   * along the clicked face (`face`).
   */
  public armSectionPick(kind: 'point' | 'face' = 'point'): void {
    this.ensureSectionBounds();
    this.sectionPickArmed = kind;
    this.setStatusMessage(
      kind === 'face'
        ? 'Click on a face of the model to cut along it.'
        : `Click on the model to cut across ${this.section.axis.toUpperCase()} there.`
    );
  }

  /** Keeps only the selected node (or the whole scene without a selection) inside a section box. */
  public sectionBoxAroundSelection(): void {
    void this.startSectionBox();
  }

  /**
   * The section box tool: a box around the selected node's bounds (waiting for them when the selection is new), or
   * around the scene without a selection. Downloads are limited to the box too.
   */
  public async startSectionBox(): Promise<void> {
    this.ensureSectionBounds();
    const nodeId: string | null = this.selectedSceneNodeId;
    if (nodeId !== null) {
      try {
        await this.loadSelectedNodeBoundingBox(nodeId);
      } catch {
        // Without the node's bounds the box falls back to the scene.
      }
    }
    if (this.sceneBounds === null) {
      await this.ensureSceneBounds();
      this.ensureSectionBounds();
    }
    const bounds: { min: [number, number, number]; max: [number, number, number] } | null =
      this.selectedOrSceneBounds();
    if (bounds === null && this.section.bounds === null) {
      this.setStatusMessage('Load a scene first.');
      return;
    }
    this.section.boxAround(bounds ?? this.section.bounds!);
    this.sectionPickArmed = false;
    this.applySection();
  }

  /** Moves one face of the section box, from the Tools tab's sliders. */
  public onSectionBoxFaceChanged(axis: HsSectionAxis, side: 'min' | 'max', event: Event): void {
    if (event.target instanceof HTMLInputElement) {
      this.section.setBoxFace(axis, side, Number.parseFloat(event.target.value));
      this.applySection();
    }
  }

  /** Slider range of a box face: the scene's extent on that axis, widened to hold the box. */
  public sectionBoxRange(axis: HsSectionAxis): { min: number; max: number; step: number } {
    const index: number = axis === 'x' ? 0 : axis === 'y' ? 1 : 2;
    const box: { min: readonly number[]; max: readonly number[] } | null = this.section.box;
    const bounds: { min: readonly number[]; max: readonly number[] } | null = this.section.bounds ?? box;
    const min: number = Math.min(bounds?.min[index] ?? 0, box?.min[index] ?? 0);
    const max: number = Math.max(bounds?.max[index] ?? 0, box?.max[index] ?? 0);
    return { min, max, step: Math.max(0.001, (max - min) / 500) };
  }

  /** Slider step of the section position: about 1/500 of the range, at least a millimeter. */
  public get sectionStep(): number {
    const range: { min: number; max: number } | null = this.section.range;
    return range === null ? 0.01 : Math.max(0.001, (range.max - range.min) / 500);
  }

  public onMeasurementEnabledChanged(event: Event): void {
    if (event.target instanceof HTMLInputElement) {
      this.setMeasurementEnabled(event.target.checked);
    }
  }

  public toggleMeasurement(): void {
    this.setMeasurementEnabled(!this.measurements.enabled);
  }

  private setMeasurementEnabled(enabled: boolean): void {
    this.measurements.setEnabled(enabled);
    if (this.measurements.enabled) {
      this.markerPlacementEnabled = false;
    }
    this.sceneStats = {
      ...this.sceneStats,
      measurementEnabled: this.measurements.enabled,
    };
    this.setStatusMessage(this.measurements.enabled ? this.measurements.hint : 'Measurement stopped.');
    this.refreshTransientOverlays();
  }

  public onMeasurementModeChanged(event: Event): void {
    if (event.target instanceof HTMLSelectElement) {
      this.measurements.setMode(event.target.value);
      this.setStatusMessage(this.measurements.hint);
      this.refreshTransientOverlays();
    }
  }

  public onMeasurementSnapModeChanged(event: Event): void {
    if (event.target instanceof HTMLSelectElement) {
      this.measurements.setSnapMode(event.target.value);
    }
  }

  public onMeasurementUnitChanged(event: Event): void {
    if (event.target instanceof HTMLSelectElement) {
      this.measurements.setUnit(event.target.value);
    }
  }

  /** Saves a polyline or an area; distances and angles save themselves when complete. */
  public saveMeasurement(): void {
    const entry: HypersceneMeasurementEntry | null = this.measurements.save();
    if (entry !== null) {
      this.measurements.tool.clear();
      this.setStatusMessage(`Saved ${this.measurements.formatEntry(entry)}.`);
      this.refreshTransientOverlays();
    }
  }

  public clearMeasurement(): void {
    this.measurements.tool.clear();
    this.refreshTransientOverlays();
  }

  public onSelectedMeasurementChanged(event: Event): void {
    if (!(event.target instanceof HTMLSelectElement)) {
      return;
    }

    const entry: HypersceneMeasurementEntry | null = this.measurements.select(
      event.target.value.length > 0 ? event.target.value : null
    );
    if (entry !== null) {
      this.setStatusMessage(`Showing ${this.measurements.formatEntry(entry)}.`);
    }
    this.refreshTransientOverlays();
  }

  public deleteSelectedMeasurement(): void {
    this.measurements.deleteSelected();
    this.measurements.tool.clear();
    this.setStatusMessage(`Measurement removed. Saved measurements: ${this.measurements.history.length}.`);
    this.refreshTransientOverlays();
  }

  public clearMeasurementHistory(): void {
    this.measurements.clearHistory();
    this.measurements.tool.clear();
    this.setStatusMessage('Measurement history cleared.');
    this.refreshTransientOverlays();
  }

  /** Draws the current view again with the current look (opacity, clipping, collision highlight). */
  public rerenderLastIngestGeometry(): void {
    if (this.lastDecodedMeshes.length + this.lastDecodedLines.length + this.lastDecodedPointClouds.length === 0) {
      this.ingestState = {
        ...this.ingestState,
        message: 'No decoded geometry cache yet. Run Upload -> Parse first.',
      };
      this.changeDetector.markForCheck();
      return;
    }

    const renderedStats: HypersceneRenderedObjectsStat | null = this.sceneSource.reapply();
    if (renderedStats === null) {
      return;
    }
    this.ingestState = {
      ...this.ingestState,
      renderedMeshObjectsCount: renderedStats.meshCount,
      renderedLineObjectsCount: renderedStats.lineCount,
      renderedPointCloudObjectsCount: renderedStats.pointCloudCount,
      collidedPointsCount: renderedStats.collidedPointsCount,
      message: `Geometry was re-rendered with collision highlight ${this.ingestHighlightCollisions ? 'on' : 'off'}.`,
      error: null,
    };
    this.changeDetector.markForCheck();
  }

  public async reloadSceneTree(): Promise<void> {
    if (this.ingestState.sceneId === null) {
      this.setIngestError('Run ingest first to initialize scene tree.');
      return;
    }

    this.sceneTreeLoading = true;
    this.changeDetector.markForCheck();

    try {
      const sceneRootNodes: HypersceneNodeSmallView[] = await this.ingestAdapter.getSceneRootNodes(
        this.ingestApiBaseUrl,
        this.ingestState.sceneId
      );
      this.sceneTreeRoots = this.filterSceneContentRoots(sceneRootNodes).map((node: HypersceneNodeSmallView) =>
        this.makeSceneTreeNode(node)
      );
      await this.refreshSceneTreeNodesVisibility(this.sceneTreeRoots);
      this.sceneLoadDebug = {
        ...this.sceneLoadDebug,
        totalSceneObjectsCount: this.countSceneObjects(this.filterSceneContentRoots(sceneRootNodes)),
      };

      this.ingestState = {
        ...this.ingestState,
        message: `Scene tree reloaded (${this.sceneTreeRoots.length} roots).`,
      };
    } catch (error: unknown) {
      const errorMessage: string = error instanceof Error ? error.message : 'Failed to reload scene tree.';
      this.setIngestError(errorMessage);
    } finally {
      this.sceneTreeLoading = false;
      this.changeDetector.markForCheck();
    }
  }

  public async toggleSceneTreeNode(node: HypersceneSceneTreeNode): Promise<void> {
    if (!node.hasChildren) {
      return;
    }

    if (!node.childrenLoaded) {
      node.childrenLoading = true;
      this.changeDetector.markForCheck();
      try {
        await this.sceneTree.loadChildren(this.ingestState.sceneId, node);
      } catch (error: unknown) {
        const errorMessage: string = error instanceof Error ? error.message : 'Failed to load node children.';
        this.setIngestError(errorMessage);
      } finally {
        this.changeDetector.markForCheck();
      }
    }

    node.isExpanded = !node.isExpanded;
    this.changeDetector.markForCheck();
  }

  public async selectSceneTreeNode(node: HypersceneSceneTreeNode): Promise<void> {
    if (this.selectedNodeBoundingBoxNodeId !== node.id) {
      this.selectedNodeBoundingBox = null;
      this.selectedNodeBoundingBoxNodeId = null;
    }
    this.selectedSceneNodeId = node.id;
    this.enterpriseSelectedNodeId = node.id;
    this.selectedSceneNodeTitle = node.title;
    this.selectedSceneNodeVisibility = 'unknown';
    this.selectedSceneNodeLoading = true;
    this.selectedSceneNodeAttributes = [];
    this.selectedSceneNodeDescendantCount = null;
    this.changeDetector.markForCheck();
    void this.highlightSelectedNode(node.id, node.hasChildren).catch(() => undefined);

    try {
      // Details, visibility and bounds are independent requests; asking them one after another took seconds.
      const [fullNode]: [HypersceneNodeView, void, void] = await Promise.all([
        this.ingestAdapter.getNode(this.ingestApiBaseUrl, node.id),
        this.refreshSelectedNodeVisibility(),
        this.loadSelectedNodeBoundingBox(node.id),
      ]);
      if (this.selectedSceneNodeId === node.id) {
        this.selectedSceneNodeDescendantCount = fullNode.allChildrenCount;
      }
      const attributes: HypersceneAttributeView[] = [...(fullNode.attributes ?? [])];
      attributes.sort((left: HypersceneAttributeView, right: HypersceneAttributeView) => {
        const groupCompare: number = (left.groupName ?? '').localeCompare(right.groupName ?? '');
        if (groupCompare !== 0) {
          return groupCompare;
        }
        return (left.name ?? '').localeCompare(right.name ?? '');
      });

      this.selectedSceneNodeTitle = fullNode.title;
      this.selectedSceneNodeAttributes = attributes;
    } catch (error: unknown) {
      const errorMessage: string = error instanceof Error ? error.message : 'Failed to load node details.';
      this.setIngestError(errorMessage);
    } finally {
      this.selectedSceneNodeLoading = false;
      this.changeDetector.markForCheck();
    }
  }

  public async runSceneTreeNodeAction(
    event: Event,
    node: HypersceneSceneTreeNode,
    action: 'hide' | 'unhide' | 'focus'
  ): Promise<void> {
    event.stopPropagation();
    if (this.selectedSceneNodeId !== node.id) {
      await this.selectSceneTreeNode(node);
    }

    if (action === 'focus') {
      this.focusSelectedSceneNode();
      return;
    }

    await this.executeSelectedNodeVisibilityAction(action);
  }

  public async toggleSceneTreeNodeVisibility(event: Event, node: HypersceneSceneTreeNode): Promise<void> {
    const action: 'hide' | 'unhide' = node.visibility === 'hidden' ? 'unhide' : 'hide';
    await this.runSceneTreeNodeAction(event, node, action);
  }

  public async hideSelectedSceneNode(): Promise<void> {
    await this.executeSelectedNodeVisibilityAction('hide');
  }

  public async unhideSelectedSceneNode(): Promise<void> {
    await this.executeSelectedNodeVisibilityAction('unhide');
  }

  public async isolateSelectedSceneNode(): Promise<void> {
    await this.executeSelectedNodeVisibilityAction('isolate');
  }

  public async unhideAllSceneNodes(): Promise<void> {
    const sceneId: string | null = this.ingestState.sceneId;
    if (sceneId === null) {
      this.setIngestError('Scene is not initialized yet. Run ingest first.');
      return;
    }

    // Objects the viewer hid itself come back at once; the server brings back what it hid before.
    const before: HsNodeVisibilityState = this.sceneSource.nodeVisibilityState();
    this.sceneSource.showAllNodes();
    const applied: number = this.sceneSource.nodeVisibilityRevision;
    this.setLoadedSceneTreeNodesVisibility(this.sceneTreeRoots, 'visible');
    if (this.selectedSceneNodeId !== null) {
      this.selectedSceneNodeVisibility = 'visible';
    }
    this.ingestState = { ...this.ingestState, message: 'All scene nodes are unhidden.', error: null };
    this.requestRender();
    this.changeDetector.markForCheck();

    try {
      // Nodes hidden on the server before were never downloaded; the refresh brings them.
      await this.persistSceneVisibility(
        sceneId,
        () => this.ingestAdapter.unhideAllSceneNodes(this.ingestApiBaseUrl, sceneId, this.visibilitySessionId),
        true
      );
    } catch (error: unknown) {
      this.rollBackSceneVisibility(before, applied);
      const errorMessage: string = error instanceof Error ? error.message : 'Failed to unhide all nodes.';
      this.setIngestError(errorMessage);
    } finally {
      this.changeDetector.markForCheck();
    }
  }

  /**
   * Starts a new visibility session for this browser: everything the old session hid shows again and the scene's own
   * visibility applies. The old session's rules expire on the server.
   */
  public async regenerateVisibilitySession(): Promise<void> {
    this.visibilitySessionId = createVisibilitySessionId();
    this.sceneSource.viewSessionId = this.visibilitySessionId;
    this.sceneSource.showAllNodes();
    this.ingestState = { ...this.ingestState, message: 'Started a new visibility session.', error: null };
    this.requestRender();
    this.changeDetector.markForCheck();
    const sceneId: string | null = this.ingestState.sceneId;
    if (sceneId === null) {
      return;
    }
    try {
      // Nodes the old session hid were dropped from the view; the refresh brings them back.
      await this.persistSceneVisibility(sceneId, () => Promise.resolve(), true);
      await this.refreshSceneTreeNodesVisibility(this.sceneTreeRoots);
    } catch (error: unknown) {
      this.setIngestError(error instanceof Error ? error.message : 'Failed to refresh the scene visibility.');
    } finally {
      this.changeDetector.markForCheck();
    }
  }

  public isSelectedSceneRootNode(): boolean {
    if (this.selectedSceneNodeId === null) {
      return false;
    }

    return this.sceneTreeRoots.some((rootNode: HypersceneSceneTreeNode) => rootNode.id === this.selectedSceneNodeId);
  }

  public async removeSelectedRootNodeFromScene(): Promise<void> {
    const sceneId: string | null = this.ingestState.sceneId;
    if (sceneId === null) {
      this.setIngestError('Scene is not initialized yet. Run ingest first.');
      return;
    }

    const selectedNodeId: string | null = this.selectedSceneNodeId;
    if (selectedNodeId === null) {
      this.setIngestError('Select root node in scene tree first.');
      return;
    }

    if (!this.isSelectedSceneRootNode()) {
      this.setIngestError('Only root nodes can be removed from scene.');
      return;
    }

    try {
      const instance: HypersceneSceneModelInstanceView | null = await this.ingestAdapter.getSceneModelInstance(
        this.ingestApiBaseUrl,
        sceneId,
        selectedNodeId
      );
      const force: boolean = instance !== null && this.isMatchingSceneModelInstance(instance, sceneId, selectedNodeId);
      await this.ingestAdapter.removeNodesTreesFromScene(this.ingestApiBaseUrl, sceneId, [selectedNodeId], force);
      this.selectedSceneNodeId = null;
      this.clearSelectedSceneModelInstance();
      this.selectedSceneNodeTitle = null;
      this.selectedSceneNodeAttributes = [];
      this.selectedSceneNodeVisibility = 'unknown';
      this.selectedNodeBoundingBox = null;
      this.selectedNodeBoundingBoxNodeId = null;
      this.enterpriseSelectedNodeId = null;

      await this.loadSelectedSceneFromRegistry();

      this.ingestState = {
        ...this.ingestState,
        message: `Root node ${selectedNodeId} removed from scene ${sceneId}.`,
        error: null,
      };
    } catch (error: unknown) {
      const errorMessage: string = error instanceof Error ? error.message : 'Failed to remove root node from scene.';
      this.setIngestError(errorMessage);
      return;
    }

    this.changeDetector.markForCheck();
  }

  public moveCameraHome(): void {
    this.cameraPoint.copy(this.homeCameraPoint);
    this.observablePoint.copy(this.homeObservablePoint);
    this.camera.worldMatrix.setPosition(this.cameraPoint.values);
    HsHypersceneMathUtils.lookAt(
      this.camera.worldMatrix,
      new HsVector3(...this.cameraPoint.values),
      this.observablePoint,
      HsHypersceneEngineConstants.UP
    );
    this.lastRenderedObjectIndex = 0;
    this.frustumUpdateTime = 1;
    this.ingestState = {
      ...this.ingestState,
      message: 'Camera reset to home view.',
    };
    this.changeDetector.markForCheck();
  }

  /** Zooms to the selected node without turning the camera, and orbits around it from then on. */
  public async focusSelectedSceneNode(): Promise<void> {
    const nodeId: string | null = this.selectedSceneNodeId;
    if (nodeId === null) {
      this.setStatusMessage('Select a scene node first.');
      return;
    }
    try {
      await this.loadSelectedNodeBoundingBox(nodeId);
    } catch {
      // An invalid box is reported below as no geometry.
    }
    const bounds: HypersceneBoundingBoxView | null = this.selectedNodeBoundingBox;
    if (nodeId !== this.selectedSceneNodeId || !this.isValidBoundingBox(bounds)) {
      this.setStatusMessage('The selected node has no geometry to zoom to.');
      return;
    }
    this.fitCameraToBounds(bounds);
    this.setStatusMessage(`Zoomed to "${this.selectedSceneNodeTitle ?? nodeId}".`);
  }

  /** Zooms to the whole scene without turning the camera. */
  public async focusLoadedScene(): Promise<void> {
    await this.fitAll();
  }

  /** Zooms to the whole scene without turning the camera; the scene's extent comes from the API once. */
  public async fitAll(): Promise<void> {
    const bounds: HypersceneBoundingBoxView | null = await this.ensureSceneBounds();
    if (bounds === null) {
      this.setStatusMessage('Load a scene first.');
      return;
    }
    this.fitCameraToBounds(bounds);
    this.setStatusMessage('Zoomed to the whole scene.');
  }

  /** Looks at the whole scene from a standard view: front, back, left, right, top, bottom or isometric. */
  public async setCameraView(view: HsCameraView): Promise<void> {
    const bounds: HypersceneBoundingBoxView | null = await this.ensureSceneBounds();
    if (bounds === null) {
      this.setStatusMessage('Load a scene first.');
      return;
    }
    const { center, radius } = boundsSphere(bounds);
    this.applyCameraMatrix(hsCameraViewMatrix(view, center, radius, this.camera.fov, this.canvasAspect()));
    this.setPivot(center);
    this.setStatusMessage(`${view === 'iso' ? 'Isometric' : view[0].toUpperCase() + view.slice(1)} view.`);
  }

  /** Double click: turns the camera to the model point under the cursor and orbits around it from then on. */
  public focusAtScreenPoint(clientX: number, clientY: number): void {
    const hit: HsMeasurementHit | null = this.pickMeasurementSurface(clientX, clientY);
    if (hit === null) {
      return;
    }
    this.setPivot(hit.point);
    this.focusCameraAtScreenPoint(clientX, clientY);
  }

  /** X-ray: the scene turns faint while the selection stays solid, to find it inside the model. */
  public toggleXray(): void {
    this.xrayEnabled = !this.xrayEnabled;
    this.lastRenderedSceneSignature = null;
    this.applyVisualControlChanges();
    this.setStatusMessage(this.xrayEnabled ? 'X-ray on: the selection stays solid.' : 'X-ray off.');
  }

  /**
   * Parallel projection, as for plans and elevations: the field of view narrows and the camera backs away so the
   * view keeps its size. A telephoto view rather than an orthographic camera, so streaming, picking and depth stay
   * as they are.
   */
  public toggleParallelProjection(): void {
    const turningOn: boolean = !this.parallelProjection;
    if (turningOn) {
      this.perspectiveFov = this.camera.fov;
      this.perspectiveFar = this.camera.far;
    }
    const fromFov: number = this.camera.fov;
    const toFov: number = turningOn ? PARALLEL_FOV : this.perspectiveFov;
    const values: HsHypersceneTypes.Array16 = this.camera.worldMatrix.values;
    const eye: number[] = [values[12], values[13], values[14]];
    const forward: number[] = [-values[8], -values[9], -values[10]];
    const distance: number = Math.max(
      0.5,
      Math.hypot(this.orbitPivot.x - eye[0], this.orbitPivot.y - eye[1], this.orbitPivot.z - eye[2])
    );
    const nextDistance: number = (distance * Math.tan(fromFov / 2)) / Math.tan(toFov / 2);
    const back: number = nextDistance - distance;
    const matrix: HsHypersceneTypes.Array16 = [...values] as HsHypersceneTypes.Array16;
    matrix[12] = eye[0] - forward[0] * back;
    matrix[13] = eye[1] - forward[1] * back;
    matrix[14] = eye[2] - forward[2] * back;
    this.parallelProjection = turningOn;
    this.camera.fov = toFov;
    const radius: number = this.sceneBounds === null ? distance : boundsSphere(this.sceneBounds).radius;
    this.camera.far = turningOn ? Math.max(this.perspectiveFar, nextDistance + radius * 4) : this.perspectiveFar;
    this.applyCameraMatrix(matrix);
    this.setStatusMessage(turningOn ? 'Parallel projection on.' : 'Perspective projection.');
  }

  /** Real meters per world unit of the open scene, 1 unless calibrated. */
  public get measurementScale(): number {
    return this.measurements.tool.scale;
  }

  /** Calibrates the open scene's units: the current distance or path is `realMeters` long in reality. */
  public calibrateMeasurementScale(realMeters: string): void {
    const sceneId: string | null = this.ingestState.sceneId;
    const meters: number = Number.parseFloat(realMeters.replace(',', '.'));
    const scale: number | null = sceneId === null || !(meters > 0) ? null : this.measurements.calibrate(meters);
    if (sceneId === null || scale === null) {
      this.setStatusMessage('Measure a known distance first, then enter its real length in meters.');
      return;
    }
    this.annotations.storeMeasurementScale(sceneId, scale);
    this.setStatusMessage(`Calibrated: 1 model unit = ${scale.toPrecision(4)} m in this scene.`);
    this.refreshTransientOverlays();
  }

  public resetMeasurementScale(): void {
    const sceneId: string | null = this.ingestState.sceneId;
    if (sceneId !== null) {
      this.annotations.storeMeasurementScale(sceneId, 1);
    }
    this.measurements.tool.scale = 1;
    this.setStatusMessage('Calibration removed: model units are meters.');
    this.refreshTransientOverlays();
  }

  /** While on, a left drag moves the section plane along its normal. */
  public toggleSectionDrag(): void {
    this.sectionDragArmed = !this.sectionDragArmed && this.section.mode !== 'off';
    this.setStatusMessage(
      this.sectionDragArmed
        ? this.section.mode === 'box'
          ? 'Drag a face of the box to resize it; turn this off to orbit again.'
          : 'Drag on the view to move the section plane; turn this off to orbit again.'
        : this.section.mode !== 'off'
        ? 'Section dragging off.'
        : 'Turn on a section first.'
    );
  }

  /** Escape: stops the active tool's current step, then clears the selection. */
  public cancelToolsAndSelection(): void {
    if (this.sectionDragArmed) {
      this.toggleSectionDrag();
      return;
    }
    if (this.sectionPickArmed !== false) {
      this.sectionPickArmed = false;
      this.setStatusMessage('Section placement canceled.');
      return;
    }
    if (this.measurements.tool.points.length > 0) {
      this.clearMeasurement();
      return;
    }
    this.clearSelection();
  }

  /** Clears the selection and its highlight. */
  public clearSelection(): void {
    this.selectedSceneNodeId = null;
    this.enterpriseSelectedNodeId = null;
    this.selectedSceneNodeTitle = null;
    this.selectedSceneNodeAttributes = [];
    this.selectedSceneNodeVisibility = 'unknown';
    this.selectedNodeBoundingBox = null;
    this.selectedNodeBoundingBoxNodeId = null;
    this.highlightNodes(new Set());
    this.changeDetector.markForCheck();
  }

  public getSceneDisplayLabel(scene: HypersceneSceneView): string {
    return `${this.getSceneDisplayName(scene)} | ${this.getSceneDisplayDate(scene)}`;
  }

  public getSceneDisplayName(scene: HypersceneSceneView): string {
    const sceneName: string | null = this.tryExtractMetadataString(scene.metadata, 'sceneName');
    const fileName: string | null = this.tryExtractMetadataString(scene.metadata, 'fileName');
    return sceneName !== null && fileName !== null && sceneName.startsWith(`${fileName} | file:`)
      ? this.normalizeSceneDisplayName(fileName)
      : this.normalizeSceneDisplayName(sceneName ?? fileName ?? 'Unnamed scene');
  }

  public getSceneDisplayDate(scene: HypersceneSceneView): string {
    return this.formatModelDate(this.resolveSceneCreatedAt(scene));
  }

  public getSceneDisplayLabelById(sceneId: string): string {
    const scene: HypersceneSceneView | undefined = this.scenesRegistry.find(
      (sceneView: HypersceneSceneView) => sceneView.id === sceneId
    );
    return scene === undefined ? 'Current scene' : this.getSceneDisplayLabel(scene);
  }

  private resolveSceneCreatedAt(scene: HypersceneSceneView): string | null {
    return scene.createdAt ?? this.tryExtractMetadataString(scene.metadata, 'createdAt');
  }

  private sortModelsByCreatedAt(models: HypersceneModelView[]): HypersceneModelView[] {
    return this.sortByCreationDate(models, (model: HypersceneModelView) => this.resolveModelCreationTimestamp(model));
  }

  private sortScenesByCreatedAt(scenes: HypersceneSceneView[]): HypersceneSceneView[] {
    return this.sortByCreationDate(scenes, (scene: HypersceneSceneView) => this.resolveSceneCreationTimestamp(scene));
  }

  private sortByCreationDate<T>(items: T[], resolveTimestamp: (item: T) => number | null): T[] {
    interface SortEntry {
      item: T;
      index: number;
      timestamp: number | null;
    }

    return items
      .map((item: T, index: number) => ({ item, index, timestamp: resolveTimestamp(item) }))
      .sort((left: SortEntry, right: SortEntry) => {
        if (left.timestamp === null && right.timestamp !== null) {
          return 1;
        }
        if (left.timestamp !== null && right.timestamp === null) {
          return -1;
        }
        if (left.timestamp !== null && right.timestamp !== null && left.timestamp !== right.timestamp) {
          return right.timestamp - left.timestamp;
        }
        return left.index - right.index;
      })
      .map(({ item }: SortEntry) => item);
  }

  private resolveModelCreationTimestamp(model: HypersceneModelView): number | null {
    const directCreatedAt: string | null = model.createdAt ?? model.uploadedAt ?? null;
    const metadataCreatedAt: string | null = this.tryExtractModelCreatedAt(model.metadata);
    const processingDates: string[] = this.processingsRegistry
      .filter((processing: HypersceneProcessingView) => processing.modelId === model.id)
      .flatMap((processing: HypersceneProcessingView) => [processing.completed, processing.started])
      .filter((value: string | null | undefined): value is string => value !== undefined && value !== null);

    for (const value of [directCreatedAt, metadataCreatedAt, ...processingDates]) {
      if (value === null || value.trim().length === 0) {
        continue;
      }
      const timestamp: number = Date.parse(value);
      if (!Number.isNaN(timestamp)) {
        return timestamp;
      }
    }

    return null;
  }

  private resolveSceneCreationTimestamp(scene: HypersceneSceneView): number | null {
    const createdAt: string | null = scene.createdAt ?? this.tryExtractMetadataString(scene.metadata, 'createdAt');
    if (createdAt === null || createdAt.trim().length === 0) {
      return null;
    }

    const timestamp: number = Date.parse(createdAt);
    return Number.isNaN(timestamp) ? null : timestamp;
  }

  private normalizeSceneDisplayName(name: string): string {
    return name.trim();
  }

  public async toggleFullscreen(): Promise<void> {
    const canvasElement: HTMLCanvasElement | undefined = this.canvasDocument?.nativeElement;
    if (canvasElement === undefined) {
      return;
    }

    try {
      if (this.document.fullscreenElement === null) {
        await canvasElement.requestFullscreen();
      } else {
        await this.document.exitFullscreen();
      }
    } catch (error: unknown) {
      const errorMessage: string = error instanceof Error ? error.message : 'Failed to switch fullscreen mode.';
      this.setIngestError(errorMessage);
    }
  }

  public async saveCurrentViewpoint(): Promise<void> {
    if (this.ingestState.sceneId === null) {
      this.setIngestError('Load a scene before saving a viewpoint.');
      return;
    }
    if (this.ingestState.phase !== 'ready') {
      this.setIngestError('Wait until the scene finishes loading before saving a viewpoint.');
      return;
    }

    const viewpointName: string = this.viewpointCreateNameDraft.trim();
    if (viewpointName.length === 0) {
      this.setIngestError('Viewpoint name cannot be empty.');
      return;
    }

    const cameraMatrix: HsHypersceneTypes.Array16 = this.captureCurrentCameraMatrix();
    const viewpoint: HypersceneViewpoint = {
      id: getUuid(),
      name: viewpointName,
      cameraMatrix,
    };

    this.viewpoints = [viewpoint, ...this.viewpoints];
    this.selectedViewpointId = viewpoint.id;
    this.viewpointNameDraft = viewpoint.name;
    this.viewpointRenameActive = false;
    this.viewpointCreateNameDraft = this.getNextViewpointName();
    try {
      await this.persistViewpointsToBackend(this.ingestState.sceneId);
      this.ingestState = {
        ...this.ingestState,
        message: `Saved ${viewpoint.name} to the current scene.`,
        error: null,
      };
      this.changeDetector.markForCheck();
    } catch (error: unknown) {
      this.viewpoints = this.viewpoints.filter((item: HypersceneViewpoint) => item.id !== viewpoint.id);
      this.selectedViewpointId = this.viewpoints[0]?.id ?? null;
      this.viewpointNameDraft = this.getSelectedViewpoint()?.name ?? '';
      this.viewpointCreateNameDraft = viewpoint.name;
      const errorMessage: string = error instanceof Error ? error.message : 'Failed to save viewpoint to backend.';
      this.setIngestError(errorMessage);
    }
  }

  public refreshSelectedViewpoint(): void {
    if (this.selectedViewpointId === null) {
      this.setIngestError('Select viewpoint to refresh.');
      return;
    }

    const viewpoint: HypersceneViewpoint | undefined = this.viewpoints.find(
      (item: HypersceneViewpoint) => item.id === this.selectedViewpointId
    );
    if (viewpoint === undefined) {
      this.setIngestError('Selected viewpoint was not found.');
      return;
    }

    this.applyViewpoint(viewpoint);
    this.ingestState = {
      ...this.ingestState,
      message: `Refreshed view to ${viewpoint.name}.`,
      error: null,
    };
    this.changeDetector.markForCheck();
  }

  public async updateSelectedViewpoint(): Promise<void> {
    if (this.selectedViewpointId === null) {
      this.setIngestError('Select viewpoint to update.');
      return;
    }

    const viewpoint: HypersceneViewpoint | undefined = this.viewpoints.find(
      (item: HypersceneViewpoint) => item.id === this.selectedViewpointId
    );
    if (viewpoint === undefined) {
      this.setIngestError('Selected viewpoint was not found.');
      return;
    }

    const confirmUpdate: (() => boolean) | undefined = this.document.defaultView?.confirm
      ? () => this.document.defaultView?.confirm(`Update viewpoint "${viewpoint.name}" to the current view?`) ?? false
      : undefined;
    if (confirmUpdate === undefined || !confirmUpdate()) {
      return;
    }

    const previousViewpoints: HypersceneViewpoint[] = this.viewpoints;
    const cameraMatrix: HsHypersceneTypes.Array16 = this.captureCurrentCameraMatrix();
    this.viewpoints = previousViewpoints.map((item: HypersceneViewpoint) =>
      item.id === viewpoint.id ? { ...item, cameraMatrix } : item
    );

    try {
      await this.persistViewpointsToBackend(this.ingestState.sceneId);
      this.ingestState = {
        ...this.ingestState,
        message: `Updated viewpoint ${viewpoint.name}.`,
        error: null,
      };
      this.changeDetector.markForCheck();
    } catch (error: unknown) {
      this.viewpoints = previousViewpoints;
      const errorMessage: string = error instanceof Error ? error.message : 'Failed to update viewpoint in backend.';
      this.setIngestError(errorMessage);
    }
  }

  public async deleteSelectedViewpoint(): Promise<void> {
    if (this.selectedViewpointId === null) {
      return;
    }

    const removedViewpoint: HypersceneViewpoint | undefined = this.viewpoints.find(
      (item: HypersceneViewpoint) => item.id === this.selectedViewpointId
    );
    const confirmDelete: (() => boolean) | undefined = this.document.defaultView?.confirm
      ? () =>
          this.document.defaultView?.confirm(`Delete viewpoint "${removedViewpoint?.name ?? 'selected viewpoint'}"?`) ??
          false
      : undefined;
    if (confirmDelete === undefined || !confirmDelete()) {
      return;
    }

    const previousViewpoints: HypersceneViewpoint[] = this.viewpoints;
    this.viewpoints = previousViewpoints.filter((item: HypersceneViewpoint) => item.id !== this.selectedViewpointId);
    this.selectedViewpointId = this.viewpoints[0]?.id ?? null;
    this.viewpointNameDraft = this.getSelectedViewpoint()?.name ?? '';
    this.viewpointCreateNameDraft = this.getNextViewpointName();
    this.viewpointRenameActive = false;
    try {
      await this.persistViewpointsToBackend(this.ingestState.sceneId);
      this.ingestState = {
        ...this.ingestState,
        message: removedViewpoint ? `Deleted ${removedViewpoint.name}.` : 'Deleted viewpoint.',
        error: null,
      };
      this.changeDetector.markForCheck();
    } catch (error: unknown) {
      this.viewpoints = previousViewpoints;
      this.selectedViewpointId = removedViewpoint?.id ?? null;
      this.viewpointNameDraft = this.getSelectedViewpoint()?.name ?? '';
      this.viewpointCreateNameDraft = this.getNextViewpointName();
      const errorMessage: string = error instanceof Error ? error.message : 'Failed to delete viewpoint from backend.';
      this.setIngestError(errorMessage);
    }
  }

  public onSelectedViewpointChanged(event: Event): void {
    if (!(event.target instanceof HTMLSelectElement)) {
      return;
    }

    this.selectedViewpointId = event.target.value.length > 0 ? event.target.value : null;
    this.viewpointNameDraft = this.getSelectedViewpoint()?.name ?? '';
    this.viewpointRenameActive = false;
    if (this.selectedViewpointId !== null) {
      this.refreshSelectedViewpoint();
    }
    this.changeDetector.markForCheck();
  }

  public onViewpointNameDraftChanged(event: Event): void {
    if (!(event.target instanceof HTMLInputElement)) {
      return;
    }

    this.viewpointNameDraft = event.target.value;
  }

  public onViewpointCreateNameDraftChanged(event: Event): void {
    if (!(event.target instanceof HTMLInputElement)) {
      return;
    }

    this.viewpointCreateNameDraft = event.target.value;
  }

  public beginViewpointRename(): void {
    if (this.selectedViewpointId === null) {
      this.setIngestError('Select viewpoint to rename.');
      return;
    }

    this.viewpointNameDraft = this.getSelectedViewpoint()?.name ?? '';
    this.viewpointRenameActive = true;
    this.changeDetector.markForCheck();
  }

  public cancelViewpointRename(): void {
    this.viewpointNameDraft = this.getSelectedViewpoint()?.name ?? '';
    this.viewpointRenameActive = false;
    this.changeDetector.markForCheck();
  }

  public async renameSelectedViewpoint(): Promise<void> {
    if (this.selectedViewpointId === null) {
      this.setIngestError('Select viewpoint to rename.');
      return;
    }

    const nextName: string = this.viewpointNameDraft.trim();
    if (nextName.length === 0) {
      this.setIngestError('Viewpoint name cannot be empty.');
      return;
    }

    const previousViewpoints: HypersceneViewpoint[] = this.viewpoints;
    this.viewpoints = previousViewpoints.map((viewpoint: HypersceneViewpoint) => {
      if (viewpoint.id !== this.selectedViewpointId) {
        return viewpoint;
      }

      return {
        ...viewpoint,
        name: nextName,
      };
    });
    try {
      await this.persistViewpointsToBackend(this.ingestState.sceneId);
      this.ingestState = {
        ...this.ingestState,
        message: `Viewpoint renamed to ${nextName}.`,
        error: null,
      };
      this.viewpointRenameActive = false;
      this.changeDetector.markForCheck();
    } catch (error: unknown) {
      this.viewpoints = previousViewpoints;
      const errorMessage: string = error instanceof Error ? error.message : 'Failed to rename viewpoint in backend.';
      this.setIngestError(errorMessage);
    }
  }

  public onMarkerPlacementEnabledChanged(event: Event): void {
    if (!(event.target instanceof HTMLInputElement)) {
      return;
    }

    this.markerPlacementEnabled = event.target.checked;
    if (this.markerPlacementEnabled && this.measurements.enabled) {
      // One tool takes canvas clicks at a time.
      this.measurements.setEnabled(false);
      this.refreshTransientOverlays();
    }
    this.ingestState = {
      ...this.ingestState,
      message: this.markerPlacementEnabled
        ? 'Marker placement mode enabled. Click on canvas to place markers.'
        : 'Marker placement mode disabled.',
      error: null,
    };
    this.changeDetector.markForCheck();
  }

  public onMarkersVisibleChanged(event: Event): void {
    if (!(event.target instanceof HTMLInputElement)) {
      return;
    }

    this.markersVisible = event.target.checked;
    this.refreshTransientOverlays();
  }

  public onSelectedMarkerChanged(event: Event): void {
    if (!(event.target instanceof HTMLSelectElement)) {
      return;
    }

    this.selectedMarkerId = event.target.value.length > 0 ? event.target.value : null;
    const selectedMarker: HypersceneMarker | undefined = this.getSelectedMarker();
    this.markerNameDraft = selectedMarker?.name ?? '';
    this.refreshTransientOverlays();
  }

  public onMarkerNameDraftChanged(event: Event): void {
    if (!(event.target instanceof HTMLInputElement)) {
      return;
    }

    this.markerNameDraft = event.target.value;
  }

  public renameSelectedMarker(): void {
    if (this.selectedMarkerId === null) {
      this.setIngestError('Select marker to rename.');
      return;
    }

    const nextName: string = this.markerNameDraft.trim();
    if (nextName.length === 0) {
      this.setIngestError('Marker name cannot be empty.');
      return;
    }

    this.markers = this.markers.map((marker: HypersceneMarker) => {
      if (marker.id !== this.selectedMarkerId) {
        return marker;
      }
      return {
        ...marker,
        name: nextName,
      };
    });
    this.saveMarkersToStorage();

    this.ingestState = {
      ...this.ingestState,
      message: `Marker renamed to ${nextName}.`,
      error: null,
    };
    this.refreshTransientOverlays();
  }

  public focusSelectedMarker(): void {
    const selectedMarker: HypersceneMarker | undefined = this.getSelectedMarker();
    if (selectedMarker === undefined) {
      this.setIngestError('Select marker first.');
      return;
    }

    const markerPoint: HsVector3 = new HsVector3(
      selectedMarker.position[0],
      selectedMarker.position[1],
      selectedMarker.position[2]
    );
    this.observablePoint.copy(markerPoint);
    HsGlobal.ROTATE_POINT.copy(markerPoint);
    this.selectedCameraToolType = HsCameraToolType.FocusAt;
    const cameraToolTypeControlAction: HsCameraToolTypeControlAction = new HsCameraToolTypeControlAction(
      HsCameraToolType.FocusAt
    );
    this.controlActionsHandler.updateControlAction(cameraToolTypeControlAction);

    this.ingestState = {
      ...this.ingestState,
      message: `Focused camera on ${selectedMarker.name}.`,
      error: null,
    };
    this.changeDetector.markForCheck();
  }

  public deleteSelectedMarker(): void {
    if (this.selectedMarkerId === null) {
      return;
    }

    const removedMarker: HypersceneMarker | undefined = this.markers.find(
      (marker: HypersceneMarker) => marker.id === this.selectedMarkerId
    );
    this.markers = this.markers.filter((marker: HypersceneMarker) => marker.id !== this.selectedMarkerId);
    this.selectedMarkerId = this.markers[0]?.id ?? null;
    this.markerNameDraft = this.getSelectedMarker()?.name ?? '';
    this.saveMarkersToStorage();
    this.ingestState = {
      ...this.ingestState,
      message: removedMarker ? `Deleted ${removedMarker.name}.` : 'Deleted marker.',
      error: null,
    };
    this.refreshTransientOverlays();
  }

  public clearMarkers(): void {
    this.markers = [];
    this.selectedMarkerId = null;
    this.markerNameDraft = '';
    this.saveMarkersToStorage();
    this.ingestState = {
      ...this.ingestState,
      message: 'All markers were cleared.',
      error: null,
    };
    this.refreshTransientOverlays();
  }

  public exportViewerData(): void {
    const payload: HypersceneViewerTransferPayload = this.annotations.makeTransferPayload(
      this.viewpoints,
      this.markers
    );
    this.viewerDataTransferText = JSON.stringify(payload, null, 2);
    this.ingestState = {
      ...this.ingestState,
      message: `Viewer data exported (${this.viewpoints.length} viewpoints, ${this.markers.length} markers).`,
      error: null,
    };
    this.changeDetector.markForCheck();
  }

  public downloadViewerDataFile(): void {
    const storagePayload: HypersceneViewerTransferPayload = this.annotations.makeTransferPayload(
      this.viewpoints,
      this.markers
    );
    const jsonData: string = JSON.stringify(storagePayload, null, 2);
    this.viewerDataTransferText = jsonData;

    const blob: Blob = new Blob([jsonData], { type: 'application/json' });
    const blobUrl: string = URL.createObjectURL(blob);
    const link: HTMLAnchorElement = this.document.createElement('a');
    const datePart: string = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    link.href = blobUrl;
    link.download = `hyperscene-viewer-data-${datePart}.json`;
    link.click();
    URL.revokeObjectURL(blobUrl);

    this.ingestState = {
      ...this.ingestState,
      message: `Viewer JSON file downloaded (${this.viewpoints.length} viewpoints, ${this.markers.length} markers).`,
      error: null,
    };
    this.changeDetector.markForCheck();
  }

  public importViewerData(): void {
    const rawValue: string = this.viewerDataTransferText.trim();
    if (rawValue.length === 0) {
      this.setIngestError('Paste viewer JSON before import.');
      return;
    }

    try {
      const payload: HypersceneViewerTransferPayload | null = this.annotations.parseTransferPayload(
        JSON.parse(rawValue)
      );
      if (payload === null) {
        this.setIngestError('Invalid viewer JSON structure.');
        return;
      }

      this.applyImportedViewerPayload(payload, 'Viewer data imported');
    } catch {
      this.setIngestError('Failed to parse viewer JSON.');
    }
  }

  public async copyViewerDataToClipboard(): Promise<void> {
    if (this.viewerDataTransferText.trim().length === 0) {
      this.setIngestError('Generate or paste viewer JSON before copy.');
      return;
    }

    try {
      const clipboard: Clipboard | undefined = this.document.defaultView?.navigator?.clipboard;
      if (clipboard === undefined) {
        this.setIngestError('Clipboard access is not available in this browser context.');
        return;
      }

      await clipboard.writeText(this.viewerDataTransferText);
      this.ingestState = {
        ...this.ingestState,
        message: 'Viewer JSON copied to clipboard.',
        error: null,
      };
      this.changeDetector.markForCheck();
    } catch {
      this.setIngestError('Clipboard access is not available in this browser context.');
    }
  }

  public async copyViewerUrlToClipboard(): Promise<void> {
    await this.copyUrlQueryToClipboard(this.standaloneViewerQuery, 'Viewer URL with camera copied to clipboard.');
  }

  public async copyViewpointUrlToClipboard(): Promise<void> {
    if (this.selectedViewpointId === null) {
      this.setIngestError('Select viewpoint before copying its URL.');
      return;
    }

    await this.copyUrlQueryToClipboard(this.standaloneViewpointQuery, 'Viewer URL with viewpoint copied to clipboard.');
  }

  private async copyUrlQueryToClipboard(query: Record<string, string | null>, successMessage: string): Promise<void> {
    const window: Window | null = this.document.defaultView;
    const clipboard: Clipboard | undefined = window?.navigator?.clipboard;
    if (clipboard === undefined || window === null) {
      this.setIngestError('Clipboard access is not available in this browser context.');
      return;
    }

    const url: URL = new URL('/viewer/embed', window.location.origin);
    Object.entries(query).forEach(([key, value]: [string, string | null]) => {
      if (value !== null) {
        url.searchParams.set(key, value);
      }
    });

    try {
      await clipboard.writeText(url.toString());
      this.ingestState = {
        ...this.ingestState,
        message: successMessage,
        error: null,
      };
      this.changeDetector.markForCheck();
    } catch {
      this.setIngestError('Clipboard access is not available in this browser context.');
    }
  }

  public async copySceneDebug(): Promise<void> {
    const snapshot: HypersceneSceneDebugSnapshot = this.getSceneDebugSnapshot();
    const text: string = [
      'Scene debug',
      `URL: ${snapshot.url}`,
      `Captured at: ${snapshot.capturedAt}`,
      `Scene ID: ${snapshot.scene.sceneId ?? 'none'}`,
      `Selected scene ID: ${snapshot.scene.selectedSceneId ?? 'none'}`,
      `Model ID: ${snapshot.scene.modelId ?? 'none'}`,
      `Root node ID: ${snapshot.scene.rootNodeId ?? 'none'}`,
      `Camera position: ${snapshot.cameraPosition.map((value: number) => value.toFixed(2)).join(', ')}`,
      `Current camera draw calls (estimated full redraw): ${snapshot.render.currentCameraDrawCalls}`,
      `Instanced batches: ${snapshot.render.instancedDrawCalls}/${snapshot.render.totalInstancedBatches} visible`,
      `Culled instanced batches: ${snapshot.render.culledInstancedBatches}`,
      `Instanced draw calls: ${snapshot.render.instancedDrawCalls}`,
      `Instanced instances: ${snapshot.render.instancedInstances}`,
      `Cached shader programs: ${snapshot.render.cachedShaderPrograms}`,
      `Active scene triangles: ${snapshot.render.activeSceneTriangles}`,
      `Downsample: ${snapshot.render.downsampleRate}x`,
      `Distributed rendering: ${snapshot.render.distributedRendering ? 'on' : 'off'}`,
      `Queued objects: ${snapshot.sceneStats.queueObjectsCount}`,
      `Frustum visible objects: ${snapshot.sceneStats.frustumVisibleObjectsCount}`,
      `Loaded mesh geometries: ${snapshot.sceneGeometry.loadedMeshGeometriesCount}`,
      `Loaded triangles: ${snapshot.sceneGeometry.loadedTrianglesCount}`,
      `Decoded mesh cache: ${(snapshot.cacheBudgets.meshBytes / 1048576).toFixed(1)} / ${(
        snapshot.cacheBudgets.meshBudgetBytes / 1048576
      ).toFixed(0)} MiB (${snapshot.cacheBudgets.meshCount} meshes)`,
      `Texture cache: ${(snapshot.cacheBudgets.textureBytes / 1048576).toFixed(1)} / ${(
        snapshot.cacheBudgets.textureBudgetBytes / 1048576
      ).toFixed(0)} MiB (${snapshot.cacheBudgets.textureCount} textures)`,
      ...(this.hlodDebug.length > 0 ? [this.hlodDebug] : []),
      `Loaded line segments: ${snapshot.sceneGeometry.loadedLineSegmentsCount}`,
      `Loaded points: ${snapshot.sceneGeometry.loadedPointsCount}`,
      `Last error: ${snapshot.sceneLoad.lastError}`,
    ].join('\n');

    await this.copySceneDebugText(text, 'Scene debug copied to clipboard.');
  }

  public async copySceneDebugAsJson(): Promise<void> {
    const snapshot: HypersceneSceneDebugSnapshot = this.getSceneDebugSnapshot();
    await this.copySceneDebugText(JSON.stringify(snapshot, null, 2), 'Scene debug JSON copied to clipboard.');
  }

  private getSceneDebugSnapshot(): HypersceneSceneDebugSnapshot {
    return {
      capturedAt: new Date().toISOString(),
      url: this.document.defaultView?.location.href ?? '',
      scene: {
        sceneId: this.ingestState.sceneId,
        selectedSceneId: this.selectedSceneId,
        modelId: this.ingestState.modelId,
        rootNodeId: this.ingestState.rootNodeId,
      },
      cameraPosition: [...this.cameraPositionDebug],
      render: {
        currentCameraDrawCalls: this.sceneRenderTotals.drawCalls,
        totalInstancedBatches: this.sceneRenderTotals.totalInstancedBatches,
        culledInstancedBatches: this.sceneRenderTotals.culledInstancedBatches,
        instancedDrawCalls: this.sceneRenderTotals.instancedDrawCalls,
        instancedInstances: this.sceneRenderTotals.instancedInstances,
        cachedShaderPrograms: this.sceneCachedShaderPrograms,
        activeSceneTriangles: this.sceneRenderTotals.triangles,
        downsampleRate: this.sceneStats.downsampleRate,
        distributedRendering: this.sceneStats.distributedRendering,
      },
      sceneStats: { ...this.sceneStats },
      sceneLoad: { ...this.sceneLoadDebug },
      sceneGeometry: { ...this.sceneGeometryDebug },
      cacheBudgets: this.getSceneCacheBudgetDebug(),
      sceneOverview: { ...this.sceneOverviewDebug },
      profiler: this.viewerProfiler.snapshot(),
      ingest: { ...this.ingestState },
    };
  }

  private async copySceneDebugText(text: string, successMessage: string): Promise<void> {
    try {
      const clipboard: Clipboard | undefined = this.document.defaultView?.navigator?.clipboard;
      if (clipboard === undefined) {
        this.setIngestError('Clipboard access is not available in this browser context.');
        return;
      }

      await clipboard.writeText(text);
      this.ingestState = {
        ...this.ingestState,
        message: successMessage,
        error: null,
      };
      this.changeDetector.markForCheck();
    } catch {
      this.setIngestError('Clipboard access is not available in this browser context.');
    }
  }

  public async onViewerDataFileChanged(event: Event): Promise<void> {
    if (!(event.target instanceof HTMLInputElement)) {
      return;
    }

    const file: File | null = event.target.files?.[0] ?? null;
    if (file === null) {
      return;
    }

    try {
      const fileText: string = await file.text();
      this.viewerDataTransferText = fileText;
      const payload: HypersceneViewerTransferPayload | null = this.annotations.parseTransferPayload(
        JSON.parse(fileText)
      );
      if (payload === null) {
        this.setIngestError('Invalid viewer JSON file structure.');
        return;
      }

      this.applyImportedViewerPayload(payload, `Viewer data loaded from ${file.name}`);
    } catch {
      this.setIngestError('Failed to read viewer JSON file.');
    } finally {
      event.target.value = '';
    }
  }

  public async saveViewerStateToBackend(): Promise<void> {
    if (this.ingestState.sceneId === null) {
      this.setIngestError('Scene is not initialized yet. Run ingest first.');
      return;
    }

    try {
      await this.annotations.saveViewpoints(this.ingestState.sceneId, this.viewpoints);
      await this.annotations.saveMarkers(this.ingestState.sceneId, this.markers);

      this.ingestState = {
        ...this.ingestState,
        message: `Viewer data persisted to backend scene (${this.viewpoints.length} viewpoints, ${this.markers.length} markers).`,
        error: null,
      };
      this.changeDetector.markForCheck();
    } catch (error: unknown) {
      const errorMessage: string = error instanceof Error ? error.message : 'Failed to persist viewer data to backend.';
      this.setIngestError(errorMessage);
    }
  }

  private async persistViewpointsToBackend(sceneId: string | null): Promise<void> {
    if (sceneId === null) {
      throw new Error('Scene is not initialized yet. Run ingest first.');
    }

    await this.annotations.saveViewpoints(sceneId, this.viewpoints);
  }

  public async loadViewerStateFromBackend(): Promise<void> {
    if (this.ingestState.sceneId === null) {
      this.setIngestError('Scene is not initialized yet. Run ingest first.');
      return;
    }

    try {
      const [viewpoints, markers]: [HypersceneViewpoint[], HypersceneMarker[]] = await Promise.all([
        this.annotations.loadViewpoints(this.ingestState.sceneId),
        this.annotations.loadMarkers(this.ingestState.sceneId),
      ]);

      const payload: HypersceneViewerTransferPayload = this.annotations.makeTransferPayload(viewpoints, markers);
      this.viewerDataTransferText = JSON.stringify(payload, null, 2);
      this.applyImportedViewerPayload(payload, 'Viewer data loaded from backend');
    } catch (error: unknown) {
      const errorMessage: string = error instanceof Error ? error.message : 'Failed to load viewer data from backend.';
      this.setIngestError(errorMessage);
    }
  }

  public clearViewerDataTransferText(): void {
    this.viewerDataTransferText = '';
    this.changeDetector.markForCheck();
  }

  public onViewerDataTransferTextChanged(event: Event): void {
    if (!(event.target instanceof HTMLTextAreaElement)) {
      return;
    }

    this.viewerDataTransferText = event.target.value;
  }

  public onViewerDataImportModeChanged(event: Event): void {
    if (!(event.target instanceof HTMLSelectElement)) {
      return;
    }

    this.viewerDataImportMode = event.target.value === 'merge' ? 'merge' : 'replace';
  }

  public startTouchMove(direction: 'forward' | 'backward', event: PointerEvent): void {
    event.preventDefault();
    this.stopTouchMove();
    this.touchMoveActionType =
      direction === 'forward' ? HsControlActionType.PressedTop : HsControlActionType.PressedBottom;
    try {
      // Capture keeps the button held when the finger slides off it; it throws for inactive pointers.
      (event.currentTarget as HTMLElement | null)?.setPointerCapture(event.pointerId);
    } catch {
      // Without capture, sliding off the button ends the move through pointerleave.
    }
    this.updateTouchNavigationActive();
    this.ngZone.runOutsideAngular(() => this.continueTouchMove());
  }

  public fitCameraFromTouchControls(): void {
    this.moveCameraHome();
    // Touch input produces no further mouse or wheel events that would schedule the next frame.
    this.requestRender();
    this.finishCameraNavigation();
  }

  public stopTouchMove(): void {
    if (this.touchMoveActionType === null) {
      return;
    }
    this.controlActionsHandler.updateControlAction(new HsKeyboardControlAction(this.touchMoveActionType, false));
    this.touchMoveActionType = null;
    window.cancelAnimationFrame(this.touchMoveFrameId);
    this.touchMoveFrameId = 0;
    this.updateTouchNavigationActive();
    this.finishCameraNavigation();
  }

  private continueTouchMove(): void {
    if (this.touchMoveActionType === null) {
      return;
    }
    // Held buttons emit no repeat events like keys do, so keep refreshing the adaptive speed and the frame.
    const canvas: HTMLCanvasElement | null = this.resolveViewerCanvasElement();
    if (canvas !== null && this.adaptiveNavigationKeyboard) {
      const bounds: DOMRect = canvas.getBoundingClientRect();
      this.updateNavigationSpeed(bounds.left + bounds.width / 2, bounds.top + bounds.height / 2);
    }
    this.controlActionsHandler.updateControlAction(
      new HsKeyboardControlAction(
        this.touchMoveActionType,
        true,
        this.adaptiveNavigationKeyboard ? this.navigationSpeedMultiplier : 1
      )
    );
    this.requestRender();
    this.touchMoveFrameId = window.requestAnimationFrame(() => this.continueTouchMove());
  }

  private updateTouchNavigationActive(): void {
    this.touchNavigationActive = this.touchGestures.activePointerCount > 0 || this.touchMoveActionType !== null;
  }

  private resolveTouchPivot(position: HsVector2, gestureId: number): HsVector3 | null {
    if (gestureId !== this.touchPivotGestureId) {
      this.touchPivotGestureId = gestureId;
      this.touchPivotRequestedAt = performance.now();
    }
    // Only a pick requested during this pinch describes the content under the fingers.
    if (this.navigationPickPoint !== null && this.navigationPickRequestedAt >= this.touchPivotRequestedAt) {
      return this.navigationPickPoint;
    }
    this.updateNavigationSpeed(position.x, position.y);
    return null;
  }

  private getTouchZoomFallbackDistance(): number {
    const cameraPosition: HsVector3 = HsVector3.makeZeroVector().setFromMatrixPosition(this.camera.worldMatrix.values);
    return Math.max(
      HsVector3.distance(cameraPosition, this.homeObservablePoint),
      HsToolsConstants.ZOOM_FALLBACK_DISTANCE
    );
  }

  private getTouchPanScale(): number {
    const canvas: HTMLCanvasElement | null = this.resolveViewerCanvasElement();
    const height: number = canvas?.getBoundingClientRect().height ?? 0;
    if (height <= 0) {
      return 1;
    }
    // Pan so the content under the fingers follows them: world units per CSS pixel at the picked distance,
    // relative to the fixed units per pixel of a middle-button drag.
    const distance: number = this.navigationPickDistance ?? this.getTouchZoomFallbackDistance();
    const worldUnitsPerPixel: number = (2 * distance * Math.tan(this.camera.fov / 2)) / height;
    return worldUnitsPerPixel / HsToolsConstants.PAN_UNITS_PER_PIXEL;
  }

  private focusCameraAtScreenPoint(clientX: number, clientY: number): void {
    const canvas: HTMLCanvasElement | null = this.resolveViewerCanvasElement();
    if (this.gl === null || this.renderObjects === null || canvas === null) {
      return;
    }
    const bounds: DOMRect = canvas.getBoundingClientRect();
    const x: number = (clientX - bounds.left) * (canvas.width / bounds.width);
    const y: number = (bounds.bottom - clientY) * (canvas.height / bounds.height);
    this.sampleRaycast(this.gl, this.renderObjects, x, y);
    if (this.raycastOutput.object === null) {
      return;
    }
    this.observablePoint.copy(this.raycastOutput.position);
    HsGlobal.ROTATE_POINT.copy(this.raycastOutput.position);
    this.selectedCameraToolType = HsCameraToolType.FocusAt;
    this.controlActionsHandler.updateControlAction(new HsCameraToolTypeControlAction(HsCameraToolType.FocusAt));
    this.requestRender();
  }

  public setDownsample(value: number): void {
    if (this.gl === null || this.canvasDocument === null || this.objectsLayer === null) {
      return;
    }
    const downsampleRate: number = Math.min(Math.max(value, this.downsampleMin), this.downsampleMax);
    this.render.downsampleRate = downsampleRate;
    this.sceneStats = {
      ...this.sceneStats,
      downsampleRate,
    };
    const rect: DOMRect = this.canvasDocument.nativeElement.getBoundingClientRect();
    this.render.setRenderSize(this.gl, rect.width, rect.height, [this.objectsLayer]);
  }

  private resolveDefaultIngestApiBaseUrl(): string {
    const runtimeConfig: HypersceneRuntimeConfig | undefined =
      // eslint-disable-next-line @typescript-eslint/naming-convention
      (globalThis as { __HYPERSCENE_RUNTIME_CONFIG__?: HypersceneRuntimeConfig }).__HYPERSCENE_RUNTIME_CONFIG__;
    const runtimeApiBaseUrl: string | undefined = runtimeConfig?.apiBaseUrl?.trim();
    if (runtimeApiBaseUrl !== undefined && runtimeApiBaseUrl.length > 0) {
      return runtimeApiBaseUrl;
    }

    const locationRef: Location | undefined = globalThis.location;
    if (locationRef === undefined) {
      return 'http://localhost:5000';
    }

    const hostName: string = locationRef.hostname.toLowerCase();
    const isLocalHost: boolean = hostName === 'localhost' || hostName === '127.0.0.1' || hostName === '::1';
    if (isLocalHost) {
      return 'http://localhost:5000';
    }

    return `${locationRef.protocol}//api.${locationRef.hostname}`;
  }

  private makeSceneTreeNode(node: HypersceneNodeSmallView): HypersceneSceneTreeNode {
    return hsMakeSceneTreeNode(node);
  }

  private filterSceneContentRoots(nodes: HypersceneNodeSmallView[]): HypersceneNodeSmallView[] {
    return nodes.filter((node: HypersceneNodeSmallView) => !isAnnotationNode(node));
  }

  private async refreshSceneTreeNodesVisibility(nodes: HypersceneSceneTreeNode[]): Promise<void> {
    await this.sceneTree.refreshVisibility(this.ingestState.sceneId, nodes);
  }

  private updateSceneTreeNodeVisibility(nodeId: string, visibility: HypersceneVisibilityState): void {
    this.sceneTree.setVisibility(nodeId, visibility);
  }

  private setLoadedSceneTreeNodesVisibility(
    nodes: HypersceneSceneTreeNode[],
    visibility: HypersceneVisibilityState
  ): void {
    this.sceneTree.setLoadedVisibility(nodes, visibility);
  }

  private setLoadedSceneTreeSubtreeVisibility(nodeId: string, visibility: HypersceneVisibilityState): void {
    this.sceneTree.setSubtreeVisibility(nodeId, visibility);
  }

  private countSceneObjects(rootNodes: HypersceneNodeSmallView[]): number {
    return rootNodes.reduce(
      (total: number, rootNode: HypersceneNodeSmallView) => total + 1 + Math.max(0, rootNode.allChildrenCount),
      0
    );
  }

  private findSceneTreeNodeById(nodeId: string, nodes: HypersceneSceneTreeNode[]): HypersceneSceneTreeNode | null {
    return this.sceneTree.find(nodeId, nodes);
  }

  private extractExternalUrls(value: string): string[] {
    if (value.trim().length === 0) {
      return [];
    }

    const urlMatches: RegExpMatchArray | null = value.match(/(?:https?:\/\/|www\.)[^\s"'<>]+/gi);
    if (urlMatches === null) {
      return [];
    }

    return urlMatches.map((rawMatch: string) => (rawMatch.startsWith('www.') ? `https://${rawMatch}` : rawMatch));
  }

  private applyVisualControlChanges(): void {
    this.lastRenderedObjectIndex = 0;
    this.forceFrustumRefresh = true;
    if (this.ingestState.phase === 'ready') {
      this.rerenderLastIngestGeometry();
      return;
    }

    this.changeDetector.markForCheck();
  }

  private applyPointCloudPointSize(): void {
    const gl: WebGL2RenderingContext | null = this.gl;
    if (gl === null) {
      return;
    }

    this.scenePointCloudObjectsByGeometryId.forEach((object: HsGraphicPoints) => {
      object.setPointSize(this.runtimeSettings.pointCloudPointSize);
    });
    this.requestRender();
  }

  private tryCaptureMeasurementPoint(clientX: number, clientY: number): void {
    this.syncMeasurementScale();
    const hit: ViewerSurfaceHit | null = this.pickMeasurementSurface(clientX, clientY, this.measurementSnapToEdges);
    const click: ViewerMeasurementClick = this.measurements.click(
      hit,
      this.screenRay(clientX, clientY),
      this.selectedSceneNodeId
    );
    if (click.kind === 'missed') {
      this.setStatusMessage(
        this.measurements.tool.snapMode === 'selected-node'
          ? 'Click on the selected node to place a point.'
          : 'Nothing under the cursor: start the measurement on the model.'
      );
      return;
    }
    const snapped: string = hit !== null && hit.kind !== 'face' ? ` (snapped to ${SNAP_NAMES[hit.kind]})` : '';
    this.setStatusMessage(
      click.kind === 'saved'
        ? `Saved ${this.measurements.formatEntry(click.entry)}${snapped}.`
        : `Point ${click.count} placed${snapped}. ${this.measurements.hint}`
    );
    this.refreshTransientOverlays();
  }

  private tryCreateMarker(clientX: number, clientY: number): void {
    const hit: ViewerSurfaceHit | null = this.pickMeasurementSurface(clientX, clientY, this.measurementSnapToEdges);
    if (hit === null) {
      this.setStatusMessage('Nothing under the cursor: place markers on the model.');
      return;
    }

    const marker: HypersceneMarker = {
      id: getUuid(),
      name: `Marker ${this.markers.length + 1}`,
      position: [hit.point[0], hit.point[1], hit.point[2]],
    };
    this.markers = [marker, ...this.markers];
    this.selectedMarkerId = marker.id;
    this.markerNameDraft = marker.name;
    this.saveMarkersToStorage();
    this.setStatusMessage(`Placed ${marker.name}.`);
    this.refreshTransientOverlays();
  }

  private resetEnterpriseMetadataState(): void {
    this.enterpriseAttributeValues = [];
    this.enterpriseFilterResults = [];
    this.enterpriseSelectedNodeId = null;
    this.enterpriseFilterLoading = false;
    this.enterpriseFilterMessage = 'Set attribute filter to search scene nodes.';
  }

  private getSelectedMarker(): HypersceneMarker | undefined {
    if (this.selectedMarkerId === null) {
      return undefined;
    }

    return this.markers.find((marker: HypersceneMarker) => marker.id === this.selectedMarkerId);
  }

  private loadLocalViewerState(): void {
    const markers: HypersceneMarker[] | null = this.annotations.readLocalMarkers();
    if (markers !== null) {
      this.markers = markers;
      this.selectedMarkerId = this.markers[0]?.id ?? null;
      this.markerNameDraft = this.getSelectedMarker()?.name ?? '';
    }

    const measurements: HypersceneMeasurementEntry[] | null = this.annotations.readLocalMeasurements();
    if (measurements !== null) {
      this.measurements.restore(measurements);
    }
  }

  private saveMarkersToStorage(): void {
    this.annotations.storeLocalMarkers(this.markers);
  }

  private applyImportedViewerPayload(payload: HypersceneViewerTransferPayload, messagePrefix: string): void {
    if (this.viewerDataImportMode === 'merge') {
      this.viewpoints = mergeById(this.viewpoints, payload.viewpoints);
      this.markers = mergeById(this.markers, payload.markers);
    } else {
      this.viewpoints = payload.viewpoints;
      this.markers = payload.markers;
    }

    this.selectedViewpointId = this.viewpoints[0]?.id ?? null;
    this.viewpointNameDraft = this.getSelectedViewpoint()?.name ?? '';
    this.viewpointCreateNameDraft = this.getNextViewpointName();
    this.viewpointRenameActive = false;
    this.selectedMarkerId = this.markers[0]?.id ?? null;
    this.markerNameDraft = this.getSelectedMarker()?.name ?? '';

    this.saveMarkersToStorage();

    this.ingestState = {
      ...this.ingestState,
      message: `${messagePrefix} (${this.viewpoints.length} viewpoints, ${this.markers.length} markers, mode: ${this.viewerDataImportMode}).`,
      error: null,
    };

    this.refreshTransientOverlays();
  }

  private resetHlodTilesets(): void {
    if (this.hlodLookupTimer !== null) {
      window.clearTimeout(this.hlodLookupTimer);
      this.hlodLookupTimer = null;
    }
    this.photogrammetry.reset();
    this.removeHlodDebugBoxes();
    this.hlodDebug = '';
  }

  private scheduleHlodLookup(delayMs: number = 250): void {
    if (this.hlodLookupTimer !== null) {
      return;
    }
    this.hlodLookupTimer = window.setTimeout(() => {
      this.hlodLookupTimer = null;
      void this.lookupHlodTilesets();
    }, delayMs);
  }

  private async lookupHlodTilesets(): Promise<void> {
    if (
      this.gl === null ||
      this.mouseButton !== null ||
      this.sceneMeshObjectsByGeometryId.size === 0 ||
      this.photogrammetry.coolingDown
    ) {
      return;
    }
    // The API accepts mesh ids only; anything else would make it reject the whole batch.
    await this.photogrammetry.lookup(
      this.selectedSceneId,
      this.collectDrawnMeshIds().filter((id: string) => MESH_ID_PATTERN.test(id))
    );
  }

  private collectDrawnMeshIds(): string[] {
    const meshIds: Set<string> = new Set<string>();
    this.sceneMeshObjectsByGeometryId.forEach((object: HsGraphicObject, renderKey: string) => {
      if (object.isDeleted || !object.visible) {
        return;
      }
      // Objects split by material are keyed `<render key>|material-part:<n>`; their mesh is the parent's.
      const parentRenderKey: string = this.sceneMeshPartInfoByRenderKey.get(renderKey)?.parentRenderKey ?? renderKey;
      meshIds.add(this.normalizeGeometryId(this.sceneMeshGeometryIdByRenderKey[parentRenderKey] ?? parentRenderKey));
    });
    return Array.from(meshIds);
  }

  private async downloadDocumentBuffers(documentIds: string[]): Promise<Map<string, ArrayBuffer>> {
    const download = async (ids: string[]): Promise<Map<string, ArrayBuffer>> => {
      const blobs: Map<string, Blob> = await this.ingestAdapter.getTextures(this.ingestApiBaseUrl, ids);
      const buffers: Map<string, ArrayBuffer> = new Map<string, ArrayBuffer>();
      for (const [id, blob] of blobs) {
        buffers.set(id, await blob.arrayBuffer());
      }
      return buffers;
    };
    return this.probeHandle === null ? download(documentIds) : this.probeHandle.documents(documentIds, download);
  }

  /** Selects the HLOD nodes for this frame; runs before frustum culling so the new set is drawn now. */
  private updateHlodTilesets(): void {
    // A frozen frustum keeps the drawn nodes, so the selection can be inspected from elsewhere.
    if (this.photogrammetry.empty || this.gl === null || this.freezeFrustum) {
      return;
    }
    // One budget and one error threshold for all photogrammetry models in the scene.
    this.photogrammetry.gpuBudgetBytes = this.runtimeSettings.photogrammetryCacheMiB * 1048576;
    this.photogrammetry.maxScreenErrorPixels = this.runtimeSettings.hlodMaxScreenErrorPixels;
    const { changed, needsFrame }: HsPhotogrammetryUpdate = this.photogrammetry.update(this.camera);
    // The load status reaches the overlay through the source's `status` event.
    if (changed) {
      this.forceFrustumRefresh = true;
      this.lastFrustumCullingAt = Number.NEGATIVE_INFINITY;
      this.lastRenderedObjectIndex = 0;
      this.viewerRenderTotalsPending = true;
      this.scheduleSceneGeometryDebugUpdate();
    }
    if (changed || this.showObjectBoundingBoxes !== this.hlodDebugBoxesShown) {
      this.updateHlodDebugBoxes();
    }
    if (needsFrame || changed) {
      this.requestRender();
    }
    if (performance.now() - this.hlodDebugUpdatedAt > 250) {
      this.hlodDebugUpdatedAt = performance.now();
      this.hlodDebug = this.formatHlodDebug();
    }
  }

  private updateHlodDebugBoxes(): void {
    this.removeHlodDebugBoxes();
    if (!this.showObjectBoundingBoxes || this.overlays === null) {
      return;
    }
    const vertices: number[] = [];
    this.photogrammetry
      .drawnNodes()
      .forEach((object: HsGraphicObject) => appendLineCubeVertices(vertices, object.boundingBox));
    this.hlodDebugBoxesShown =
      this.overlays.addLines(HLOD_DEBUG_OVERLAYS, 'debug-hlod-node-boxes', vertices, [1, 0.1, 0.8]) !== null;
    this.viewerRenderOrder.invalidate();
    this.viewerSpatialCulling.refresh();
  }

  private removeHlodDebugBoxes(): void {
    this.overlays?.clear(HLOD_DEBUG_OVERLAYS);
    this.hlodDebugBoxesShown = false;
  }

  private findHlodRootObjects(meshId: string): HsGraphicObject[] {
    const objects: HsGraphicObject[] = [];
    this.sceneMeshObjectsByGeometryId.forEach((object: HsGraphicObject, renderKey: string) => {
      if (
        !object.isDeleted &&
        this.normalizeGeometryId(this.sceneMeshGeometryIdByRenderKey[renderKey] ?? renderKey) === meshId
      ) {
        objects.push(object);
      }
    });
    return objects;
  }

  private getHlodTextureTotals(): { bytes: number; count: number } {
    return this.photogrammetry.textureTotals();
  }

  private formatHlodDebug(): string {
    const totals: HsHlodTilesetStats[] = this.photogrammetry.stats();
    if (totals.length === 0) {
      return '';
    }
    const sum = (field: keyof HsHlodTilesetStats): number =>
      totals.reduce((total: number, stats: HsHlodTilesetStats) => total + stats[field], 0);
    return (
      `HLOD: ${sum('rendered')} nodes drawn (${sum('renderedFaces').toLocaleString()} faces, ` +
      `${sum('occluded')} occluded), ${sum('resident')} resident (${sum('hidden')} hidden, ` +
      `${(sum('residentBytes') / 1048576).toFixed(1)} MiB GPU of ${this.runtimeSettings.photogrammetryCacheMiB}, ` +
      `error x${Math.max(...totals.map((stats: HsHlodTilesetStats) => stats.errorScale)).toFixed(1)}), ` +
      `${sum('loading')} loading, ${sum('queued')} queued, ${sum('failed')} failed, ` +
      `${(sum('downloadedBytes') / 1048576).toFixed(1)} MiB downloaded, ${sum('nodes')} nodes known, ` +
      `${sum('requests')} requested (${sum('refetches')} refetched), ${sum('evictions')} evicted` +
      `${
        this.photogrammetry.textureFormats.size > 0
          ? `, textures ${[...this.photogrammetry.textureFormats].join(' + ')}`
          : ''
      }${this.formatHlodCacheDebug()}`
    );
  }

  private formatHlodCacheDebug(): string {
    const cache: HsHlodPayloadCacheStats | null = this.photogrammetry.cacheStats();
    if (cache === null) {
      return '';
    }
    const state: string = !this.hlodPayloadCache.available
      ? 'off (store failed)'
      : this.hlodPayloadCache.enabled
      ? 'on'
      : 'off';
    return (
      `; disk cache ${state}: ${cache.hits} hits / ${cache.misses} misses, ` +
      `${(cache.hitBytes / 1048576).toFixed(1)} MiB served, ${(cache.storedBytes / 1048576).toFixed(1)} MiB of ` +
      `${(this.hlodPayloadCache.maxBytes / 1048576).toFixed(0)} stored in ${cache.entries} documents, ` +
      `${cache.evictions} evicted, ${cache.revalidations} revalidated (${cache.replaced} replaced), ${cache.errors} errors`
    );
  }

  private normalizeGeometryId(value: string): string {
    return hsNormalizeGeometryId(value);
  }

  /** Decoded scene meshes; the budget below applies to these only. */
  private getDecodedMeshCacheBytes(): number {
    return this.geometryStore.meshBytes();
  }

  private getSceneCacheBudgetDebug(): HypersceneSceneCacheBudgetDebugState {
    let textureBytes: number = 0;
    for (const size of this.sceneTextureBytesById.values()) {
      textureBytes += size;
    }
    // HLOD node textures live on the GPU outside sceneTextures; they are memory the caches hold for the
    // current scene.
    const hlod: { bytes: number; count: number } = this.getHlodTextureTotals();
    return {
      meshBytes: this.getDecodedMeshCacheBytes(),
      meshBudgetBytes: this.meshCacheBudgetBytes,
      meshCount: this.geometryStore.meshCount(),
      textureBytes: textureBytes + hlod.bytes,
      textureBudgetBytes: this.sceneSource.textureBudgetBytes,
      textureCount: this.sceneTextures.size + hlod.count,
    };
  }

  private getSceneMeshMaterialError(): string {
    return this.sceneSource.materialErrorSummary();
  }

  private countMeshTriangles(mesh: HsDecodedMesh): number {
    return hsCountMeshTriangles(mesh);
  }

  private invalidateSceneGeometryTotals(): void {
    this.sceneObjects.invalidateTotals();
  }

  private updateSceneRenderTotals(): void {
    this.sceneRenderTotals = this.sceneObjects.renderTotals(this.renderObjects, this.render);
  }

  private clearOriginalMeshSources(): void {
    this.sceneSource.clearCompositeSources();
  }

  private scheduleSceneGeometryDebugUpdate(): void {
    if (this.sceneGeometryDebugUpdateTimer !== null) {
      return;
    }

    this.sceneGeometryDebugUpdateTimer = window.setTimeout(() => {
      this.sceneGeometryDebugUpdateTimer = null;
      this.updateSceneGeometryDebug();
      this.changeDetector.markForCheck();
    }, 120);
  }

  private updateSceneGeometryDebug(): void {
    this.sceneCacheBudgetDebug = this.getSceneCacheBudgetDebug();
    let originalMeshesCount: number = 0;
    let originalTrianglesCount: number = 0;
    for (const [id, mesh] of this.decodedOriginalMeshesById) {
      if (!this.decodedMeshesById.has(id)) {
        originalMeshesCount++;
        originalTrianglesCount += this.countMeshTriangles(mesh);
      }
    }
    this.sceneGeometryDebug = {
      loadedMeshGeometriesCount: this.decodedMeshesById.size + originalMeshesCount,
      loadedLineGeometriesCount: this.decodedLinesById.size,
      loadedPointCloudGeometriesCount: this.decodedPointCloudsById.size,
      loadedTrianglesCount: this.loadedTrianglesCount + originalTrianglesCount,
      loadedLineSegmentsCount: this.loadedLineSegmentsCount,
      loadedPointsCount: this.loadedPointsCount,
    };
    this.updateSceneRenderTotals();

    this.sceneOverviewDebug = {
      frustumMeshObjectsCount: this.sceneMeshRenderKeys.length,
      frustumLineObjectsCount: this.sceneLineRenderKeys.length,
      frustumPointCloudObjectsCount: this.scenePointCloudRenderKeys.length,
    };
  }

  /** Drops the viewer caches that hold scene objects: draw batches, culling and the draw order. */
  private resetViewerSceneCaches(): void {
    this.render.clearMeshBatches();
    this.viewerSpatialCulling.invalidate();
    this.viewerNavigationCulling.invalidate();
    this.invalidateSceneGeometryTotals();
    this.viewerRenderOrder.clear();
  }

  private clearTrackedSceneBaseObjects(objectsQueue: HsGraphicQueue, graphics: HsGraphics): void {
    this.resetViewerSceneCaches();
    this.sceneSource.clearObjects(objectsQueue, graphics);
  }

  /** The scene look from the page's opacity, transparency, clipping, collision and quality settings. */
  private makeSceneLook(): HsSceneLook {
    const look: HsSceneLook = hsSceneLook({
      opacity: this.xrayEnabled ? XRAY_OPACITY : this.sceneOpacity,
      disableTransparentMaterials: this.disableTransparentMaterials && !this.xrayEnabled,
      clippingPlanes: this.section.planes(),
      clippingStorage: this.sectionClipping,
      highlightCollisions: this.ingestHighlightCollisions,
      instancing: this.enableInstancing,
      pointSize: this.runtimeSettings.pointCloudPointSize,
      quality: this.visualQualitySettings,
      onShaderBuilt: () => this.visualQualityShaderBuildCount++,
    });
    this.sectionClipping = look.clippingStorage;
    // Picks skip what the section cut away: selection, measurements and the orbit pivot alike.
    if (this.viewer !== null) {
      this.viewer.pickClipping = look.clippingStorage;
    }
    this.raycast.clippingStorage = look.clippingStorage;
    return look;
  }

  /**
   * Returns an already compiled shader with the same generated program when one exists. Material colors are
   * per-object uniforms, so per-color materials can share one program instead of switching between copies.
   */
  private clearSceneMaterialCache(): void {
    this.render.clearMeshBatches();
    this.sceneMaterials.clear(this.gl);
  }

  private clearSceneTextures(): void {
    this.sceneSource.clearTextures();
  }

  private bumpSceneEpochAndCancelDecode(): void {
    this.sceneEpoch++;
    this.resetHlodTilesets();
    this.pointCloudOcclusion.clear();
    this.pointCloudOcclusionChecked = [];
    this.pointCloudOcclusionStale = true;
    this.viewerSpatialCulling.invalidate();
    this.viewerNavigationCulling.invalidate();
    this.viewerRenderOrder.clear();
    this.frustumSceneSessionId = null;
    this.frustumRefreshInProgress = false;
    this.frustumRefreshPending = false;
    if (this.frustumRefreshTimer !== null) {
      window.clearTimeout(this.frustumRefreshTimer);
      this.frustumRefreshTimer = null;
    }
    if (this.pointCloudDetailTimer !== null) {
      window.clearTimeout(this.pointCloudDetailTimer);
      this.pointCloudDetailTimer = null;
    }
    this.pointCloudNavigationActive = false;
    this.sceneSource.abort();
    this.decodePool.cancelPending();
  }

  private isDecodeCanceledError(error: unknown): boolean {
    if (error instanceof Error && error.message === DECODE_CANCELED_ERROR_MESSAGE) {
      return true;
    }

    return error instanceof DOMException && error.name === 'AbortError';
  }

  private async loadSceneViewpoints(sceneId: string): Promise<void> {
    const sceneEpoch: number = this.sceneEpoch;
    const viewpoints: HypersceneViewpoint[] = await this.annotations.loadViewpoints(sceneId);
    if (sceneEpoch !== this.sceneEpoch || this.ingestState.sceneId !== sceneId) {
      throw new Error(DECODE_CANCELED_ERROR_MESSAGE);
    }
    this.viewpoints = viewpoints;
    const requestedViewpoint: HypersceneViewpoint | undefined =
      this.pendingViewpointId === null
        ? undefined
        : viewpoints.find((viewpoint: HypersceneViewpoint) => viewpoint.id === this.pendingViewpointId);
    this.selectedViewpointId = requestedViewpoint?.id ?? null;
    this.viewpointNameDraft = requestedViewpoint?.name ?? '';
    this.viewpointCreateNameDraft = this.getNextViewpointName();
    this.viewpointRenameActive = false;
  }

  private getSelectedViewpoint(): HypersceneViewpoint | undefined {
    if (this.selectedViewpointId === null) {
      return undefined;
    }

    return this.viewpoints.find((item: HypersceneViewpoint) => item.id === this.selectedViewpointId);
  }

  private captureCurrentCameraMatrix(): HsHypersceneTypes.Array16 {
    return [...this.camera.worldMatrix.values] as HsHypersceneTypes.Array16;
  }

  private getNextViewpointName(): string {
    return `Viewpoint ${this.viewpoints.length + 1}`;
  }

  private tryExtractMetadataString(metadata: unknown[] | undefined, key: string): string | null {
    if (!Array.isArray(metadata)) {
      return null;
    }

    for (const entry of metadata) {
      if (typeof entry !== 'object' || entry === null) {
        continue;
      }

      const entryRecord: { value?: unknown } = entry as { value?: unknown };
      const payload: unknown = entryRecord.value ?? entry;
      if (typeof payload !== 'object' || payload === null) {
        continue;
      }

      const value: unknown = (payload as Record<string, unknown>)[key];
      if (typeof value !== 'string') {
        continue;
      }

      const normalizedValue: string = value.trim();
      if (normalizedValue.length > 0) {
        return normalizedValue;
      }
    }

    return null;
  }

  private tryExtractModelCreatedAt(metadata: unknown[] | undefined): string | null {
    for (const key of ['createdAt', 'uploadedAt', 'uploadDate']) {
      const value: string | null = this.tryExtractMetadataString(metadata, key);
      if (value !== null) {
        return value;
      }
    }

    return null;
  }

  private formatModelDate(value: string | null): string {
    if (value === null) {
      return '-';
    }

    const timestamp: number = Date.parse(value);
    if (Number.isNaN(timestamp)) {
      return value;
    }

    return `${new Date(timestamp).toISOString().slice(0, 16).replace('T', ' ')} UTC`;
  }

  private tryExtractModelFileName(metadata: unknown[] | undefined): string | null {
    return this.tryExtractMetadataString(metadata, 'fileName');
  }

  private tryExtractModelDisplayName(metadata: unknown[] | undefined): string | null {
    return this.tryExtractMetadataString(metadata, 'modelName') ?? this.tryExtractModelFileName(metadata);
  }

  private syncSelectedModelRenameDraft(): void {
    if (this.selectedModelId === null) {
      this.selectedModelRenameDraft = '';
      return;
    }

    const model: HypersceneModelView | undefined = this.modelsRegistry.find(
      (modelView: HypersceneModelView) => modelView.id === this.selectedModelId
    );
    if (model === undefined) {
      this.selectedModelRenameDraft = '';
      return;
    }

    this.selectedModelRenameDraft = this.tryExtractModelDisplayName(model.metadata) ?? '';
  }

  private syncSelectedSceneRenameDraft(): void {
    if (this.selectedSceneId === null) {
      this.selectedSceneRenameDraft = '';
      return;
    }

    const scene: HypersceneSceneView | undefined = this.scenesRegistry.find(
      (sceneView: HypersceneSceneView) => sceneView.id === this.selectedSceneId
    );
    if (scene === undefined) {
      this.selectedSceneRenameDraft = '';
      return;
    }

    this.selectedSceneRenameDraft = this.tryExtractMetadataString(scene.metadata, 'sceneName') ?? '';
  }

  private async loadSelectedNodeBoundingBox(nodeId: string): Promise<void> {
    if (this.selectedNodeBoundingBoxNodeId === nodeId) {
      return;
    }

    const response: { value: HypersceneBoundingBoxView | null } = await this.ingestAdapter.getNodesBoundingBox(
      this.ingestApiBaseUrl,
      [nodeId]
    );
    const bounds: HypersceneBoundingBoxView | null = response.value;
    if (bounds !== null) {
      if (
        bounds.min.length !== 3 ||
        bounds.max.length !== 3 ||
        !bounds.min.every(Number.isFinite) ||
        !bounds.max.every(Number.isFinite) ||
        bounds.min.some((value: number, index: number) => value > bounds.max[index])
      ) {
        throw new Error('Invalid selected node bounding box.');
      }
    }

    this.selectedNodeBoundingBox = bounds;
    this.selectedNodeBoundingBoxNodeId = nodeId;
  }

  /**
   * Applies a visibility action on screen at once and persists it in the background: the viewer hides or
   * shows the node and its loaded descendants itself, then the server request and a visibility refresh
   * (which covers descendants the tree has not loaded) follow. A failed request rolls the change back.
   */
  private async executeSelectedNodeVisibilityAction(action: 'hide' | 'unhide' | 'isolate'): Promise<void> {
    const sceneId: string | null = this.ingestState.sceneId;
    const nodeId: string | null = this.selectedSceneNodeId;
    if (sceneId === null) {
      this.setIngestError('Scene is not initialized yet. Run ingest first.');
      return;
    }
    if (nodeId === null) {
      this.setIngestError('Select a scene node first.');
      return;
    }

    const subtree: { nodeIds: string[]; complete: boolean } = this.loadedSubtreeNodeIds(nodeId);
    const before: HsNodeVisibilityState = this.sceneSource.nodeVisibilityState();
    if (action === 'hide') {
      this.sceneSource.hideNodes(subtree.nodeIds);
    } else if (action === 'unhide' || !subtree.complete) {
      // Isolating needs the whole subtree; without it the server's refresh hides the rest.
      this.sceneSource.showNodes(subtree.nodeIds);
    } else {
      this.sceneSource.isolateNodes(subtree.nodeIds);
    }
    const applied: number = this.sceneSource.nodeVisibilityRevision;
    const subtreeVisibility: HypersceneVisibilityState = action === 'hide' ? 'hidden' : 'visible';
    this.setLoadedSceneTreeSubtreeVisibility(nodeId, subtreeVisibility);
    this.selectedSceneNodeVisibility = subtreeVisibility;
    const actionLabel: string = action === 'hide' ? 'Hidden' : action === 'unhide' ? 'Unhidden' : 'Isolated';
    this.ingestState = {
      ...this.ingestState,
      message: `${actionLabel} node \"${this.selectedSceneNodeTitle ?? nodeId}\".`,
      error: null,
    };
    this.requestRender();
    this.changeDetector.markForCheck();

    try {
      // A hide or isolation of a fully loaded subtree is complete on screen, and a refresh would drop the hidden
      // objects that a later unhide or show all brings back at once. Showing may need geometry never downloaded.
      const refresh: boolean = action === 'unhide' || !subtree.complete;
      await this.persistSceneVisibility(
        sceneId,
        async () => {
          if (action === 'hide') {
            await this.ingestAdapter.hideSceneNodes(this.ingestApiBaseUrl, sceneId, [nodeId], this.visibilitySessionId);
          } else if (action === 'unhide') {
            await this.ingestAdapter.unhideSceneNodes(
              this.ingestApiBaseUrl,
              sceneId,
              [nodeId],
              this.visibilitySessionId
            );
          } else {
            await this.ingestAdapter.isolateSceneNodes(
              this.ingestApiBaseUrl,
              sceneId,
              [nodeId],
              this.visibilitySessionId
            );
          }
        },
        refresh
      );
      if (action === 'isolate' && !subtree.complete && this.sceneSource.nodeVisibilityRevision === applied) {
        // Nodes the viewer hid earlier may lie in the unloaded part of the subtree the server just showed.
        await this.showNodesTheServerShows(sceneId);
      }
    } catch (error: unknown) {
      this.rollBackSceneVisibility(before, applied);
      const errorMessage: string = error instanceof Error ? error.message : 'Failed to update node visibility.';
      this.setIngestError(errorMessage);
    } finally {
      this.changeDetector.markForCheck();
    }
  }

  /**
   * Runs a visibility request after the ones before it, so the server applies them in click order, then
   * reads the selected node's state and, with `refresh`, the drawn geometry from the server.
   */
  private persistSceneVisibility(sceneId: string, request: () => Promise<void>, refresh: boolean): Promise<void> {
    const persisted: Promise<void> = this.sceneVisibilityRequests.then(async () => {
      await request();
      await Promise.all([
        refresh ? this.syncRenderedGeometryWithSceneVisibility(sceneId) : Promise.resolve(),
        this.refreshSelectedNodeVisibility(),
      ]);
    });
    this.sceneVisibilityRequests = persisted.catch(() => undefined);
    return persisted;
  }

  /** Undoes a visibility change the server rejected, unless a later change replaced it on screen. */
  private rollBackSceneVisibility(before: HsNodeVisibilityState, applied: number): void {
    if (this.sceneSource.nodeVisibilityRevision === applied) {
      this.sceneSource.restoreNodeVisibility(before);
      this.requestRender();
    }
    void this.refreshSceneTreeNodesVisibility(this.sceneTreeRoots)
      .then(() => this.refreshSelectedNodeVisibility())
      .catch(() => undefined)
      .finally(() => this.changeDetector.markForCheck());
  }

  private async showNodesTheServerShows(sceneId: string): Promise<void> {
    const hidden: readonly string[] = this.sceneSource.nodeVisibilityState().hidden;
    if (hidden.length === 0) {
      return;
    }
    const state: HypersceneNodesVisibilityView = await this.ingestAdapter.getSceneNodesVisibility(
      this.ingestApiBaseUrl,
      sceneId,
      [...hidden],
      this.visibilitySessionId
    );
    if (state.enabled.length > 0) {
      this.sceneSource.showNodes(state.enabled);
      this.requestRender();
    }
  }

  /** The node and the descendants the scene tree has loaded, and whether they are the whole subtree. */
  private loadedSubtreeNodeIds(nodeId: string): { nodeIds: string[]; complete: boolean } {
    const node: HypersceneSceneTreeNode | null = this.sceneTree.find(nodeId);
    const nodeIds: string[] = [nodeId];
    const collect = (children: readonly HypersceneSceneTreeNode[]): void =>
      children.forEach((child: HypersceneSceneTreeNode) => {
        nodeIds.push(child.id);
        collect(child.children);
      });
    collect(node?.children ?? []);
    const descendants: number | null = node?.allChildrenCount ?? this.selectedSceneNodeDescendantCount;
    return { nodeIds, complete: descendants !== null && nodeIds.length - 1 >= descendants };
  }

  private async refreshSelectedNodeVisibility(): Promise<void> {
    if (this.ingestState.sceneId === null || this.selectedSceneNodeId === null) {
      this.selectedSceneNodeVisibility = 'unknown';
      return;
    }

    const state: HypersceneNodesVisibilityView = await this.ingestAdapter.getSceneNodesVisibility(
      this.ingestApiBaseUrl,
      this.ingestState.sceneId,
      [this.selectedSceneNodeId],
      this.visibilitySessionId
    );

    const enabledCount: number = state.enabled.length;
    const disabledCount: number = state.disabled.length;
    if (enabledCount > 0 && disabledCount === 0) {
      this.selectedSceneNodeVisibility = 'visible';
      this.updateSceneTreeNodeVisibility(this.selectedSceneNodeId, 'visible');
      return;
    }
    if (disabledCount > 0 && enabledCount === 0) {
      this.selectedSceneNodeVisibility = 'hidden';
      this.updateSceneTreeNodeVisibility(this.selectedSceneNodeId, 'hidden');
      return;
    }
    if (enabledCount > 0 && disabledCount > 0) {
      this.selectedSceneNodeVisibility = 'mixed';
      this.updateSceneTreeNodeVisibility(this.selectedSceneNodeId, 'mixed');
      return;
    }
    this.selectedSceneNodeVisibility = 'unknown';
    this.updateSceneTreeNodeVisibility(this.selectedSceneNodeId, 'unknown');
  }

  private async frameViewerSceneBounds(sceneId: string): Promise<boolean> {
    const sceneEpoch: number = this.sceneEpoch;
    const bounds: { value: { min: number[]; max: number[] } | null } = await this.ingestAdapter.getSceneBoundingBox(
      this.ingestApiBaseUrl,
      sceneId
    );
    if (sceneEpoch !== this.sceneEpoch || this.selectedSceneId !== sceneId) {
      throw new Error(DECODE_CANCELED_ERROR_MESSAGE);
    }
    if (bounds.value === null) {
      return false;
    }
    if (!this.isValidBoundingBox(bounds.value)) {
      throw new Error('Invalid scene bounding box.');
    }
    this.sceneBounds = bounds.value;
    this.frameCameraToBounds(bounds.value, true);
    return true;
  }

  private getCameraViewUrlKey(cameraView: ViewerCameraView | null): string | null {
    return cameraView === null ? null : `${cameraView.position.join(',')}|${cameraView.direction.join(',')}`;
  }

  private applyPendingViewUrlParameters(sceneId: string): void {
    const cameraView: ViewerCameraView | null = this.pendingCameraView;
    if (cameraView !== null) {
      this.applyCameraView(cameraView);
      this.pendingCameraView = null;
    }
    const requestedViewpointId: string | null = this.pendingViewpointId;
    const viewpointId: string | null =
      requestedViewpointId !== null && this.restoreViewpointById(requestedViewpointId) ? requestedViewpointId : null;
    if (cameraView !== null || viewpointId !== null) {
      this.clearAppliedViewUrlParameters(sceneId, cameraView, viewpointId);
    }
  }

  private clearAppliedViewUrlParameters(
    sceneId: string | null,
    cameraView: ViewerCameraView | null,
    viewpointId: string | null
  ): void {
    const params: ParamMap = this.route.snapshot.queryParamMap;
    if (sceneId === null || params.get('sceneId')?.trim() !== sceneId) {
      return;
    }
    const configuration: ReturnType<typeof readViewerUrlSettings> = readViewerUrlSettings(params);
    if (configuration.error !== null) {
      return;
    }
    const queryParams: Record<string, null> = {};
    if (
      cameraView !== null &&
      this.getCameraViewUrlKey(configuration.cameraView) === this.getCameraViewUrlKey(cameraView)
    ) {
      queryParams.camera = null;
    }
    if (viewpointId !== null && configuration.viewpointId === viewpointId) {
      queryParams.viewpointId = null;
    }
    if (Object.keys(queryParams).length === 0) {
      return;
    }
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private restoreViewpointById(viewpointId: string): boolean {
    const viewpoint: HypersceneViewpoint | undefined = this.viewpoints.find(
      (item: HypersceneViewpoint) => item.id === viewpointId
    );
    if (viewpoint === undefined) {
      this.setIngestError(`Viewpoint ${viewpointId} was not found in the current scene.`);
      return false;
    }

    this.selectedViewpointId = viewpoint.id;
    this.viewpointNameDraft = viewpoint.name;
    this.applyViewpoint(viewpoint);
    return true;
  }

  private applyViewpoint(viewpoint: HypersceneViewpoint): void {
    this.cancelCameraNavigationForViewpoint();
    this.camera.worldMatrix.setFromArray(viewpoint.cameraMatrix);
    this.camera.updateProjectionMatrix();
    this.camera.updateCameraMatrixInverse();

    const position: HsHypersceneTypes.Array3 = this.camera.worldMatrix.getPositionArray();
    this.cameraPoint.set(position[0], position[1], position[2]);
    const values: HsHypersceneTypes.Array16 = this.camera.worldMatrix.values;
    this.observablePoint.set(
      position[0] - values[8] * ROTATION_POINT_DISTANCE,
      position[1] - values[9] * ROTATION_POINT_DISTANCE,
      position[2] - values[10] * ROTATION_POINT_DISTANCE
    );
    HsGlobal.ROTATE_POINT.copy(this.observablePoint);
    this.viewer?.syncCamera();
    this.lastRenderedObjectIndex = 0;
    this.frustumUpdateTime = 1;
    this.forceFrustumRefresh = true;
    this.requestRender();
    if (this.ingestState.sceneId !== null) {
      this.scheduleFrustumRefresh(this.ingestState.sceneId, 0, 'viewpoint-selection');
    }
  }

  private cancelCameraNavigationForViewpoint(): void {
    this.navigationButtonOverride = null;
    this.viewerInput?.cancel();
    this.pointCloudNavigationActive = false;
    this.controlActionsHandler.resetControlActions();

    if (this.cameraToolsHandler.activeCameraToolType !== HsCameraToolType.Default) {
      this.controlActionsHandler.updateControlAction(new HsCameraToolTypeControlAction(HsCameraToolType.Default));
      this.selectedCameraToolType = HsCameraToolType.Default;
    }
  }

  private applyCameraView(cameraView: ViewerCameraView): void {
    const cameraPosition: HsVector3 = new HsVector3(...cameraView.position);
    const direction: HsVector3 = new HsVector3(...cameraView.direction).normalize();
    const observablePoint: HsVector3 = new HsVector3(
      cameraPosition.x + direction.x * ROTATION_POINT_DISTANCE,
      cameraPosition.y + direction.y * ROTATION_POINT_DISTANCE,
      cameraPosition.z + direction.z * ROTATION_POINT_DISTANCE
    );
    this.cameraPoint.copy(cameraPosition);
    this.observablePoint.copy(observablePoint);
    this.camera.worldMatrix.setPosition(cameraPosition.values);
    HsHypersceneMathUtils.lookAt(
      this.camera.worldMatrix,
      cameraPosition,
      observablePoint,
      HsHypersceneEngineConstants.UP
    );
    this.camera.updateProjectionMatrix();
    this.camera.updateCameraMatrixInverse();
    this.viewer?.syncCamera();
    HsGlobal.ROTATE_POINT.copy(observablePoint);
    this.lastRenderedObjectIndex = 0;
    this.forceFrustumRefresh = true;
    this.requestRender();
  }

  private isValidBoundingBox(bounds: HypersceneBoundingBoxView | null): bounds is HypersceneBoundingBoxView {
    return (
      bounds !== null &&
      bounds.min.length === 3 &&
      bounds.max.length === 3 &&
      bounds.min.every(Number.isFinite) &&
      bounds.max.every(Number.isFinite) &&
      !bounds.min.some((value: number, index: number) => value > bounds.max[index])
    );
  }

  /** The open scene's extent, asked from the API on first use; null without a scene or geometry. */
  private async ensureSceneBounds(): Promise<HypersceneBoundingBoxView | null> {
    const sceneId: string | null = this.ingestState.sceneId;
    if (this.sceneBounds === null && sceneId !== null) {
      const bounds: { value: { min: number[]; max: number[] } | null } = await this.ingestAdapter.getSceneBoundingBox(
        this.ingestApiBaseUrl,
        sceneId
      );
      if (sceneId === this.ingestState.sceneId && this.isValidBoundingBox(bounds.value)) {
        this.sceneBounds = bounds.value;
      }
    }
    return this.sceneBounds;
  }

  /** Moves the camera back along its view direction until the box fills the view; the box center is the pivot. */
  private fitCameraToBounds(bounds: HypersceneBoundingBoxView): void {
    const { center, radius } = boundsSphere(bounds);
    this.applyCameraMatrix(
      hsFitCameraMatrix(this.camera.worldMatrix.values, center, radius, this.camera.fov, this.canvasAspect())
    );
    this.setPivot(center);
  }

  /** Puts the camera at a world matrix, as standard views and zooming to fit do, and refreshes the scene. */
  private applyCameraMatrix(matrix: HsHypersceneTypes.Array16): void {
    this.cancelCameraNavigationForViewpoint();
    this.camera.worldMatrix.setFromArray(matrix);
    this.camera.updateProjectionMatrix();
    this.camera.updateCameraMatrixInverse();
    this.cameraPoint.set(matrix[12], matrix[13], matrix[14]);
    this.viewer?.syncCamera();
    this.lastRenderedObjectIndex = 0;
    this.frustumUpdateTime = 1;
    this.forceFrustumRefresh = true;
    this.requestRender();
    if (this.ingestState.sceneId !== null) {
      this.scheduleFrustumRefresh(this.ingestState.sceneId, 0, 'camera-view');
    }
  }

  /** The point the camera orbits around and focuses on. */
  private setPivot(point: ArrayLike<number>): void {
    this.orbitPivot.set(point[0], point[1], point[2]);
    this.observablePoint.copy(this.orbitPivot);
    HsGlobal.ROTATE_POINT.copy(this.orbitPivot);
    const move: HsCameraDefaultMove | undefined = this.cameraToolsHandler.defaultMove;
    if (move !== undefined) {
      move.orbitPivot = this.orbitPivot;
    }
  }

  /** Takes the open scene's calibration into the measurement tool. */
  private syncMeasurementScale(): void {
    const sceneId: string | null = this.ingestState.sceneId;
    this.measurements.tool.scale = sceneId === null ? 1 : this.annotations.readMeasurementScale(sceneId);
  }

  /**
   * Moves the section plane while the left button is held: the drag along the plane normal's direction on the
   * screen becomes meters along the normal. The drawn clipping follows at once; the scene look is rebuilt on release.
   */
  private startSectionDrag(event: MouseEvent): void {
    this.viewerInput?.cancel();
    if (this.section.mode === 'box') {
      this.startBoxFaceDrag(event);
      return;
    }
    const anchor: HsMeasurementPoint = this.pickMeasurementSurface(event.clientX, event.clientY)?.point ?? [
      this.orbitPivot.x,
      this.orbitPivot.y,
      this.orbitPivot.z,
    ];
    this.dragAlong(event, anchor, this.section.direction, (meters: number) => this.section.moveBy(meters));
  }

  /**
   * Resizes the section box: the face under the cursor (or, off the box, the face whose center shows nearest to
   * it) follows the drag along its axis.
   */
  private startBoxFaceDrag(event: MouseEvent): void {
    const ray: HsScreenRay | null = this.screenRay(event.clientX, event.clientY);
    const rect: DOMRect | undefined = this.resolveViewerCanvasElement()?.getBoundingClientRect();
    let face: { axis: HsSectionAxis; side: 'min' | 'max' } | null =
      ray === null ? null : this.section.pickBoxFace(ray.origin, ray.direction);
    if (face === null && rect !== undefined) {
      let nearest: number = Infinity;
      for (const axis of ['x', 'y', 'z'] as const) {
        for (const side of ['min', 'max'] as const) {
          const center: [number, number, number] | null = this.section.boxFaceCenter(axis, side);
          const screen: { x: number; y: number } | null =
            center === null ? null : hsProjectToScreen(this.camera, rect, center);
          const distance: number =
            screen === null ? Infinity : Math.hypot(screen.x - event.clientX, screen.y - event.clientY);
          if (distance < nearest) {
            nearest = distance;
            face = { axis, side };
          }
        }
      }
    }
    const anchor: [number, number, number] | null =
      face === null ? null : this.section.boxFaceCenter(face.axis, face.side);
    if (face === null || anchor === null) {
      return;
    }
    const grabbed: { axis: HsSectionAxis; side: 'min' | 'max' } = face;
    const index: number = grabbed.axis === 'x' ? 0 : grabbed.axis === 'y' ? 1 : 2;
    const direction: [number, number, number] = [0, 0, 0];
    direction[index] = 1;
    this.setStatusMessage(
      `Resizing the box: ${grabbed.side === 'min' ? 'low' : 'high'} ${grabbed.axis.toUpperCase()} face.`
    );
    this.dragAlong(event, anchor, direction, (meters: number) => {
      const box: { min: readonly number[]; max: readonly number[] } | null = this.section.box;
      if (box !== null) {
        const current: number = grabbed.side === 'min' ? box.min[index] : box.max[index];
        this.section.setBoxFace(grabbed.axis, grabbed.side, current + meters);
      }
    });
  }

  /**
   * Follows a left drag along a world direction: the drag along that direction's image on the screen becomes
   * meters, passed to `move` on every mouse move. The drawn clipping follows at once; the download box is fetched
   * again after release.
   */
  private dragAlong(
    event: MouseEvent,
    anchor: HsMeasurementPoint,
    direction: readonly [number, number, number],
    move: (meters: number) => void
  ): void {
    const rect: DOMRect | undefined = this.resolveViewerCanvasElement()?.getBoundingClientRect();
    const from: { x: number; y: number } | null =
      rect === undefined ? null : hsProjectToScreen(this.camera, rect, anchor);
    const to: { x: number; y: number } | null =
      rect === undefined
        ? null
        : hsProjectToScreen(this.camera, rect, [
            anchor[0] + direction[0],
            anchor[1] + direction[1],
            anchor[2] + direction[2],
          ]);
    if (from === null || to === null) {
      return;
    }
    // Pixels per meter along the direction on screen; a direction pointing at the camera has almost none.
    const screen: number[] = [to.x - from.x, to.y - from.y];
    const lengthSquared: number = screen[0] * screen[0] + screen[1] * screen[1];
    if (lengthSquared < 1) {
      this.setStatusMessage('That direction points at the camera; turn the view to drag along it.');
      return;
    }
    let last: { x: number; y: number } = { x: event.clientX, y: event.clientY };
    const moves: Subscription = fromEvent<MouseEvent>(this.document, 'mousemove').subscribe((mouse: MouseEvent) => {
      move(((mouse.clientX - last.x) * screen[0] + (mouse.clientY - last.y) * screen[1]) / lengthSquared);
      last = { x: mouse.clientX, y: mouse.clientY };
      this.updateDrawnSectionPlane();
    });
    const release: Subscription = fromEvent<MouseEvent>(this.document, 'mouseup').subscribe(() => {
      moves.unsubscribe();
      release.unsubscribe();
      this.ngZone.run(() => this.applySection());
    });
  }

  /**
   * Moves the drawn section without new shaders: the scene, batch, highlight and picking shaders all read the planes
   * from one storage, which the look rewrites in place. Returns false when the number of planes changed (turning the
   * section on or off, or switching between plane and box), which needs the scene look rebuilt.
   */
  private updateDrawnSectionPlane(): boolean {
    const drawn: number = this.sectionClipping?.clippingPlanes.length ?? 0;
    if (drawn === 0 || drawn !== this.section.planes().length) {
      return false;
    }
    this.makeSceneLook();
    this.refreshSectionOverlay();
    // The new outline replaces the old one in the queue, which keeps its size, so the cached draw order would still
    // hold the old object; the next culling pass then draws the new one.
    this.viewerRenderOrder.invalidate();
    this.viewer?.invalidate(true);
    // Distributed rendering keeps the finished frame; the moved planes need a full pass, shown once it completes.
    this.lastRenderedObjectIndex = 0;
    return true;
  }

  /** Measurement values and marker names as labels over the 3D view. */
  private refreshLabels(): void {
    if (this.viewerLabels === null) {
      const container: HTMLElement | null = this.labelsLayer?.nativeElement ?? null;
      if (container === null) {
        return;
      }
      this.viewerLabels = new ViewerLabels(container, (point: readonly [number, number, number]) => {
        const rect: DOMRect | undefined = this.resolveViewerCanvasElement()?.getBoundingClientRect();
        return rect === undefined ? null : hsProjectToScreen(this.camera, rect, point);
      });
    }
    const labels: ViewerLabel[] = [];
    const anchor: HsMeasurementPoint | null = this.measurements.tool.labelAnchor;
    if (anchor !== null) {
      labels.push({ id: 'measurement', text: this.measurements.tool.label, point: anchor, kind: 'measurement' });
    }
    if (this.markersVisible) {
      this.markers.forEach((marker: HypersceneMarker) =>
        labels.push({ id: `marker:${marker.id}`, text: marker.name, point: marker.position, kind: 'marker' })
      );
    }
    this.viewerLabels.set(labels);
  }

  private canvasAspect(): number {
    const bounds: DOMRect | undefined = this.resolveViewerCanvasElement()?.getBoundingClientRect();
    return bounds === undefined || bounds.height <= 0 ? 1 : bounds.width / bounds.height;
  }

  /** Highlights the nodes in the 3D view; a later scene refresh keeps the highlight. */
  private highlightNodes(nodeIds: ReadonlySet<string>): void {
    this.selectionHighlightNodeIds = nodeIds;
    this.selectionHighlight.apply(nodeIds);
    this.requestRender();
  }

  /**
   * Highlights the selected node at once and, for a group such as a storey, its descendants once the API lists
   * them: drawn meshes belong to leaf nodes.
   */
  private async highlightSelectedNode(nodeId: string, hasChildren: boolean): Promise<void> {
    this.highlightNodes(new Set([nodeId]));
    if (!hasChildren) {
      return;
    }
    const children: HypersceneNodeSmallView[] = await this.ingestAdapter.getNodesAllChildren(this.ingestApiBaseUrl, [
      nodeId,
    ]);
    if (this.selectedSceneNodeId === nodeId) {
      this.highlightNodes(new Set([nodeId, ...children.map((child: HypersceneNodeSmallView) => child.id)]));
    }
  }

  /** The highlight material of the current look: solid, with the scene's lighting and section. */
  private selectionHighlightMaterial(): HsMaterial | null {
    const gl: WebGL2RenderingContext | null = this.gl;
    if (gl === null) {
      return null;
    }
    const look: HsSceneLook = this.makeSceneLook();
    return new HsSceneMaterials({
      gl,
      cache: this.sceneMaterials,
      cachePrefix: `highlight:${look.signature}`,
      opacity: 1,
      disableTransparentMaterials: true,
      clippingStorage: look.clippingStorage,
      configureMeshShader: (shaderData: HsShaderData): HsShaderData => look.configureMeshShader(shaderData),
    }).selectionMaterials({
      fresnelEnabled: this.visualQualitySettings.fresnelEnabled,
      unlit: this.visualQualitySettings.shadingMode === 'unlit',
    }).mesh;
  }

  /** Places the section plane through the model point under a click, across the current axis. */
  private placeSectionAt(clientX: number, clientY: number): void {
    const hit: ViewerSurfaceHit | null = this.pickMeasurementSurface(clientX, clientY, true);
    if (hit === null) {
      this.setStatusMessage('Nothing under the cursor: click on the model to place the section.');
      return;
    }
    const alongFace: boolean = this.sectionPickArmed === 'face';
    this.sectionPickArmed = false;
    this.ensureSectionBounds();
    if (alongFace && hit.normal !== null) {
      // Face the normal towards the camera and cut just behind the face, so the cut opens the face up.
      const eye: HsHypersceneTypes.Array3 = this.camera.worldMatrix.getPositionArray();
      const facing: number =
        hit.normal[0] * (eye[0] - hit.point[0]) +
        hit.normal[1] * (eye[1] - hit.point[1]) +
        hit.normal[2] * (eye[2] - hit.point[2]);
      const normal: [number, number, number] =
        facing < 0 ? [-hit.normal[0], -hit.normal[1], -hit.normal[2]] : [hit.normal[0], hit.normal[1], hit.normal[2]];
      this.section.cutThroughFace(
        [
          hit.point[0] - normal[0] * FACE_SECTION_DEPTH,
          hit.point[1] - normal[1] * FACE_SECTION_DEPTH,
          hit.point[2] - normal[2] * FACE_SECTION_DEPTH,
        ],
        normal
      );
    } else {
      if (alongFace) {
        this.setStatusMessage('No face found there; cutting across the current axis instead.');
      }
      this.section.cutAt(hit.point);
    }
    this.applySection();
  }

  /** Bounds the section by the scene, or by a range around the view center when the scene has none. */
  private ensureSectionBounds(): void {
    const bounds: HypersceneBoundingBoxView | null = this.sceneBounds;
    if (bounds !== null) {
      this.section.setBounds({
        min: [bounds.min[0], bounds.min[1], bounds.min[2]],
        max: [bounds.max[0], bounds.max[1], bounds.max[2]],
      });
      return;
    }
    if (this.section.bounds === null) {
      const center: HsMeasurementPoint = this.viewCenterPoint();
      const half: number = SECTION_FALLBACK_HALF_SPAN;
      this.section.setBounds({
        min: [center[0] - half, center[1] - half, center[2] - half],
        max: [center[0] + half, center[1] + half, center[2] + half],
      });
    }
    void this.ensureSceneBounds().then((sceneBounds: HypersceneBoundingBoxView | null) => {
      if (sceneBounds !== null && this.section.mode !== 'off') {
        this.ensureSectionBounds();
        this.refreshSectionOverlay();
        this.changeDetector.markForCheck();
      }
    });
  }

  /** The selected node's box when it is known, else the scene's. */
  private selectedOrSceneBounds(): { min: [number, number, number]; max: [number, number, number] } | null {
    const bounds: HypersceneBoundingBoxView | null =
      this.selectedSceneNodeId !== null && this.isValidBoundingBox(this.selectedNodeBoundingBox)
        ? this.selectedNodeBoundingBox
        : this.sceneBounds;
    return bounds === null
      ? null
      : { min: [bounds.min[0], bounds.min[1], bounds.min[2]], max: [bounds.max[0], bounds.max[1], bounds.max[2]] };
  }

  /**
   * Applies the section as the scene's clipping and draws its outline. Moving, flipping or resizing it only rewrites
   * the drawn shaders' planes, so the model neither blinks nor waits for a rebuild; the download box is fetched
   * again once the section stops changing, keeping the drawn geometry meanwhile.
   */
  private applySection(): void {
    if (!this.updateDrawnSectionPlane()) {
      this.sceneStats = { ...this.sceneStats, clippingEnabled: this.section.mode !== 'off' };
      this.lastRenderedSceneSignature = null;
      this.applyVisualControlChanges();
      this.refreshSectionOverlay();
    }
    // The download box changed with the section: ask for what it lets through.
    if (this.ingestState.sceneId !== null) {
      this.scheduleFrustumRefresh(this.ingestState.sceneId, SECTION_REFETCH_DEBOUNCE_MS, 'section');
    }
    // Re-rendering reports the look it applied; the section is what the user changed.
    this.setStatusMessage(
      this.section.mode === 'off'
        ? 'Section off.'
        : this.section.mode === 'box'
        ? 'Section box: only its inside stays visible.'
        : `Section across ${this.section.axis.toUpperCase()} at ${this.section.position.toFixed(2)} m` +
          `${this.section.flipped ? ', low side cut' : ''}.`
    );
  }

  private refreshSectionOverlay(): void {
    const overlays: HsViewerOverlays | null = this.overlays;
    if (overlays === null) {
      return;
    }
    overlays.clear(SECTION_OVERLAY);
    overlays.addLines(SECTION_OVERLAY, 'debug-section-outline', this.section.outlineVertices(), [1, 0.45, 0.1]);
    this.requestRender();
  }

  private frameCameraToBounds(bounds: HypersceneBoundingBoxView, updateHomeCamera: boolean): void {
    const center: HsVector3 = new HsVector3(
      (bounds.min[0] + bounds.max[0]) / 2,
      (bounds.min[1] + bounds.max[1]) / 2,
      (bounds.min[2] + bounds.max[2]) / 2
    );
    const span: number = Math.max(
      bounds.max[0] - bounds.min[0],
      bounds.max[1] - bounds.min[1],
      bounds.max[2] - bounds.min[2]
    );
    const distance: number = Math.max(span * 0.5, 5);
    this.cameraPoint.set(center.x + distance, center.y + distance * 0.5, center.z + distance);
    this.observablePoint.copy(center);
    if (updateHomeCamera) {
      this.homeCameraPoint.copy(this.cameraPoint);
      this.homeObservablePoint.copy(center);
    }
    this.camera.worldMatrix.setPosition(this.cameraPoint.values);
    HsHypersceneMathUtils.lookAt(
      this.camera.worldMatrix,
      this.cameraPoint,
      this.observablePoint,
      HsHypersceneEngineConstants.UP
    );
    this.camera.updateProjectionMatrix();
    this.camera.updateCameraMatrixInverse();
    this.viewer?.syncCamera();
    this.setPivot([center.x, center.y, center.z]);
    this.forceFrustumRefresh = true;
    this.lastRenderedObjectIndex = 0;
    this.requestRender();
  }

  private async syncRenderedGeometryWithSceneVisibility(sceneId: string): Promise<void> {
    await this.refreshGeometryByFrustum(sceneId, true, 'visibility-sync');
    return;
  }

  private setFrustumFrozen(frozen: boolean): void {
    const changed: boolean = this.freezeFrustum !== frozen;
    this.freezeFrustum = frozen;
    if (frozen) {
      if (this.frustumRefreshTimer !== null) {
        window.clearTimeout(this.frustumRefreshTimer);
        this.frustumRefreshTimer = null;
      }
      this.sceneSource.abort();
      this.frustumRefreshPending = false;
    }
    if (changed) {
      this.forceFrustumRefresh = true;
      this.lastRenderedObjectIndex = 0;
      this.requestRender();
    }
  }

  private scheduleFrustumRefresh(sceneId: string, debounceMs: number, reason: string = 'unspecified'): void {
    this.viewerProfiler?.recordEvent(
      'frustum.schedule',
      `reason=${reason};delay=${Math.max(0, debounceMs)};nav=${this.isCameraNavigationActive()};` +
        `inProgress=${this.frustumRefreshInProgress};pending=${this.frustumRefreshPending}`
    );
    if (this.freezeFrustum || this.ingestState.phase !== 'ready') {
      return;
    }

    if (this.sceneLoadInProgress && !this.frustumRefreshInProgress) {
      // A refresh requested while the viewer page is still loading the scene (for example, the
      // initial request was canceled once a background tab became visible) must not be lost:
      // the scene load flushes it when it finishes.
      this.frustumRefreshPending = true;
      return;
    }

    this.frustumScheduler.schedule(
      debounceMs,
      () => this.isCameraNavigationActive(),
      () => {
        this.viewerProfiler?.recordEvent(
          'frustum.timer-fired',
          `scene=${sceneId};nav=false;pending=${this.frustumRefreshPending}`
        );
        void this.refreshGeometryByFrustum(sceneId, false, 'scheduled');
      }
    );
  }

  // eslint-disable-next-line complexity
  private async refreshGeometryByFrustum(
    sceneId: string,
    visibilityOnly: boolean = false,
    trigger: string = 'direct'
  ): Promise<void> {
    if (this.freezeFrustum) {
      this.viewerProfiler?.recordEvent('frustum.skip', 'reason=frozen');
      return;
    }

    if (this.isCameraNavigationActive()) {
      this.frustumRefreshPending = true;
      this.viewerProfiler?.recordEvent('frustum.skip', 'reason=navigation-active;pending=true');
      return;
    }

    if (
      !this.ensureViewerRuntimeReady() ||
      this.ingestState.sceneId !== sceneId ||
      this.ingestState.phase !== 'ready'
    ) {
      this.viewerProfiler?.recordEvent('frustum.skip', 'reason=runtime-not-ready');
      return;
    }

    this.sceneRefreshTrigger = trigger;
    this.sceneSource.sceneId = sceneId;
    this.sceneSource.setRootNodeIds(this.sceneTreeRoots.map((rootNode: HypersceneSceneTreeNode) => rootNode.id));
    // A request while a refresh runs makes that refresh stop downloading and run again when it ends.
    await this.sceneSource.update({ visibilityOnly });
  }

  /** What `HsSceneSource.refresh` needs from this page: camera, drawing, settings and progress UI. */

  private beginViewerLoading(message: string): void {
    this.viewerLoadingState = {
      message,
      completed: 0,
      total: null,
    };
    this.changeDetector.markForCheck();
  }

  private setViewerLoadingProgress(completed: number, total: number, message: string): void {
    const normalizedTotal: number = Math.max(1, Math.floor(total));
    this.viewerLoadingState = {
      message,
      completed: Math.max(0, Math.min(normalizedTotal, Math.floor(completed))),
      total: normalizedTotal,
    };
    this.changeDetector.markForCheck();
  }

  /** Keeps the overlay's photogrammetry line current; the status arrives from the render loop, outside Angular. */
  private onPhotogrammetryStatus(status: HsPhotogrammetryLoadStatus): void {
    const busyChanged: boolean = status.busy !== this.photogrammetryLoading.status.busy;
    this.photogrammetryLoading = {
      status,
      peakPending: status.busy
        ? Math.max(this.photogrammetryLoading.peakPending, pendingPhotogrammetryWork(status))
        : 0,
    };
    if (busyChanged) {
      if (this.photogrammetryStatusTimer !== null) {
        window.clearTimeout(this.photogrammetryStatusTimer);
        this.photogrammetryStatusTimer = null;
      }
      this.ngZone.run(() => this.changeDetector.markForCheck());
      return;
    }
    this.photogrammetryStatusTimer ??= window.setTimeout(() => {
      this.photogrammetryStatusTimer = null;
      this.ngZone.run(() => this.changeDetector.markForCheck());
    }, 200);
  }

  private finishViewerLoadingIfIdle(): void {
    if (this.sceneLoadInProgress || this.frustumRefreshInProgress) {
      return;
    }
    this.viewerLoadingState = {
      message: '',
      completed: 0,
      total: null,
    };
    this.changeDetector.markForCheck();
  }

  /** The page's runtime settings as the SDK's Frustum request settings. */
  private getFrustumRequestSettings(): HsFrustumRequestSettings {
    return {
      meshLimit: this.runtimeSettings.meshLimit,
      meshAngle: this.runtimeSettings.meshAngle,
      meshSortOrder: this.meshFrustumSortOrder,
      lineLimit: this.runtimeSettings.lineLimit,
      lineAngle: this.runtimeSettings.lineAngle,
      // The demo uses the budget LOD only; the level settings keep the SDK defaults.
      pointCloudLodStrategy: 'budget',
      pointCloudSortOrder: 'angular-size',
      pointCloudLimit: HS_DEFAULT_FRUSTUM_REQUEST_SETTINGS.pointCloudLimit,
      pointCloudBoxLimit: HS_DEFAULT_FRUSTUM_REQUEST_SETTINGS.pointCloudBoxLimit,
      pointCloudAngle: HS_DEFAULT_FRUSTUM_REQUEST_SETTINGS.pointCloudAngle,
      minLevel: HS_DEFAULT_FRUSTUM_REQUEST_SETTINGS.minLevel,
      maxLevel: HS_DEFAULT_FRUSTUM_REQUEST_SETTINGS.maxLevel,
      pointCloudBudget: this.runtimeSettings.pointCloudBudget,
      pointSize: this.runtimeSettings.pointCloudPointSize,
    };
  }

  private getFrustumViewport(): HsFrustumViewport {
    return {
      drawingBufferWidth: this.gl?.drawingBufferWidth ?? 1280,
      drawingBufferHeight: this.gl?.drawingBufferHeight ?? 720,
    };
  }

  private getPointCloudDownloadBatching(): Pick<
    HsFrustumDownloadSettings,
    'pointCloudBatchBytes' | 'pointCloudFirstBatchSize'
  > {
    return hsPointCloudDownloadBatching({ pointCloudLodStrategy: 'budget' });
  }

  /**
   * Points and blocks the point cloud frustum session may hold (`points`) and the decoded cache keeps
   * (`cachePoints`). With the budget LOD the session follows the regulated draw budget with headroom: the
   * first view downloads a share of the configured budget, and navigation that stays within the target
   * frame time raises the regulated budget and, with it, what the next refresh downloads.
   */
  private getPointCloudLimits(): { points: number; cachePoints: number; boxes: number } {
    const budget: number = hsResolvePointCloudBudget(this.runtimeSettings.pointCloudBudget, {
      ...hsPointCloudBudgetDevice(),
      drawingBufferWidth: this.gl?.drawingBufferWidth ?? 1280,
      drawingBufferHeight: this.gl?.drawingBufferHeight ?? 720,
    });
    const frameBudgetMs: number = this.runtimeSettings.pointCloudFrameBudgetMs;
    this.pointCloudDrawBudget.configure({
      maxPoints: budget,
      targetGpuMs: frameBudgetMs,
      // Frame intervals never drop below the display period, hence the floor.
      targetIntervalMs: Math.max(25, frameBudgetMs * 1.8),
    });
    const points: number = this.pointCloudDrawBudget.downloadPoints(
      POINT_CLOUD_DOWNLOAD_HEADROOM,
      POINT_CLOUD_DOWNLOAD_MIN_SHARE
    );
    return { points, cachePoints: budget, boxes: HS_POINT_CLOUD_BUDGET_BOX_LIMIT };
  }

  private async ensureSceneHasRoots(sceneId: string): Promise<boolean> {
    if (this.sceneTreeRoots.length > 0) {
      return false;
    }

    this.sceneLoadDebug = {
      ...this.sceneLoadDebug,
      loadMode: 'scene-root-recovery',
      lastUpdatedAt: new Date().toLocaleTimeString(),
      lastError: 'scene-has-no-roots',
    };

    let fallbackModelId: string | null = this.selectedModelId ?? this.ingestState.modelId;
    if (fallbackModelId === null) {
      if (this.modelsRegistry.length === 0) {
        await this.refreshModelsRegistry();
      }
      fallbackModelId = this.selectedModelId ?? this.ingestState.modelId ?? this.modelsRegistry[0]?.id ?? null;
    }

    if (fallbackModelId === null) {
      this.sceneLoadDebug = {
        ...this.sceneLoadDebug,
        loadMode: 'root-only',
        rootNodesCount: 0,
        visibleRootNodesCount: 0,
        meshIdsCount: 0,
        lineIdsCount: 0,
        pointCloudIdsCount: 0,
        lastUpdatedAt: new Date().toLocaleTimeString(),
        lastError: 'No scene roots and no fallback model available.',
      };
      return false;
    }

    const modelRootNode: HypersceneNodeView = await this.ingestAdapter.getRootNode(
      this.ingestApiBaseUrl,
      fallbackModelId
    );
    await this.ingestAdapter.putNodeTreeToScene(this.ingestApiBaseUrl, sceneId, modelRootNode.id);
    await this.reloadSceneTree();

    this.sceneLoadDebug = {
      ...this.sceneLoadDebug,
      loadMode: 'scene-root-recovery',
      rootNodesCount: this.sceneTreeRoots.length,
      visibleRootNodesCount: this.sceneTreeRoots.length,
      lastUpdatedAt: new Date().toLocaleTimeString(),
      lastError: 'none',
    };

    this.ingestState = {
      ...this.ingestState,
      modelId: fallbackModelId,
      rootNodeId: modelRootNode.id,
      message: `Scene had no roots. Attached model ${fallbackModelId} root ${modelRootNode.id}.`,
      error: null,
    };
    return true;
  }

  // eslint-disable-next-line complexity

  private addFrustumDebugObjects(): void {
    const overlays: HsViewerOverlays | null = this.overlays;
    if (overlays === null) {
      return;
    }
    overlays.clear(FRUSTUM_DEBUG_OVERLAYS);
    if (this.showObjectBoundingBoxes) {
      const sceneObjects: Set<HsGraphicObject> = new Set<HsGraphicObject>([
        ...this.sceneMeshObjectsByGeometryId.values(),
        ...this.sceneLineObjectsByGeometryId.values(),
      ]);
      const vertices: number[] = [];
      let boxesAdded: number = 0;
      for (const object of sceneObjects) {
        if (boxesAdded >= MAX_DEBUG_OBJECT_BOUNDING_BOXES) {
          break;
        }
        if (!object.visible || object.isDeleted || object.geometry.isDeleted || object.boundingBox.isEmpty()) {
          continue;
        }
        appendLineCubeVertices(vertices, object.boundingBox);
        boxesAdded++;
      }
      overlays.addLines(FRUSTUM_DEBUG_OVERLAYS, `debug-object-boxes-${boxesAdded}`, vertices, [1, 0.1, 0.8]);
    }
    if (this.showPointCloudBoxes) {
      for (const [geometryId, pointCloudObject] of this.scenePointCloudObjectsByGeometryId) {
        if (pointCloudObject.boundingBox.isEmpty()) {
          continue;
        }
        const vertices: number[] = [];
        appendLineCubeVertices(vertices, pointCloudObject.boundingBox);
        overlays.addLines(FRUSTUM_DEBUG_OVERLAYS, `debug-point-cloud-box-${geometryId}`, vertices, [0.1, 0.8, 1]);
      }
    }
  }

  private ensureViewerRuntimeReady(): boolean {
    if (this.gl !== null && this.graphics !== null && this.objectsQueue !== null && this.viewerRuntimeInitialized) {
      return true;
    }

    const canvasElement: HTMLCanvasElement | null = this.resolveViewerCanvasElement();
    if (canvasElement === null) {
      return false;
    }

    if (this.gl === null) {
      this.gl = canvasElement.getContext('webgl2', { preserveDrawingBuffer: true, antialias: false });
    }

    if (this.gl === null) {
      return false;
    }

    if (!this.viewerRuntimeInitialized || this.graphics === null || this.objectsQueue === null) {
      this.loadLocalViewerState();
      this.sceneSettingsWorkerStatus = typeof Worker === 'undefined' ? 'not-supported' : 'available';
      this.initOptions();
      this.initScene();
      this.viewerRuntimeInitialized = true;
    }

    return this.graphics !== null && this.objectsQueue !== null;
  }

  private resolveViewerCanvasElement(): HTMLCanvasElement | null {
    const canvasFromViewChild: HTMLCanvasElement | undefined = this.canvasDocument?.nativeElement;
    if (canvasFromViewChild !== undefined) {
      return canvasFromViewChild;
    }

    const canvasFromDom: Element | null = this.document.querySelector('hyperscene-advanced canvas.canvas');
    if (canvasFromDom instanceof HTMLCanvasElement) {
      return canvasFromDom;
    }

    return null;
  }

  private setIngestError(message: string): void {
    this.ingestState = {
      ...this.ingestState,
      phase: 'failed',
      error: message,
      message: 'Ingest pipeline failed.',
    };
    this.changeDetector.markForCheck();
  }

  /** Shows a tool's progress in the status line; unlike `setIngestError` it leaves the ingest phase alone. */
  private setStatusMessage(message: string): void {
    this.ingestState = { ...this.ingestState, message, error: null };
    this.changeDetector.markForCheck();
  }

  private handleFullscreenChange(): Subscription {
    return fromEvent(this.document, 'fullscreenchange')
      .pipe(subscribeOutsideAngular(this.ngZone))
      .subscribe(() => {
        this.isFullscreen = this.document.fullscreenElement !== null;
        this.resize();
        this.changeDetector.markForCheck();
      });
  }

  private initOptions(): void {
    if (this.gl === null) {
      return;
    }
    if (this.canvasDocument === null) {
      return;
    }

    const canvas: HTMLCanvasElement = this.canvasDocument.nativeElement;
    const viewer: HsHypersceneViewer = new HsHypersceneViewer({
      canvas,
      gl: this.gl,
      camera: this.camera,
      render: this.render,
      cameraTools: this.cameraToolsHandler,
      antialiasingSamples: this.resolveVisualQualityAntialiasingSamples(),
      autoResize: false,
      frames: window,
      filter: this.filter,
      sorter: this.sorter,
      cullingIntervalMs: FRUSTUM_CULLING_MIN_INTERVAL_MS,
      frameBudgetMs: VIEWER_FRAME_BUDGET_MS,
      isNavigating: () => this.isCameraNavigationActive(),
      reuseUnchangedLists: () => this.useCachedBatchData,
      spans: () => (this.viewerProfilingEnabled ? this.viewerProfiler : null),
      hooks: {
        beforeCulling: (frame: HsViewerFrameState) => this.beforeViewerCulling(frame),
        afterCulling: () => this.afterViewerCulling(),
        beforeDraw: (frame: HsViewerFrameState) => this.beforeViewerDraw(frame),
        afterDraw: (frame: HsViewerFrameState, complete: boolean) => this.afterViewerDraw(frame, complete),
        keepDrawing: () => this.shouldContinueRendering(),
      },
    });
    this.viewer = viewer;
    viewer.use(this.sceneSource);
    this.sceneSourceListeners = this.listenToSceneSource();
    this.viewerInput = this.createViewerInput(viewer);
    const surface: HsViewerSurface = viewer.surface;
    this.overlays = new HsViewerOverlays({ gl: this.gl, graphics: surface.graphics, queue: surface.objectsQueue });
    this.graphics = surface.graphics;
    this.objectsLayer = surface.objectsLayer;
    this.objectsQueue = surface.objectsQueue;
    this.renderObjects = surface.renderObjects;
    this.applyCadPostProcess();

    this.cameraToolsHandler.updateCanvasSize(surface.canvasSize);
    this.canvasSize = surface.canvasSize;

    window.removeEventListener('resize', this.handleWindowResize);
    window.addEventListener('resize', this.handleWindowResize);
    this.observeCanvasResize(canvas);
    this.markRenderStateDirty(true);
  }

  /** Attaches the CAD post process to the scene layer for the `cad` profile and detaches it otherwise. */
  private applyCadPostProcess(): void {
    if (this.gl === null || this.objectsLayer === null) {
      return;
    }
    const gl: WebGL2RenderingContext = this.gl;
    if (!viewerCadEffectsActive(this.visualQualitySettings)) {
      if (this.objectsLayer.postProcess !== null) {
        this.objectsLayer.setPostProcess(gl, null);
        this.cadPostProcess?.dispose(gl);
        this.cadPostProcess = null;
      }
      return;
    }
    if (this.cadPostProcess === null) {
      this.cadPostProcess = new HsCadPostProcess(this.visualQualitySettings.cad);
      this.objectsLayer.setPostProcess(gl, this.cadPostProcess);
    }
    this.cadPostProcess.setSettings(this.visualQualitySettings.cad);
    this.cadPostProcess.clearColor = [this.render.clearColor.r, this.render.clearColor.g, this.render.clearColor.b];
    this.markRenderStateDirty(true);
  }

  private applyVisualQualityFramebufferSettings(): void {
    if (this.gl === null) {
      return;
    }
    this.applyCadPostProcess();
    const antialiasingSamples: 0 | 2 | 4 | 8 = this.resolveVisualQualityAntialiasingSamples();
    [this.objectsLayer].forEach((layer: HsGraphicLayer | null) => {
      if (layer === null || layer.antialiasingSamples === antialiasingSamples) {
        return;
      }
      layer.antialiasingSamples = antialiasingSamples;
      layer.updateSamplingFramebuffer(this.gl as WebGL2RenderingContext);
      layer.hasPresentedFrame = false;
      layer.presentDistributedProgress = true;
    });
  }

  private resolveVisualQualityAntialiasingSamples(): 0 | 2 | 4 | 8 {
    if (this.gl === null || this.visualQualitySettings.antiAliasing === 'off') {
      this.visualQualityDiagnosticMessage = '';
      return 0;
    }
    const requestedSamples: 2 | 4 | 8 =
      this.visualQualitySettings.antiAliasing === 'msaa2'
        ? 2
        : this.visualQualitySettings.antiAliasing === 'msaa8'
        ? 8
        : 4;
    const maxSamples: number = Number(this.gl.getParameter(this.gl.MAX_SAMPLES));
    const availableSamples: 0 | 2 | 4 | 8 =
      Number.isFinite(maxSamples) && maxSamples >= requestedSamples
        ? requestedSamples
        : Number.isFinite(maxSamples) && maxSamples >= 8
        ? 8
        : Number.isFinite(maxSamples) && maxSamples >= 4
        ? 4
        : Number.isFinite(maxSamples) && maxSamples >= 2
        ? 2
        : 0;
    if (availableSamples !== requestedSamples) {
      this.visualQualityDiagnosticMessage = `MSAA ${requestedSamples}x unavailable (GPU maximum: ${
        maxSamples > 0 ? maxSamples : 0
      }); using ${availableSamples > 0 ? `MSAA ${availableSamples}x` : 'off'}.`;
      return availableSamples;
    }
    this.visualQualityDiagnosticMessage = '';
    return availableSamples;
  }

  private initScene(): void {
    if (this.gl === null) {
      return;
    }

    this.camera.fov = 1;
    this.camera.worldMatrix.setPosition(CAMERA_POSITION);
    this.camera.updateProjectionMatrix();
    this.camera.updateLocalMatrix();

    if (this.objectsLayer === null) {
      return;
    }

    this.raycast.layers = [this.objectsLayer];

    this.cameraToolsHandler.initialize();
    this.controlActionsHandler.updateControlAction(new HsCameraToolTypeControlAction(HsCameraToolType.Default));
    this.controlActionsHandler.updateControlAction(new HsCameraTypeControlAction(HsCameraType.Perspective));
    this.selectedCameraToolType = this.cameraToolsHandler.activeCameraToolType;

    this.requestRender();
  }

  private requestRender(): void {
    this.viewer?.requestRender();
  }

  /** Before culling: HLOD tiles, the camera-move bookkeeping and the switch of size culling. */
  private beforeViewerCulling(frame: HsViewerFrameState): void {
    if (this.viewerProfilingEnabled) {
      this.viewerProfiler?.pollGpuQueries();
      this.viewerFrameSpan = this.viewerProfiler?.startSpan('frame.update') ?? null;
    }
    if (this.gl !== null) {
      this.observePointCloudFrameCost(this.gl, frame.startedAt);
    }
    this.updateHlodTilesets();
    this.cameraNavigationFrame = frame.navigating;
    if (frame.cameraMoved) {
      this.markRenderStateDirty(true);
    }
    const navigationCullingActive: boolean = this.navigationMinScreenSize > 0 && this.cameraNavigationFrame;
    if (navigationCullingActive !== this.navigationCullingActive) {
      this.navigationCullingActive = navigationCullingActive;
      this.lastFrustumCullingAt = Number.NEGATIVE_INFINITY;
    }
    if (this.applyPointCloudDrawBudget(frame.navigating)) {
      this.forceFrustumRefresh = true;
    }
  }

  /** Per-frame render settings, mesh batch preparation and the draw spans. */
  private beforeViewerDraw(frame: HsViewerFrameState): void {
    const gl: WebGL2RenderingContext | null = this.gl;
    const viewer: HsHypersceneViewer | null = this.viewer;
    if (gl === null || viewer === null) {
      return;
    }
    this.distributedRendering = this.viewerDistributedRendering;
    viewer.distributedRendering = this.distributedRendering;
    this.render.meshDrawBatchingEnabled = this.enableInstancing;
    this.render.meshBatchPreparationEnabled = this.usePreparedMeshBatches && this.distributedRendering;
    this.render.meshBatchDataReuseEnabled = this.useCachedBatchData;
    this.render.distributedCostPredictionEnabled = this.useMeasuredDistributedCost;
    this.render.frameTimeForDistributedRendering = this.runtimeSettings.frameBudgetMs;
    this.render.distributedRenderingInertiaFrames = this.runtimeSettings.drawCountInertiaFrames;
    if (this.render.meshBatchPreparationEnabled && !this.cameraNavigationFrame) {
      this.render.prepareMeshBatches(gl, viewer.visibleObjects);
    }
    if (this.viewerProfilingEnabled) {
      this.viewerDrawSpan = this.viewerProfiler?.startSpan('frame.draw') ?? null;
      this.viewerGpuSpan = this.viewerProfiler?.startGpuSpan(gl, 'frame.draw') ?? null;
    }
    this.cadPostProcess?.setNavigating(frame.navigating);
    this.pointCloudGpuTimer.begin(gl);
  }

  /** Render totals, profiler counters, the FPS counter and scene stats of a drawn frame. */
  private afterViewerDraw(frame: HsViewerFrameState, renderComplete: boolean): void {
    this.viewerLabels?.update();
    if (this.gl !== null) {
      // Inside the timed span, so the draw budget regulator pays for the pass too.
      this.submitPointCloudVisibilityPass(this.gl);
      this.pointCloudGpuTimer.end(this.gl);
    }
    this.lastFrameStartedAt = frame.startedAt;
    this.lastFrameNavigating = frame.navigating;
    this.lastFrameContinuous = frame.navigating || !renderComplete || this.shouldContinueRendering();
    if (this.renderObjects === null || this.objectsQueue === null) {
      return;
    }
    if (
      renderComplete &&
      (this.viewerRenderTotalsPending || this.lastMeshBatchingRevision !== this.render.meshBatchingRevision)
    ) {
      this.lastMeshBatchingRevision = this.render.meshBatchingRevision;
      this.viewerRenderTotalsPending = false;
      this.updateSceneRenderTotals();
    }
    if (this.viewerProfilingEnabled) {
      const totalRenderObjects: number =
        this.renderObjects.unsortedObjects.length + this.renderObjects.sortedObjects.length;
      this.viewerProfiler?.recordCounter('frame.renderObjects', totalRenderObjects);
      this.viewerProfiler?.recordCounter('frame.distributedCursor', this.lastRenderedObjectIndex);
      this.viewerProfiler?.recordCounter('frame.drawCalls', this.render.frameStats.drawCalls);
      this.viewerProfiler?.recordCounter('pointCloud.loadedPoints', this.pointCloudLoadedPoints);
      this.viewerProfiler?.recordCounter('pointCloud.drawnPoints', this.pointCloudDrawnPoints);
      this.viewerProfiler?.recordCounter('pointCloud.regulatedPoints', this.pointCloudDrawBudget.regulated);
      this.viewerProfiler?.recordCounter('frame.instancedDrawCalls', this.render.frameStats.instancedDrawCalls);
      this.viewerProfiler?.recordCounter('frame.instancedInstances', this.render.frameStats.instancedInstances);
      this.viewerProfiler?.recordCounter('frame.multiDrawCalls', this.render.frameStats.multiDrawCalls);
      this.viewerProfiler?.recordCounter('frame.multiDrawObjects', this.render.frameStats.multiDrawObjects);
      this.viewerProfiler?.recordCounter('frame.submittedTriangles', this.render.frameStats.submittedTriangles);
      this.viewerProfiler?.recordCounter('frame.framebufferTransfers', this.render.frameStats.framebufferTransfers);
      this.viewerProfiler?.recordEvent(
        'distributed.chunk',
        `enabled=${this.distributedRendering};cursor=${this.lastRenderedObjectIndex};` +
          `objects=${totalRenderObjects};drawCalls=${this.render.frameStats.drawCalls};` +
          `triangles=${this.render.frameStats.submittedTriangles}`
      );
      this.viewerProfiler?.endGpuSpan(this.viewerGpuSpan);
      this.viewerProfiler?.endSpan(this.viewerDrawSpan);
      this.viewerGpuSpan = null;
      this.viewerDrawSpan = null;
    }

    if (this.canvasSize !== null) {
      this.frame++;
      if (performance.now() - this.time > 1000) {
        this.time = performance.now();
        this.fps = this.frame;
        if (this.viewerProfilingEnabled) {
          this.viewerProfilerSnapshot = this.viewerProfiler.snapshot();
        }
        this.changeDetector.markForCheck();
        this.frame = 0;
      }
      this.sceneStats = {
        ...this.sceneStats,
        queueObjectsCount: this.objectsQueue.objects.size,
        distributedRendering: this.distributedRendering,
      };
    }
    if (this.viewerFrameSpan !== null) {
      this.viewerProfiler?.endSpan(this.viewerFrameSpan);
      this.viewerFrameSpan = null;
    }
    if (this.probeHandle?.listensToFrames === true) {
      this.probeHandle.frame(performance.now() - frame.startedAt);
    }
  }

  private updateViewerObjectBounds(object: HsGraphicObject): void {
    const previousRadius: number = object.boundingSphere.radius;
    const previousX: number = object.boundingSphere.center.x;
    const previousY: number = object.boundingSphere.center.y;
    const previousZ: number = object.boundingSphere.center.z;
    object.updateBoundingSphere();
    if (
      object.boundingSphere.radius !== previousRadius ||
      object.boundingSphere.center.x !== previousX ||
      object.boundingSphere.center.y !== previousY ||
      object.boundingSphere.center.z !== previousZ
    ) {
      this.viewerSpatialCulling.refresh();
    }
    if (object.boundingSphere.radius !== previousRadius) {
      this.viewerRenderOrder.invalidate();
    }
  }

  /**
   * Feeds the draw budget regulator with the cost of the previous frame: the GPU time of its draw when a
   * timer query finished, else the interval since it started when it was part of a continuous sequence
   * (navigation or growth), which the browser stretches when the GPU falls behind.
   */
  private observePointCloudFrameCost(gl: WebGL2RenderingContext, frameStartedAt: number): void {
    const gpuMs: number | null = this.pointCloudGpuTimer.poll(gl);
    if (gpuMs !== null) {
      this.pointCloudDrawBudget.observe(gpuMs, 'gpu', this.lastFrameNavigating);
      return;
    }
    if (this.lastFrameContinuous && this.lastFrameStartedAt > 0) {
      this.pointCloudDrawBudget.observe(frameStartedAt - this.lastFrameStartedAt, 'interval', this.lastFrameNavigating);
    }
  }

  /**
   * Decides which loaded point cloud blocks this frame draws, and how many points of each: coarse and near
   * blocks first, up to the draw budget (regulated while navigating, everything wanted once the camera
   * rests). A block that projects denser than the target spacing draws only the share of its (interleaved)
   * points that reaches it, and a block the visibility pass found hidden behind other points is skipped.
   * Skipped blocks keep their GPU buffers. While fewer points are drawn, points grow so surfaces stay
   * closed. Returns whether the drawn set changed, so the culling pass rebuilds its lists.
   */
  private applyPointCloudDrawBudget(navigating: boolean): boolean {
    const objects: Map<string, HsGraphicPoints> = this.scenePointCloudObjectsByGeometryId;
    if (objects.size === 0) {
      this.render.pointCloudSizeScale = 1;
      this.pointCloudDrawGrowing = false;
      this.pointCloudDrawnPoints = 0;
      this.pointCloudWantedPoints = 0;
      this.pointCloudLoadedPoints = 0;
      this.pointCloudOccludedBlocks = 0;
      this.pointCloudOcclusionDraws = null;
      return false;
    }
    const loaded: HsGraphicPoints[] = [];
    let loadedPoints: number = 0;
    // The root block is never skipped as occluded: it is the coarsest stand-in for the whole cloud.
    let root: HsGraphicPoints | null = null;
    let rootEdge: number = -1;
    for (const object of objects.values()) {
      if (object.isDeleted || !object.visible) {
        continue;
      }
      loaded.push(object);
      loadedPoints += object.geometry.positionArrayLength / 3;
      const { min, max } = object.boundingBox;
      const edge: number = Math.max(max.x - min.x, max.y - min.y, max.z - min.z);
      if (edge > rootEdge) {
        rootEdge = edge;
        root = object;
      }
    }
    this.trackPointCloudOcclusionCamera(rootEdge, `${loaded.length}:${loadedPoints}`);
    this.applyPointCloudVisibility(this.pointCloudVisibilityPass?.poll() ?? null);
    const blocks: HsGraphicPoints[] = [];
    const views: HsPointCloudBlockView[] = [];
    let changed: boolean = false;
    let occludedBlocks: number = 0;
    for (const object of loaded) {
      if (this.pointCloudOcclusionEnabled && object !== root && this.pointCloudOcclusion.isOccluded(object)) {
        occludedBlocks++;
        changed = changed || !object.budgetSkipped;
        object.budgetSkipped = true;
        object.drawCount = null;
        continue;
      }
      blocks.push(object);
      views.push({ boundingBox: object.boundingBox, pointCount: object.geometry.positionArrayLength / 3 });
    }
    const camera: ArrayLike<number> = this.camera.worldMatrix.values;
    const drawingBufferHeight: number = this.gl?.drawingBufferHeight ?? 720;
    const order: HsPointCloudDrawOrderEntry[] = hsOrderPointCloudBlocks(views, {
      cameraPosition: [camera[12], camera[13], camera[14]],
      pixelsPerUnitAtUnitDistance: (drawingBufferHeight * this.camera.projectionMatrix.values[5]) / 2,
    });
    const targetSpacing: number = this.runtimeSettings.pointCloudPointSize * HS_POINT_CLOUD_TARGET_SPACING_SHARE;
    let wantedPoints: number = 0;
    for (const entry of order) {
      wantedPoints += hsPointCloudDrawCount(views[entry.index].pointCount, entry.spacing, targetSpacing);
    }
    const { points, growing } = this.pointCloudDrawBudget.budgetFor(wantedPoints, navigating);
    this.pointCloudDrawGrowing = growing;
    const selection: HsPointCloudDrawSelection = hsSelectPointCloudBlocks(order, views, points, targetSpacing);
    for (let index: number = 0; index < blocks.length; index++) {
      const block: HsGraphicPoints = blocks[index];
      const skipped: boolean = selection.drawn[index] === 0;
      if (block.budgetSkipped !== skipped) {
        block.budgetSkipped = skipped;
        changed = true;
      }
      block.drawCount = skipped || selection.counts[index] >= views[index].pointCount ? null : selection.counts[index];
    }
    this.pointCloudDrawnPoints = selection.drawnPoints;
    this.pointCloudWantedPoints = wantedPoints;
    this.pointCloudLoadedPoints = loadedPoints;
    this.pointCloudOccludedBlocks = occludedBlocks;
    if (this.pointCloudAdaptivePointSize) {
      // Blocks size their points for the spacing drawn over their region, which also covers budget cuts.
      this.applyAdaptivePointSizes(loaded, blocks, order, selection, `${loaded.length}:${loadedPoints}`);
      this.render.pointCloudSizeScale = 1;
    } else {
      const basePointSize: number = this.runtimeSettings.pointCloudPointSize;
      loaded.forEach((object: HsGraphicPoints) => object.setPointSize(basePointSize));
      this.render.pointCloudSizeScale =
        navigating && selection.drawnPoints > 0 && selection.drawnPoints < wantedPoints
          ? Math.min(POINT_CLOUD_NAVIGATION_SIZE_SCALE_MAX, Math.sqrt(wantedPoints / selection.drawnPoints))
          : 1;
    }
    this.preparePointCloudVisibilityDraws(loaded);
    return changed;
  }

  /**
   * Adaptive point sizes: each drawn block gets `hsAdaptivePointSize` for the points drawn over its region,
   * its own and those of its loaded descendants (octree levels overlap). Occluded descendants count as
   * covering their region whole, since it is hidden either way. The descendants are found again only when
   * the loaded blocks change.
   */
  private applyAdaptivePointSizes(
    loaded: readonly HsGraphicPoints[],
    blocks: readonly HsGraphicPoints[],
    order: readonly HsPointCloudDrawOrderEntry[],
    selection: HsPointCloudDrawSelection,
    signature: string
  ): void {
    if (signature !== this.pointCloudDescendantsSignature) {
      this.pointCloudDescendants = hsPointCloudBlockDescendants(
        loaded.map((object: HsGraphicPoints) => ({
          boundingBox: object.boundingBox,
          pointCount: object.geometry.positionArrayLength / 3,
        }))
      );
      this.pointCloudDescendantsSignature = signature;
    }
    const blockIndex: Map<HsGraphicPoints, number> = new Map<HsGraphicPoints, number>();
    blocks.forEach((object: HsGraphicPoints, index: number) => blockIndex.set(object, index));
    const spacings: Float64Array = new Float64Array(blocks.length);
    order.forEach((entry: HsPointCloudDrawOrderEntry) => (spacings[entry.index] = entry.spacing));
    const drawn: Float64Array = new Float64Array(loaded.length);
    loaded.forEach((object: HsGraphicPoints, index: number) => {
      const selected: number | undefined = blockIndex.get(object);
      drawn[index] = selected === undefined ? object.geometry.positionArrayLength / 3 : selection.counts[selected];
    });
    const basePointSize: number = this.runtimeSettings.pointCloudPointSize;
    loaded.forEach((object: HsGraphicPoints, index: number) => {
      const selected: number | undefined = blockIndex.get(object);
      if (selected === undefined || drawn[index] === 0) {
        return;
      }
      let covering: number = drawn[index];
      for (const descendant of this.pointCloudDescendants[index] ?? []) {
        covering += drawn[descendant];
      }
      object.setPointSize(
        hsAdaptivePointSize(spacings[selected], object.geometry.positionArrayLength / 3, covering, basePointSize)
      );
    });
  }

  /**
   * Marks the visibility stale when the camera or the loaded blocks changed since the last pass, and
   * forgets every hidden block when the camera jumped: a pass for the new view takes a few frames, and
   * until then blocks the old view hid may be in front.
   */
  private trackPointCloudOcclusionCamera(rootEdge: number, signature: string): void {
    if (signature !== this.pointCloudOcclusionSignature) {
      this.pointCloudOcclusionStale = true;
      this.pointCloudOcclusionSignature = signature;
    }
    const current: ArrayLike<number> = this.camera.worldMatrix.values;
    const last: ArrayLike<number> = this.pointCloudOcclusionCamera.values;
    if (this.camera.worldMatrix.equals(this.pointCloudOcclusionCamera, 0.001)) {
      return;
    }
    this.pointCloudOcclusionStale = true;
    // Column 2 is the view axis and column 3 the position of the column-major world matrix.
    const directionDot: number = current[8] * last[8] + current[9] * last[9] + current[10] * last[10];
    const move: number = Math.hypot(current[12] - last[12], current[13] - last[13], current[14] - last[14]);
    if (
      directionDot < POINT_CLOUD_OCCLUSION_JUMP_MIN_DIRECTION_DOT ||
      move > Math.max(0, rootEdge) * POINT_CLOUD_OCCLUSION_JUMP_MOVE_SHARE
    ) {
      this.pointCloudOcclusion.clear();
      this.pointCloudOcclusionChecked = [];
      this.pointCloudOcclusionCamera.copy(this.camera.worldMatrix);
    }
  }

  /**
   * Collects the draws of the next visibility pass when the camera or the loaded blocks changed since the
   * last one: every loaded block in the view frustum, occluded ones included so they can reappear, as a
   * sample thinned to the pass resolution. Blocks outside the frustum lose their hidden streak, so they are
   * drawn at once when the camera turns back to them.
   */
  private preparePointCloudVisibilityDraws(loaded: readonly HsGraphicPoints[]): void {
    this.pointCloudOcclusionDraws = null;
    if (
      !this.pointCloudOcclusionDue() ||
      performance.now() - this.pointCloudOcclusionLastPassAt < POINT_CLOUD_OCCLUSION_MIN_INTERVAL_MS
    ) {
      return;
    }
    const gl: WebGL2RenderingContext | null = this.gl;
    if (gl === null || loaded.length < 2) {
      return;
    }
    const camera: ArrayLike<number> = this.camera.worldMatrix.values;
    // The pass is POINT_CLOUD_OCCLUSION_PASS_WIDTH wide; spacings shrink by the same factor.
    const view: HsPointCloudDrawView = {
      cameraPosition: [camera[12], camera[13], camera[14]],
      pixelsPerUnitAtUnitDistance:
        (gl.drawingBufferHeight * this.camera.projectionMatrix.values[5] * POINT_CLOUD_OCCLUSION_PASS_WIDTH) /
        (2 * Math.max(1, gl.drawingBufferWidth)),
    };
    const draws: HsVisibilityDraw<HsGraphicPoints>[] = [];
    for (const object of loaded) {
      if (!this.camera.frustum.hasIntersectionWithSphere(object.boundingSphere)) {
        this.pointCloudOcclusion.forget(object);
        continue;
      }
      const pointCount: number = object.geometry.positionArrayLength / 3;
      const sample: number = hsPointCloudDrawCount(
        pointCount,
        hsProjectedPointSpacing({ boundingBox: object.boundingBox, pointCount }, view),
        POINT_CLOUD_OCCLUSION_SAMPLE_SPACING_PIXELS
      );
      draws.push({
        key: object,
        buffer: object.geometry.buffer,
        indexCount: 0,
        pointCount: Math.min(pointCount, Math.max(POINT_CLOUD_OCCLUSION_MIN_SAMPLE_POINTS, sample)),
        modelMatrix: object.worldMatrix.values,
      });
    }
    this.pointCloudOcclusionDraws = draws;
  }

  private pointCloudOcclusionDue(): boolean {
    return (
      this.pointCloudOcclusionEnabled &&
      this.pointCloudOcclusionStale &&
      this.pointCloudVisibilityPass?.busy !== true &&
      this.scenePointCloudObjectsByGeometryId.size > 1
    );
  }

  private submitPointCloudVisibilityPass(gl: WebGL2RenderingContext): void {
    const draws: HsVisibilityDraw<HsGraphicPoints>[] | null = this.pointCloudOcclusionDraws;
    this.pointCloudOcclusionDraws = null;
    if (draws === null || draws.length === 0) {
      return;
    }
    try {
      this.pointCloudVisibilityPass ??= new HsTileVisibilityPass<HsGraphicPoints>(gl, POINT_CLOUD_OCCLUSION_PASS_WIDTH);
      if (
        !this.pointCloudVisibilityPass.submit(
          draws,
          this.camera.worldMatrixInverse.values,
          this.camera.projectionMatrix.values,
          gl.drawingBufferWidth / Math.max(1, gl.drawingBufferHeight)
        )
      ) {
        return;
      }
    } catch (error: unknown) {
      // Without the pass every block counts as visible, as before.
      console.warn('Point cloud visibility pass failed; drawing without occlusion.', error);
      this.pointCloudOcclusionEnabled = false;
      this.pointCloudOcclusion.clear();
      return;
    }
    this.pointCloudOcclusionStale = false;
    this.pointCloudOcclusionLastPassAt = performance.now();
    this.pointCloudOcclusionCamera.copy(this.camera.worldMatrix);
    this.schedulePointCloudVisibilityPoll();
  }

  /** Polls the fence off the render loop, so an idle viewer still receives the result. */
  private schedulePointCloudVisibilityPoll(): void {
    if (this.pointCloudOcclusionPollTimer !== null) {
      return;
    }
    this.pointCloudOcclusionPollTimer = window.setTimeout(() => {
      this.pointCloudOcclusionPollTimer = null;
      const pass: HsTileVisibilityPass<HsGraphicPoints> | null = this.pointCloudVisibilityPass;
      if (pass === null || !pass.busy) {
        return;
      }
      const result: HsVisibilityResult<HsGraphicPoints> | null = pass.poll();
      if (result === null) {
        this.schedulePointCloudVisibilityPoll();
        return;
      }
      // A camera that moved while the pass ran needs another pass, also when it has stopped since.
      if (this.applyPointCloudVisibility(result) || this.pointCloudOcclusionDue()) {
        this.requestRender();
      }
    }, 16);
  }

  /**
   * Records a pass result. Returns whether a block changed between occluded and visible, or another pass
   * is due to confirm a block hidden for the first time.
   */
  private applyPointCloudVisibility(result: HsVisibilityResult<HsGraphicPoints> | null): boolean {
    if (result === null) {
      return false;
    }
    const visible: Set<HsGraphicPoints> = new Set<HsGraphicPoints>(result.pixels.keys());
    const checked: Set<HsGraphicPoints> = new Set<HsGraphicPoints>(result.checked);
    // Blocks unloaded since the previous pass leave the hysteresis with it.
    for (const object of this.pointCloudOcclusionChecked) {
      if (!checked.has(object) && object.isDeleted) {
        this.pointCloudOcclusion.forget(object);
      }
    }
    this.pointCloudOcclusionChecked = result.checked;
    let changed: boolean = false;
    let pending: boolean = false;
    for (const object of result.checked) {
      const before: boolean = this.pointCloudOcclusion.isOccluded(object);
      this.pointCloudOcclusion.observe([object], visible);
      changed = changed || before !== this.pointCloudOcclusion.isOccluded(object);
      pending = pending || this.pointCloudOcclusion.isPending(object);
    }
    if (pending) {
      this.pointCloudOcclusionStale = true;
    }
    this.viewerProfiler?.recordEvent(
      'pointCloud.visibility',
      `blocks=${result.checked.length};visible=${visible.size};ms=${result.cpuMs.toFixed(2)}`
    );
    return changed || pending;
  }

  private disposePointCloudVisibilityPass(): void {
    if (this.pointCloudOcclusionPollTimer !== null) {
      window.clearTimeout(this.pointCloudOcclusionPollTimer);
      this.pointCloudOcclusionPollTimer = null;
    }
    this.pointCloudVisibilityPass?.dispose();
    this.pointCloudVisibilityPass = null;
    this.pointCloudOcclusion.clear();
    this.pointCloudOcclusionChecked = [];
    this.pointCloudOcclusionDraws = null;
    this.pointCloudOcclusionStale = true;
  }

  private shouldContinueRendering(): boolean {
    if (this.mouseButton !== null || this.pointCloudNavigationActive || this.navigationCullingActive) {
      return true;
    }
    if (this.forceFrustumRefresh || this.pointCloudDrawGrowing || this.pointCloudOcclusionDue()) {
      return true;
    }
    if (this.render.meshBatchPreparationPending) {
      return true;
    }
    if (!this.distributedRendering || this.renderObjects === null) {
      return false;
    }
    const objectsTotal: number = this.renderObjects.unsortedObjects.length + this.renderObjects.sortedObjects.length;
    return this.lastRenderedObjectIndex < objectsTotal;
  }

  private getViewerRenderOrder(queue: HsGraphicQueue): readonly HsGraphicObject[] {
    return this.sceneSource.drawOrder(queue);
  }

  /** After a culling pass of the viewer: timing, visible counts and render totals. */
  private afterViewerCulling(): void {
    if (this.renderObjects === null || this.objectsQueue === null) {
      return;
    }
    this.frustumUpdateTime = performance.now() - this.lastFrustumCullingAt;
    if (this.viewerProfilingEnabled) {
      this.viewerProfiler?.recordCounter('frustum.queueObjects', this.objectsQueue.objects.size);
      this.viewerProfiler?.recordCounter(
        'frustum.visibleObjects',
        this.renderObjects.unsortedObjects.length + this.renderObjects.sortedObjects.length
      );
      this.viewerProfiler?.recordCounter('frustum.sortedObjects', this.renderObjects.sortedObjects.length);
      this.viewerProfiler?.recordCounter('frustum.cullingMs', this.frustumUpdateTime);
    }
    this.sceneStats = {
      ...this.sceneStats,
      frustumVisibleObjectsCount: this.renderObjects.unsortedObjects.length + this.renderObjects.sortedObjects.length,
      frustumSortedObjectsCount: this.renderObjects.sortedObjects.length,
      frustumCullingMs: this.frustumUpdateTime,
      queueObjectsCount: this.objectsQueue.objects.size,
      distributedRendering: this.distributedRendering,
      clippingEnabled: this.section.mode !== 'off',
      measurementEnabled: this.measurements.enabled,
    };
    const totalsSpan: HsViewerProfilerSpan | null = this.viewerProfilingEnabled
      ? this.viewerProfiler?.startSpan('frustum.totals') ?? null
      : null;
    if (this.useCachedBatchData && (this.isCameraNavigationActive() || this.cameraNavigationFrame)) {
      this.viewerRenderTotalsPending = true;
    } else {
      this.updateSceneRenderTotals();
      this.viewerRenderTotalsPending = false;
    }
    if (totalsSpan !== null) {
      this.viewerProfiler?.endSpan(totalsSpan);
    }
    this.changeDetector.markForCheck();
  }

  private isCameraNavigationActive(): boolean {
    return this.mouseButton !== null || this.touchNavigationActive || this.pointCloudNavigationActive;
  }

  private async selectSceneObjectAt(clientX: number, clientY: number): Promise<void> {
    if (this.gl === null || this.renderObjects === null) {
      return;
    }
    const canvas: HTMLCanvasElement | null = this.resolveViewerCanvasElement();
    if (canvas === null) {
      return;
    }

    const bounds: DOMRect = canvas.getBoundingClientRect();
    const x: number = (clientX - bounds.left) * (canvas.width / bounds.width);
    const y: number = (bounds.bottom - clientY) * (canvas.height / bounds.height);
    this.sampleRaycast(this.gl, this.renderObjects, x, y);

    const nodeId: string | null = this.getSceneNodeIdForObject(
      this.raycastOutput.object,
      this.raycastOutput.instanceIndex
    );
    if (nodeId === null) {
      // A click on empty space clears the selection, as in CAD viewers.
      this.ngZone.run(() => this.clearSelection());
      return;
    }
    // Show the selection at once; the tree path and the node's details follow from the API.
    this.selectedSceneNodeId = nodeId;
    this.highlightNodes(new Set([nodeId]));
    const node: HypersceneSceneTreeNode | null = await this.revealSceneTreeNode(nodeId);
    if (node === null) {
      return;
    }
    await this.selectSceneTreeNode(node);
    this.scrollToSceneTreeNode(node.id);
  }

  private getSceneNodeIdForObject(object: HsGraphicObject | null, instanceIndex: number | null = null): string | null {
    const renderKey: string | null = object === null ? null : this.sceneSource.renderKeyOf(object, instanceIndex);
    return renderKey === null ? null : hsNodeIdOfRenderKey(renderKey);
  }

  private async revealSceneTreeNode(nodeId: string): Promise<HypersceneSceneTreeNode | null> {
    return this.sceneTree.reveal(nodeId);
  }

  private scrollToSceneTreeNode(nodeId: string): void {
    window.requestAnimationFrame(() => {
      const treeNode: HTMLElement | undefined = Array.from(
        this.document.querySelectorAll<HTMLElement>('[data-scene-node-id]')
      ).find((element: HTMLElement) => element.dataset['sceneNodeId'] === nodeId);
      treeNode?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    });
  }

  private finishCameraNavigation(): void {
    this.viewerProfiler?.recordEvent(
      'frustum.navigation-stop',
      `force=${this.forceFrustumRefresh};pending=${this.frustumRefreshPending};` +
        `inProgress=${this.frustumRefreshInProgress};pointCloudNav=${this.pointCloudNavigationActive}`
    );
    if (
      (this.forceFrustumRefresh || this.frustumRefreshPending) &&
      !this.freezeFrustum &&
      this.ingestState.sceneId !== null &&
      this.ingestState.phase === 'ready'
    ) {
      this.scheduleFrustumRefresh(this.ingestState.sceneId, 0, 'navigation-stop');
    }
    this.scheduleHlodLookup();
  }

  private updateNavigationSpeed(clientX: number, clientY: number): void {
    const now: number = performance.now();
    const updateInterval: number = Math.max(16, this.runtimeSettings.debounceMs);
    if (
      this.navigationSpeedSampling ||
      (this.lastNavigationSpeedUpdateTime !== 0 && now - this.lastNavigationSpeedUpdateTime < updateInterval)
    ) {
      return;
    }
    this.lastNavigationSpeedUpdateTime = now;

    if (this.gl === null || this.renderObjects === null) {
      this.navigationSpeedMultiplier = 1;
      return;
    }

    const canvas: HTMLCanvasElement | null = this.resolveViewerCanvasElement();
    if (canvas === null) {
      this.navigationSpeedMultiplier = 1;
      return;
    }

    const bounds: DOMRect = canvas.getBoundingClientRect();
    if (bounds.width <= 0 || bounds.height <= 0) {
      this.navigationSpeedMultiplier = 1;
      return;
    }

    const x: number = (clientX - bounds.left) * (canvas.width / bounds.width);
    const y: number = (bounds.bottom - clientY) * (canvas.height / bounds.height);
    this.navigationSpeedSampling = true;
    void this.sampleNavigationSpeed(this.gl, this.renderObjects, x, y, this.navigationSpeedSampleRevision);
  }

  private async sampleNavigationSpeed(
    gl: WebGL2RenderingContext,
    renderObjects: HsRenderObjects<HsGraphicObject, HsGraphicLayer>,
    x: number,
    y: number,
    revision: number
  ): Promise<void> {
    const requestedAt: number = performance.now();
    try {
      await new Promise<void>((resolve: () => void) => window.setTimeout(resolve, 0));
      if (revision !== this.navigationSpeedSampleRevision) {
        return;
      }
      const cameraPosition: HsVector3 = HsVector3.makeZeroVector().setFromMatrixPosition(
        this.camera.worldMatrix.values
      );
      const output: HsRaycastOutput = new HsRaycastOutput();
      const drawSpan: HsViewerProfilerSpan | null = this.viewerProfilingEnabled
        ? this.viewerProfiler.startSpan('raycast.draw')
        : null;
      try {
        this.raycast.draw(gl, this.render, this.camera, renderObjects, [x, y]);
      } finally {
        this.viewerProfiler.endSpan(drawSpan);
      }
      const readSpan: HsViewerProfilerSpan | null = this.viewerProfilingEnabled
        ? this.viewerProfiler.startSpan('raycast.async-readback')
        : null;
      try {
        await this.raycast.readRaycastAsync(gl, x, y, output);
      } finally {
        this.viewerProfiler.endSpan(readSpan);
      }
      if (revision !== this.navigationSpeedSampleRevision) {
        return;
      }
      this.navigationPickPoint = output.object === null ? null : HsVector3.makeCopy(output.position);
      this.navigationPickDistance = output.object === null ? null : HsVector3.distance(cameraPosition, output.position);
      this.navigationPickRequestedAt = requestedAt;
      this.navigationSpeedMultiplier =
        output.object === null
          ? 1
          : hsAdaptiveNavigationSpeedMultiplier(HsVector3.distance(cameraPosition, output.position));
    } catch (error: unknown) {
      const message: string = error instanceof Error ? error.message : 'Navigation picking failed';
      // eslint-disable-next-line no-console
      console.error(`Viewer navigation: ${message}`);
      this.viewerProfiler.recordEvent('navigation.picking-error', message);
    } finally {
      if (revision === this.navigationSpeedSampleRevision) {
        this.navigationSpeedSampling = false;
      }
    }
  }

  private sampleRaycast(
    gl: WebGL2RenderingContext,
    renderObjects: HsRenderObjects<HsGraphicObject, HsGraphicLayer>,
    x: number,
    y: number
  ): void {
    const drawSpan: HsViewerProfilerSpan | null = this.viewerProfilingEnabled
      ? this.viewerProfiler?.startSpan('raycast.draw') ?? null
      : null;
    this.raycast.draw(gl, this.render, this.camera, renderObjects, [x, y]);
    if (drawSpan !== null) {
      this.viewerProfiler?.endSpan(drawSpan);
    }
    const readSpan: HsViewerProfilerSpan | null = this.viewerProfilingEnabled
      ? this.viewerProfiler?.startSpan('raycast.readback') ?? null
      : null;
    this.raycast.readRaycast(gl, x, y, this.raycastOutput);
    if (readSpan !== null) {
      this.viewerProfiler?.endSpan(readSpan);
    }
  }

  private handleOutputMessage(): HsSubscriberData<HsOutputMessageObject> {
    return HsGlobal.outputMessages.subscribe((outputMessageObject: HsOutputMessageObject) => {
      if (
        outputMessageObject.type === HsOutputMessageType.CameraLookAtComplete ||
        outputMessageObject.type === HsOutputMessageType.CameraFocusAtComplete
      ) {
        this.selectedCameraToolType = HsCameraToolType.Default;
        const cameraToolTypeControlAction: HsCameraToolTypeControlAction = new HsCameraToolTypeControlAction(
          this.selectedCameraToolType
        );
        this.controlActionsHandler.updateControlAction(cameraToolTypeControlAction);
      }
    });
  }

  private isKeyboardNavigationAction(actionType: HsControlActionType): boolean {
    return (
      actionType === HsControlActionType.PressedTop ||
      actionType === HsControlActionType.PressedRight ||
      actionType === HsControlActionType.PressedLeft ||
      actionType === HsControlActionType.PressedBottom ||
      actionType === HsControlActionType.PressedAdditionalOne ||
      actionType === HsControlActionType.PressedAdditionalTwo
    );
  }

  private markRenderStateDirty(resetDistributedRenderingCursor: boolean): void {
    this.forceFrustumRefresh = true;
    this.frustumUpdateTime = Math.max(1, this.frustumUpdateTime);
    this.viewerProfiler?.recordEvent(
      'frustum.camera-dirty',
      `nav=${this.isCameraNavigationActive()};inProgress=${this.frustumRefreshInProgress};` +
        `pending=${this.frustumRefreshPending};timer=${this.frustumRefreshTimer !== null}`
    );

    if (resetDistributedRenderingCursor) {
      this.lastRenderedObjectIndex = 0;
    }

    this.requestRender();

    if (this.ingestState.sceneId === null || this.ingestState.phase !== 'ready') {
      return;
    }

    if (!this.freezeFrustum) {
      if (this.frustumRefreshTimer !== null) {
        window.clearTimeout(this.frustumRefreshTimer);
        this.frustumRefreshTimer = null;
      }
      this.frustumRefreshPending = this.frustumRefreshInProgress;
    }
    this.schedulePointCloudNavigationRefresh(this.ingestState.sceneId);
  }

  private schedulePointCloudNavigationRefresh(sceneId: string): void {
    this.pointCloudNavigationActive = true;

    if (this.pointCloudDetailTimer !== null) {
      window.clearTimeout(this.pointCloudDetailTimer);
    }
    this.pointCloudDetailTimer = window.setTimeout(() => {
      this.pointCloudDetailTimer = null;
      this.pointCloudNavigationActive = false;
      this.setPointCloudVisibility(true);
      this.viewerProfiler?.recordEvent(
        'frustum.point-cloud-detail-timeout',
        `pending=${this.frustumRefreshPending};inProgress=${this.frustumRefreshInProgress}`
      );
      if (!this.freezeFrustum) {
        this.scheduleFrustumRefresh(sceneId, 0, 'point-cloud-detail-timeout');
      } else if (this.scenePointCloudObjectsByGeometryId.size > 0) {
        this.lastRenderedObjectIndex = 0;
        this.requestRender();
      }
    }, Math.max(0, this.runtimeSettings.debounceMs));
  }

  private setPointCloudVisibility(visible: boolean): void {
    const visibleGeometryIds: Set<string> = visible
      ? new Set(this.scenePointCloudObjectsByGeometryId.keys())
      : new Set();
    for (const [geometryId, pointCloudObject] of this.scenePointCloudObjectsByGeometryId) {
      const objectVisible: boolean = visibleGeometryIds.has(geometryId);
      if (pointCloudObject.visible !== objectVisible) {
        this.invalidateSceneGeometryTotals();
        pointCloudObject.visible = objectVisible;
      }
    }
  }

  private observeCanvasResize(canvas: HTMLCanvasElement): void {
    this.canvasResizeObserver?.disconnect();
    this.canvasResizeObserver = null;

    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    this.canvasResizeObserver = new ResizeObserver(() => {
      this.resize();
    });
    this.canvasResizeObserver.observe(canvas);
  }

  private resize(): void {
    if (this.canvasDocument === null || this.gl === null || this.objectsLayer === null || this.canvasSize === null) {
      return;
    }

    const canvasSize: HsCanvasSize = this.viewer?.surface.resize(this.render) ?? this.canvasSize;
    this.cameraToolsHandler.updateCanvasSize(canvasSize);
    this.markRenderStateDirty(true);
  }

  /** What the scene source requests and how it downloads and draws, from the page's runtime settings. */
  private makeSceneStreaming(): HsSceneStreamingSettings {
    const limits: { points: number; cachePoints: number; boxes: number } = this.getPointCloudLimits();
    return {
      frustumObjects: hsFrustumObjects(this.getFrustumRequestSettings(), {
        navigating: this.pointCloudNavigationActive,
        includeStaticGeometry: true,
        viewport: this.getFrustumViewport(),
        // Downloads follow the regulated draw budget instead of a fixed navigation limit.
        pointLimit: limits.points,
      }),
      nodeTags: hsParseNodeTags(this.ingestTag),
      // Geometry the section hides is not downloaded: the API returns only nodes meeting this box.
      cutBox: this.section.cutBox(),
      // The page schedules refreshes itself; one asked for during a refresh runs right after it.
      refreshDelayMs: 0,
      downloadBatchSize: this.runtimeSettings.downloadBatchSize,
      texturedMeshDownloadBatchSize: this.runtimeSettings.texturedMeshDownloadBatchSize,
      pointCloudDownloadBatchSize: this.runtimeSettings.pointCloudDownloadBatchSize,
      ...(this.downloadBatchAuto ? { adaptiveMeshBatchBytes: this.runtimeSettings.downloadMinBatchBytes } : {}),
      ...this.getPointCloudDownloadBatching(),
      renderBatchSize: this.runtimeSettings.renderBatchSize,
      renderBudgetMs: this.runtimeSettings.renderBudgetMs,
      pointCloudLimits: { points: limits.cachePoints, geometries: limits.boxes },
      instancing: this.enableInstancing,
    };
  }

  /** Refreshes the scene source schedules by itself wait for the same conditions as the page's own. */
  private canRefreshScene(): boolean {
    return (
      !this.freezeFrustum &&
      !this.isCameraNavigationActive() &&
      !this.sceneLoadInProgress &&
      this.ingestState.phase === 'ready'
    );
  }

  /** Loading UI, profiling, debug overlays and HLOD lookups around the scene source's refreshes. */
  private listenToSceneSource(): HsDisposable[] {
    return [
      this.sceneSource.on('started', (options: HsSceneUpdateOptions) => this.onSceneRefreshStarted(options)),
      this.sceneSource.on('progress', (progress: HsSceneRefreshProgress) => this.onSceneRefreshProgress(progress)),
      this.sceneSource.on('refreshed', (result: HsSceneRefreshResult) => this.onSceneRefreshed(result)),
      this.sceneSource.on('error', (error: unknown) => this.onSceneRefreshFailed(error)),
      this.sceneSource.on('finished', () => this.onSceneRefreshFinished()),
      this.sceneSource.on('applied', (event: HsSceneAppliedEvent) => this.onSceneApplied(event)),
      // Applying geometry gives objects scene materials and new instance buffers again.
      this.sceneSource.on('applied', (event: HsSceneAppliedEvent) => {
        if (event.stage !== 'chunk' && this.selectionHighlightNodeIds.size > 0) {
          this.selectionHighlight.apply(this.selectionHighlightNodeIds);
        }
      }),
    ];
  }

  private onSceneRefreshStarted(options: HsSceneUpdateOptions): void {
    const requestId: number = ++this.frustumRequestSequence;
    this.viewerProfiler?.recordEvent(
      'frustum.request-start',
      `id=${requestId};trigger=${this.sceneRefreshTrigger};visibilityOnly=${options.visibilityOnly === true};` +
        `nav=${this.isCameraNavigationActive()}`
    );
    // Refreshes the source starts by itself follow a pending request.
    this.sceneRefreshTrigger = 'pending-after-request';
    this.sceneRefreshSpan = this.viewerProfilingEnabled
      ? this.viewerProfiler?.startSpan('frustum.refresh') ?? null
      : null;
    this.beginViewerLoading('Requesting visible scene geometry...');
  }

  private onSceneRefreshProgress(progress: HsSceneRefreshProgress): void {
    this.setViewerLoadingProgress(progress.completed, progress.total, progress.message);
    this.ingestState = {
      ...this.ingestState,
      meshesBinaryBytes: progress.bytes.meshes,
      linesBinaryBytes: progress.bytes.lines,
      pointCloudsBinaryBytes: progress.bytes.pointClouds,
      renderedMeshObjectsCount: progress.stats.meshCount,
      renderedLineObjectsCount: progress.stats.lineCount,
      renderedPointCloudObjectsCount: progress.stats.pointCloudCount,
      message: 'Loading frustum geometry...',
    };
  }

  private onSceneRefreshed(result: HsSceneRefreshResult): void {
    this.firstGeometryRefreshFinished = true;
    const renderedStats: HypersceneRenderedObjectsStat = result.stats;
    this.sceneLoadDebug = {
      ...this.sceneLoadDebug,
      loadMode: 'frustum',
      meshIdsCount: result.geometryIds.meshes,
      lineIdsCount: result.geometryIds.lines,
      pointCloudIdsCount: result.geometryIds.pointClouds,
      meshesBinaryBytes: result.bytes.meshes,
      linesBinaryBytes: result.bytes.lines,
      pointCloudsBinaryBytes: result.bytes.pointClouds,
      decodedMeshesCount: result.decoded.meshes,
      decodedLinesCount: result.decoded.lines,
      decodedPointCloudsCount: result.decoded.pointClouds,
      meshRejectedInvalidVertices: renderedStats.meshRejectedInvalidVertices,
      meshRejectedEmptyIndices: renderedStats.meshRejectedEmptyIndices,
      meshRejectedInvalidIndices: renderedStats.meshRejectedInvalidIndices,
      meshRejectedIndexedBuildFailed: renderedStats.meshRejectedIndexedBuildFailed,
      meshRejectedRegularFallbackFailed: renderedStats.meshRejectedRegularFallbackFailed,
      lastUpdatedAt: new Date().toLocaleTimeString(),
      lastError: this.getSceneMeshMaterialError(),
    };

    this.ingestState = {
      ...this.ingestState,
      meshGeometryIdsCount: result.geometryIds.meshes,
      lineGeometryIdsCount: result.geometryIds.lines,
      pointCloudGeometryIdsCount: result.geometryIds.pointClouds,
      meshesBinaryBytes: result.bytes.meshes,
      linesBinaryBytes: result.bytes.lines,
      pointCloudsBinaryBytes: result.bytes.pointClouds,
      renderedMeshObjectsCount: renderedStats.meshCount,
      renderedLineObjectsCount: renderedStats.lineCount,
      renderedPointCloudObjectsCount: renderedStats.pointCloudCount,
      collidedPointsCount: renderedStats.collidedPointsCount,
      message:
        this.sceneMeshMaterialErrors.size === 0
          ? 'Frustum sync completed.'
          : `Frustum sync completed with ${this.sceneMeshMaterialErrors.size} invalid mesh objects skipped.`,
    };
    this.scheduleHlodLookup();
  }

  private onSceneRefreshFailed(error: unknown): void {
    this.firstGeometryRefreshFinished = true;
    this.sceneLoadDebug = {
      ...this.sceneLoadDebug,
      lastUpdatedAt: new Date().toLocaleTimeString(),
      lastError: error instanceof Error ? error.message : 'Frustum sync failed.',
    };
  }

  private onSceneRefreshFinished(): void {
    this.viewerProfiler?.recordEvent(
      'frustum.request-end',
      `id=${this.frustumRequestSequence};pending=${this.frustumRefreshPending};nav=${this.isCameraNavigationActive()}`
    );
    this.finishViewerLoadingIfIdle();
    this.changeDetector.markForCheck();
    if (this.sceneRefreshSpan !== null) {
      this.viewerProfiler?.endSpan(this.sceneRefreshSpan);
      this.sceneRefreshSpan = null;
    }
  }

  /** Page work after the scene source drew: culling caches, debug boxes, distributed drawing and profiling. */
  private onSceneApplied(event: HsSceneAppliedEvent): void {
    this.invalidateSceneGeometryTotals();
    this.viewerSpatialCulling.refresh();
    if (this.pointCloudNavigationActive) {
      this.setPointCloudVisibility(true);
    }
    this.addFrustumDebugObjects();
    this.frustumUpdateTime = 1;
    if (event.stage === 'final' || event.stage === 'reapply') {
      this.lastRenderedObjectIndex = 0;
    }
    // Chunks of a large delta draw in the next frames; the stages after them start the culling at once.
    if (event.stage !== 'chunk') {
      this.forceFrustumRefresh = true;
      this.requestRender();
    }
    this.scheduleSceneGeometryDebugUpdate();
    this.viewerProfiler?.recordEvent(
      'geometry.apply.done',
      `stage=${event.stage};meshes=${event.stats.meshCount};lines=${event.stats.lineCount};` +
        `pointClouds=${event.stats.pointCloudCount};queue=${this.objectsQueue?.objects.size ?? 0}`
    );
  }

  /** Draws the measurement and marker overlays again; scene geometry is not touched. */
  private refreshTransientOverlays(): void {
    const overlays: HsViewerOverlays | null = this.overlays;
    if (overlays === null) {
      return;
    }
    overlays.clear(TRANSIENT_OVERLAYS);
    overlays.addLines(
      TRANSIENT_OVERLAYS,
      'debug-measurement-overlay',
      this.measurements.tool.lineVertices(),
      [1, 0.72, 0.12]
    );
    overlays.addPoints(
      TRANSIENT_OVERLAYS,
      'debug-measurement-points',
      this.measurements.tool.pointVertices(),
      this.measurements.tool.pointColors(),
      14
    );
    if (this.markersVisible && this.markers.length > 0) {
      const markerPoints: number[] = [];
      const markerColors: number[] = [];
      this.markers.forEach((marker: HypersceneMarker) => {
        markerPoints.push(marker.position[0], marker.position[1], marker.position[2]);
        const isSelected: boolean = marker.id === this.selectedMarkerId;
        markerColors.push(isSelected ? 1 : 0.18, isSelected ? 0.25 : 0.82, isSelected ? 0.15 : 0.95);
      });
      overlays.addPoints(TRANSIENT_OVERLAYS, 'annotation-markers', markerPoints, markerColors, 10);
    }
    this.viewerSpatialCulling.refresh();
    this.refreshLabels();
    this.forceFrustumRefresh = true;
    this.requestRender();
    this.changeDetector.markForCheck();
  }

  /** Mouse, wheel, key and touch navigation of the canvas, created with the viewer. */
  private viewerInput: HsViewerInput | null = null;
  /** A held button the benchmark probe simulates while it navigates the camera itself. */
  private navigationButtonOverride: number | null = null;

  /** Canvas input outside Angular's zone, so pointer moves do not run change detection. */
  private createViewerInput(viewer: HsHypersceneViewer): HsViewerInput {
    this.subscription.add(
      fromEvent<KeyboardEvent>(viewer.surface.canvas, 'keydown').subscribe((event: KeyboardEvent) =>
        this.onViewerShortcut(event)
      )
    );
    return this.ngZone.runOutsideAngular(
      () =>
        new HsViewerInput(viewer, {
          keyMap: KEY_MAP,
          touchGestures: this.touchGestures,
          hooks: {
            pointerDown: (event: MouseEvent) => this.onViewerPointerDown(event),
            click: (event: MouseEvent) => this.onViewerClick(event),
            doubleClick: (event: MouseEvent) =>
              this.ngZone.run(() => this.focusAtScreenPoint(event.clientX, event.clientY)),
            speedScale: (source: 'wheel' | 'key', clientX: number, clientY: number, action?: HsControlActionType) =>
              this.navigationSpeedScale(source, clientX, clientY, action),
            touchStart: () => this.onViewerTouchStart(),
            navigationEnd: () => {
              const move: HsCameraDefaultMove | undefined = this.cameraToolsHandler.defaultMove;
              if (move !== undefined) {
                move.panAnchor = null;
              }
              this.overlays?.clear(ORBIT_PIVOT_OVERLAY);
              this.requestRender();
              this.updateTouchNavigationActive();
              this.finishCameraNavigation();
            },
          },
        })
    );
  }

  /**
   * A left click without a drag places a marker or a measurement point when those tools are on, and selects the
   * scene object under it otherwise. Tools take clicks rather than presses, so orbiting the camera with the
   * tool on places nothing.
   */
  private onViewerClick(event: MouseEvent): void {
    if (this.sectionPickArmed !== false) {
      this.ngZone.run(() => this.placeSectionAt(event.clientX, event.clientY));
    } else if (this.markerPlacementEnabled) {
      this.ngZone.run(() => this.tryCreateMarker(event.clientX, event.clientY));
    } else if (this.measurements.enabled) {
      this.ngZone.run(() => this.tryCaptureMeasurementPoint(event.clientX, event.clientY));
    } else if (!this.pointCloudNavigationActive) {
      void this.selectSceneObjectAt(event.clientX, event.clientY);
    }
  }

  /**
   * Mouse navigation as in CAD and BIM viewers: a left drag looks around from the camera's place (or moves the
   * section when its drag is on), a right drag orbits around the model point pressed, and a middle drag pans with
   * that point under the cursor. Off the model the previous pivot stays. The orbit pivot shows as a dot while
   * dragging.
   */
  private onViewerPointerDown(event: MouseEvent): void {
    const move: HsCameraDefaultMove | undefined = this.cameraToolsHandler.defaultMove;
    if (move === undefined) {
      return;
    }
    if (event.button === 0) {
      if (this.sectionDragArmed && this.section.mode !== 'off') {
        this.startSectionDrag(event);
      }
      return;
    }
    if (event.button !== 1 && event.button !== 2) {
      return;
    }
    const hit: HsMeasurementHit | null = this.pickMeasurementSurface(event.clientX, event.clientY);
    if (hit !== null) {
      this.orbitPivot.set(hit.point[0], hit.point[1], hit.point[2]);
    }
    move.orbitPivot = this.orbitPivot;
    if (event.button === 1) {
      move.panAnchor = this.orbitPivot;
      return;
    }
    const overlays: HsViewerOverlays | null = this.overlays;
    if (overlays !== null) {
      overlays.clear(ORBIT_PIVOT_OVERLAY);
      overlays.addPoints(ORBIT_PIVOT_OVERLAY, 'debug-orbit-pivot', [...this.orbitPivot.values], [0.1, 0.55, 1], 9);
    }
  }

  private shortcutAction(key: string): (() => void) | undefined {
    switch (key) {
      case 'f':
        return () => void (this.selectedSceneNodeId === null ? this.fitAll() : this.focusSelectedSceneNode());
      case 'h':
        return () => void this.hideSelectedSceneNode();
      case 'i':
        return () => void this.isolateSelectedSceneNode();
      case 'u':
        return () => void this.unhideAllSceneNodes();
      case 'x':
        return () => this.toggleXray();
      case 'p':
        return () => this.toggleParallelProjection();
      case 'Escape':
        return () => this.cancelToolsAndSelection();
      default:
        return undefined;
    }
  }

  /** Keyboard shortcuts of the canvas, as in CAD viewers; keys while typing in a field never reach the canvas. */
  private onViewerShortcut(event: KeyboardEvent): void {
    if (event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }
    const key: string = event.key.length === 1 ? event.key.toLowerCase() : event.key;
    const view: HsCameraView | undefined = VIEW_SHORTCUTS.get(key);
    const action: (() => void) | undefined =
      view !== undefined ? () => void this.setCameraView(view) : this.shortcutAction(key);
    if (action === undefined) {
      return;
    }
    event.preventDefault();
    this.ngZone.run(action);
  }

  /** Wheel and key steps follow the distance to the scene under the pointer when adaptive navigation is on. */
  private navigationSpeedScale(
    source: 'wheel' | 'key',
    clientX: number,
    clientY: number,
    action?: HsControlActionType
  ): number {
    const adaptive: boolean =
      source === 'wheel'
        ? this.adaptiveNavigationWheel
        : this.adaptiveNavigationKeyboard && action !== undefined && this.isKeyboardNavigationAction(action);
    if (!adaptive) {
      return 1;
    }
    this.updateNavigationSpeed(clientX, clientY);
    return this.navigationSpeedMultiplier;
  }

  /** The first touch shows the on-screen camera controls. */
  private onViewerTouchStart(): void {
    this.updateTouchNavigationActive();
    if (!this.touchNavigationControlsVisible) {
      this.ngZone.run(() => {
        this.touchNavigationControlsVisible = true;
        this.changeDetector.markForCheck();
      });
    }
  }

  /**
   * The drawn surface under a client point, for measurements and markers: any scene geometry, photogrammetry
   * tile or point cloud, with the scene node it belongs to when it is one.
   */
  private pickMeasurementSurface(clientX: number, clientY: number, snap: boolean = false): ViewerSurfaceHit | null {
    const hit: HsViewerPick | null = this.pickSurface(clientX, clientY);
    if (hit === null) {
      return null;
    }
    const renderKey: string | null = this.sceneSource.renderKeyOf(hit.object, hit.instanceIndex);
    const nodeId: string | null = renderKey === null ? null : hsNodeIdOfRenderKey(renderKey);
    const snapped: HsSnapResult | null = snap && renderKey !== null ? this.snapToDrawnMesh(hit, renderKey) : null;
    return snapped === null
      ? { point: hit.point, nodeId, kind: 'face', normal: null }
      : { point: snapped.point, nodeId, kind: snapped.kind, normal: snapped.normal };
  }

  /**
   * The drawn object and world point under a client point, from the page's own picking pass; `viewer.pick` would
   * allocate a second full-size picking target (about 18 MiB of GPU memory at 1280 x 720).
   */
  private pickSurface(clientX: number, clientY: number): HsViewerPick | null {
    const canvas: HTMLCanvasElement | null = this.resolveViewerCanvasElement();
    if (this.gl === null || this.renderObjects === null || canvas === null) {
      return null;
    }
    const bounds: DOMRect = canvas.getBoundingClientRect();
    if (bounds.width <= 0 || bounds.height <= 0) {
      return null;
    }
    const x: number = (clientX - bounds.left) * (canvas.width / bounds.width);
    const y: number = (bounds.bottom - clientY) * (canvas.height / bounds.height);
    this.sampleRaycast(this.gl, this.renderObjects, x, y);
    const output: HsRaycastOutput = this.raycastOutput;
    return output.object === null
      ? null
      : {
          object: output.object,
          instanceIndex: output.instanceIndex,
          point: [output.position.x, output.position.y, output.position.z],
        };
  }

  /**
   * Snaps a pick to a corner, edge middle or edge of the decoded mesh drawn there, within a few pixels; null when
   * the mesh is not in memory (photogrammetry tiles, point clouds, trimmed cache entries).
   */
  private snapToDrawnMesh(pick: HsViewerPick, renderKey: string): HsSnapResult | null {
    const geometryIds: Record<string, string> = this.sceneSource.frustum.meshes.geometryIdByRenderKey;
    const geometryId: string | undefined =
      geometryIds[renderKey] ?? geometryIds[renderKey.replace(/\|material-part:\d+$/, '')];
    const mesh: HsDecodedMesh | undefined =
      geometryId === undefined ? undefined : this.sceneSource.geometry.meshes.get(geometryId);
    const matrix: ArrayLike<number> | null =
      pick.object instanceof HsGraphicCollection
        ? pick.instanceIndex === null
          ? null
          : pick.object.getInstanceMatrix(pick.instanceIndex)
        : pick.object.worldMatrix.values;
    const height: number = this.resolveViewerCanvasElement()?.getBoundingClientRect().height ?? 0;
    if (mesh === undefined || matrix === null || height <= 0) {
      return null;
    }
    const eye: HsHypersceneTypes.Array3 = this.camera.worldMatrix.getPositionArray();
    const distance: number = Math.hypot(pick.point[0] - eye[0], pick.point[1] - eye[1], pick.point[2] - eye[2]);
    return hsSnapToMesh(mesh, matrix, pick.point, hsPixelsToWorld(SNAP_PIXELS, distance, this.camera.fov, height));
  }

  /** The model point in the middle of the canvas, or the point the camera was last aimed at. */
  private viewCenterPoint(): HsMeasurementPoint {
    const canvas: HTMLCanvasElement | null = this.resolveViewerCanvasElement();
    const bounds: DOMRect | undefined = canvas?.getBoundingClientRect();
    const hit: HsMeasurementHit | null =
      bounds === undefined
        ? null
        : this.pickMeasurementSurface(bounds.left + bounds.width / 2, bounds.top + bounds.height / 2);
    return hit?.point ?? [this.observablePoint.x, this.observablePoint.y, this.observablePoint.z];
  }

  /** The camera ray through a client point of the canvas. */
  private screenRay(clientX: number, clientY: number): HsScreenRay | null {
    const canvas: HTMLCanvasElement | null = this.resolveViewerCanvasElement();
    return canvas === null ? null : hsScreenRay(this.camera, canvas.getBoundingClientRect(), clientX, clientY);
  }
}
/* eslint-enable @typescript-eslint/member-ordering */
